import ExportButton from "./ExportButton";
import PdfExportButton from "./PdfExportButton";
import React, { useEffect } from "react";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import { t } from "../utils/translations";
import {
  backendCurrencyOrFallback,
  isBlockedTaxRow,
  pickFiatBuy,
  pickFiatPrice,
  pickFiatSell,
  pickFiatGrossProfit,
  pickFiatTaxable,
  pickFiatNonTaxable,
  pickFiatShort,
  pickFiatLong,
  pickFiatTotal,
} from "../utils/fiatFields";
import {
  extractEventIdentityFields,
  formatCsvDateTimeUtc,
  normalizeTaxClassificationForExport,
  shouldIncludeCustomerExportRow,
} from "../utils/exportFormatting.js";
import { buildNftAcquisitionExportRows } from "../../shared/nftAcquisitionLedger.js";
import {
  getSyntheticVthoCsvFields,
  isSyntheticVthoRow,
  pickDisplayHash,
  pickExportVthoTokenAmount,
  pickSyntheticVthoIncomeFiat,
} from "../utils/syntheticVthoDisplay.js";
import { applyVthoGeneratedDisplayRollup } from "../utils/rollupGeneratedVthoByDay.js";
import {
  hasUsableUkFinalMoney,
  normalizeUkIncomeEventKeyForDedup,
  pickUkEventUniq,
  makeUkFinalEventKeyFromTaxRow,
} from "../utils/ukTaxUi.js";
import {
  buildUkFinalExport,
  summarizeUkSa108CapitalRows,
  isUkTokenCapitalDisposal,
} from "../utils/ukFinalExport.js";
import { isTaxYearOpen } from "../utils/taxYear.js";
import {
  isLongTermHolding,
  calculateHoldingDays,
  resolveLegHoldingDays,
  resolveLegIsLongTerm,
  getUsHoldingTerm,
} from "../../shared/usHoldingTerm.js";
import { buildExchangeEventId } from "../../shared/exchangeEventId.js";
import {
  isTaxExportEligibleDisposal,
  isInternalNftAcquisitionAllocationRow,
  isActualTaxReviewRow,
  reconcileDisposalForm8949,
  FEE_TOKEN_DISPOSAL_CLASSES,
} from "../../shared/taxExportEligibility.js";
import {
  shouldIncludeNftDisposalExportRow,
} from "../utils/nftTransformExport.js";
import {
  buildCanonicalEventKey,
  buildCanonicalEventKeyFromRow,
} from "../../shared/canonicalEventKey.js";
import {
  isVthoGasFeeRow,
  finiteMoneyOrNull,
} from "../utils/vthoGasFees.js";

function getVthoDisplayRows(ds, vthoData) {
  return applyVthoGeneratedDisplayRollup(
    Array.isArray(ds?.vthoTaxRows) ? ds.vthoTaxRows : (vthoData || [])
  );
}

function expectedCurrencyForCountry(country = "DE") {
  const c = String(country || "DE").trim().toUpperCase().replace(/\s+/g, "_");
  if (["US", "USA", "UNITED_STATES"].includes(c)) return "USD";
  if (["UK", "GB", "UNITED_KINGDOM", "UNITED KINGDOM"].includes(c)) return "GBP";
  if (["CH", "CHE", "SWITZERLAND"].includes(c)) return "CHF";
  return "EUR";
}

function normalizeFiatCurrency(currency, country = "DE") {
  const c = String(currency || "").trim().toUpperCase();
  if (c && c !== "UNKNOWN" && c !== "NULL" && c !== "UNDEFINED") return c;
  return expectedCurrencyForCountry(country);
}

function normalizeExportComparableValue(value) {
  if (value === null || value === undefined) return "";
  const n = Number(value);
  if (Number.isFinite(n)) return n.toFixed(12).replace(/\.?0+$/, "");
  return String(value).trim().toLowerCase();
}

function economicExportKey(row = {}, moneyKeys = []) {
  const parts = [
    row.Steuerjahr,
    row.Quelle,
    row.Klassifizierung,
    row.Asset,
    normalizeExportComparableValue(row.Menge),
    row.Datum_UTC,
    row.Tx_Hash,
    String(row.Event_Key || "").trim().toLowerCase(),
    ...moneyKeys.map((k) => normalizeExportComparableValue(row[k])),
  ];
  return parts.map((v) => String(v ?? "").trim().toLowerCase()).join("|");
}

function dedupeFinalExportRows(rows = [], moneyKeys = []) {
  const seen = new Set();
  const out = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = economicExportKey(row, moneyKeys);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

function ukIncomeEconomicKey(row = {}, incomeKey, taxableKey) {
  const txHash = String(row.Tx_Hash || "").toLowerCase();
  const eventKey = normalizeUkIncomeEventKeyForDedup(row.Event_Key, txHash);

  return [
    row.Steuerjahr,
    row.Quelle,
    row.Klassifizierung,
    row.Asset,
    normalizeExportComparableValue(row.Menge),
    row.Datum_UTC,
    txHash,
    eventKey,
    normalizeExportComparableValue(row[incomeKey]),
    normalizeExportComparableValue(row[taxableKey]),
  ].join("|").toLowerCase();
}

function dedupeUkIncomeRows(rows = [], incomeKey, taxableKey) {
  const seen = new Set();
  const out = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = ukIncomeEconomicKey(row, incomeKey, taxableKey);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

function dedupeUkIncomeExportRows(rows = [], incomeKey, taxableKey) {
  const seen = new Set();
  const out = [];

  for (const row of Array.isArray(rows) ? rows : []) {
    const hash = String(row.Tx_Hash || "").trim().toLowerCase();
    const normalizedEventKey = normalizeUkIncomeEventKeyForDedup(
      row.Event_Key || "",
      hash
    );

    const key = [
      row.Steuerjahr,
      row.Quelle,
      row.Klassifizierung,
      row.Asset,
      normalizeExportComparableValue(row.Menge),
      row.Datum_UTC,
      hash,
      normalizedEventKey,
      normalizeExportComparableValue(row[taxableKey] ?? row[incomeKey]),
    ]
      .map((v) => String(v ?? "").trim().toLowerCase())
      .join("|");

    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }

  return out;
}

function economicExportLooseKey(row = {}) {
  const parts = [
    row.Steuerjahr,
    row.Quelle,
    row.Klassifizierung,
    row.Asset,
    normalizeExportComparableValue(row.Menge),
    row.Datum_UTC,
    row.Tx_Hash,
  ];
  return parts.map((v) => String(v ?? "").trim().toLowerCase()).join("|");
}

function buildRawLedgerDedupeKey(row = {}) {
  const identity =
    extractEventIdentityFields(row);

  const hash = String(
    row.hash ||
      row.txid ||
      row.transactionHash ||
      ""
  )
    .trim()
    .toLowerCase();

  const type = String(
    row.type ||
      row.classification ||
      ""
  )
    .trim()
    .toLowerCase();

  const token = String(
    row.token ||
      row.asset ||
      ""
  )
    .trim()
    .toUpperCase();

  const amount =
    normalizeExportComparableValue(
      row.amount ?? ""
    );

  const hasStructuredIdentity =
    identity.clause_index != null ||
    identity.event_index != null ||
    identity.log_index != null;

  /*
   * Neue und vollständige Daten:
   * Das Chain-Event ist über Hash + Clause + Event eindeutig.
   */
  if (hasStructuredIdentity) {
    const canonicalEventKey =
      row.canonical_event_key ||
      identity.canonical_event_key ||
      buildCanonicalEventKeyFromRow(
        row,
        {
          txHash: hash,
          kind: type || "raw_transfer",
        }
      );

    return [
      "event",
      String(
        canonicalEventKey || ""
      ).toLowerCase(),

      String(
        identity.token_contract ||
          row.token_contract ||
          ""
      ).toLowerCase(),

      type,
      token,
      amount,
    ].join("|");
  }

  /*
   * Rückwärtskompatibler Fallback für ältere Snapshots ohne
   * Clause-/Event-Indizes.
   *
   * Sender, Empfänger, Zeitpunkt und eventRef verhindern, dass
   * unterschiedliche Same-Hash-Events unnötig kollidieren.
   */
  return [
    "fallback",
    hash,

    String(
      row.eventRef || ""
    )
      .trim()
      .toLowerCase(),

    type,
    token,
    amount,

    String(
      row.sender ||
        row.from ||
        ""
    )
      .trim()
      .toLowerCase(),

    String(
      row.recipient ||
        row.to ||
        ""
    )
      .trim()
      .toLowerCase(),

    String(
      row.sortTimestamp ||
        row.timestamp ||
        ""
    ),
  ].join("|");
}

/** Zeilen, die nicht berechnet werden konnten und ins Review-CSV müssen. */
function isExcludedFromFinalTaxExport(row = {}, opts = {}) {
  // Explicit null (JSON sparse holes) must not crash Analysis render.
  if (row == null || typeof row !== "object") return false;

  const isUK = opts?.isUK === true;

  // Interne Multi-Mint-Cost-Basis-Helfer: weder Steuer-Export noch Review.
  if (isInternalNftAcquisitionAllocationRow(row)) {
    return false;
  }

  if (isUK && hasUsableUkFinalMoney(row)) {
    return false;
  }

  if (isActualTaxReviewRow(row)) return true;

  return isBlockedTaxRow(row);
}

function pickExclusionReasonForExport(row = {}) {
  if (row == null || typeof row !== "object") {
    return "excluded_from_tax_totals";
  }

  const displayStatus = String(row?.displayPriceStatus || "").toLowerCase();
  const pricingStatus = String(row?.pricingStatus || "").toLowerCase();

  if (row.fifoShortage || pricingStatus === "fifo_quantity_shortage") {
    return "fifo_shortage";
  }

  if (
    row.taxResolutionStatus === "needs_receipt_allocation" ||
    row.displayPriceStatus === "SALE_ALLOCATION_REQUIRED"
  ) {
    return "receipt_allocation_required";
  }

  if (row.pricingReason) return String(row.pricingReason).toLowerCase();
  if (row.reviewReason) return String(row.reviewReason).toLowerCase();

  if (
    row.missingFiatPrice ||
    row.pricingMissing ||
    row.priceMissing ||
    displayStatus.includes("price_not_available") ||
    pricingStatus.includes("missing_price")
  ) {
    return "token_price_not_available";
  }

  if (
    row.missingCostBasis ||
    row.costBasisUnknown ||
    displayStatus.includes("cost_basis") ||
    String(row.taxableStatus || "").toLowerCase().includes("cost_basis")
  ) {
    return "cost_basis_unknown";
  }

  return "excluded_from_tax_totals";
}

function approxEq(a, b, eps = 1e-8) {
  return Math.abs(Number(a || 0) - Number(b || 0)) <= eps;
}

/** Summiert finale CSV-Zeilen (dynamische Fiat-Spaltenkeys). */
function buildExportAudit(rows = [], fiatCur = "EUR") {
  const PROFIT_KEY = `GewinnVerlust_${fiatCur}`;
  const TAXABLE_KEY = `Steuerpflichtig_${fiatCur}`;
  const ST_KEY = `ShortTerm_${fiatCur}`;
  const LT_KEY = `LongTerm_${fiatCur}`;

  const taxableNetFiat = rows.reduce(
    (s, r) => s + Number(r[TAXABLE_KEY] || 0),
    0
  );
  const grossProfitNetFiat = rows.reduce(
    (s, r) => s + Number(r[PROFIT_KEY] || 0),
    0
  );
  const shortTermNetFiat = rows.reduce((s, r) => s + Number(r[ST_KEY] || 0), 0);
  const longTermNetFiat = rows.reduce((s, r) => s + Number(r[LT_KEY] || 0), 0);

  return {
    currency: fiatCur,
    rows: rows.length,
    taxableNetFiat,
    grossProfitNetFiat,
    shortTermNetFiat,
    longTermNetFiat,
  };
}

/**
 * Vergleicht Frontend-Export mit Backend-`exportFingerprint` (usFiatAudit).
 * Liquidity: CSV hat eine Zeile pro Disposal, Backend zählt LP-Events → rows_match ausgelassen.
 */
function verifyExportAgainstBackend(
  sectionName,
  exportRows,
  expectedAudit,
  fiatCur
) {
  if (!expectedAudit) {
    return { status: "skipped", reason: "missing_backend_expected_audit" };
  }

  const actual = buildExportAudit(exportRows, fiatCur);

  const checks = {
    currency_matches: actual.currency === expectedAudit.currency,
    rows_match:
      sectionName === "liquidity"
        ? true
        : Number(actual.rows || 0) === Number(expectedAudit.rows || 0),
    taxable_matches: approxEq(actual.taxableNetFiat, expectedAudit.taxableNetFiat),
    gross_profit_matches: approxEq(
      actual.grossProfitNetFiat,
      expectedAudit.grossProfitNetFiat
    ),
    st_matches: approxEq(actual.shortTermNetFiat, expectedAudit.shortTermNetFiat),
    lt_matches: approxEq(actual.longTermNetFiat, expectedAudit.longTermNetFiat),
  };

  const status = Object.values(checks).every(Boolean) ? "verified" : "failed";

  
  return { status, expected: expectedAudit, actual, checks };
}

export default function ExportAllSection({
  vetData,
  vthoData,
  b3trData,
  nftData,
  liquidityData,
  vip180Data,
  vetVthoData,
  exchangeData,
  exchangeTaxRows,
  taxDataset = null,
  selectedCountry = "DE",
  year,
  walletSet = null,
  onPrintVariant,
  isPreviewMode = false,
  onExportPrepNeeded,
  deferExportPrep = false,
}) {
  const { language } = useLanguage();

  useEffect(() => {
    if (typeof onExportPrepNeeded === "function") {
      onExportPrepNeeded();
    }
  }, [onExportPrepNeeded]);

  const ds = taxDataset || {};

  const engineReviewRows = Array.isArray(ds?.reviewRows)
    ? ds.reviewRows
    : (
        Array.isArray(ds?.walletScopeReviewRows)
          ? ds.walletScopeReviewRows
          : []
      );

  const exportGuard =
    ds?.exportGuard ||
    taxDataset?.exportGuard ||
    {};

  const guardCode = String(
    exportGuard?.code || ""
  )
    .trim()
    .toUpperCase();

  const guardAuditStatus = String(
    exportGuard?.auditStatus || ""
  )
    .trim()
    .toLowerCase();

  /*
   * Rückwärtskompatibilität mit bereits gespeicherten Analysen:
   *
   * Der bisherige Backend-Code hat blocked=true bereits bei normalen
   * Review-Fällen gesetzt. Solche alten Snapshots dürfen nicht weiter
   * alle Reports blockieren.
   */
  const isReviewOnlyLegacyGuard =
    exportGuard?.blocked === true &&
    guardCode === "WALLET_SCOPE_REVIEW_REQUIRED" &&
    guardAuditStatus !== "failed" &&
    Number(exportGuard?.auditViolations || 0) === 0;

  /*
   * Nur ein echter Engine-/Audit-Fehler: PDFs optional sperren.
   * CSVs bleiben nach abgeschlossener Vollberechnung verfügbar.
   * Normale Review-Fälle blockieren nie.
   */
  const hasHardExportFailure = Boolean(
    (exportGuard?.pdfBlocked === true ||
      exportGuard?.hardAuditFailure === true ||
      (exportGuard?.blocked === true && !isReviewOnlyLegacyGuard)) &&
    String(exportGuard?.status || "").toUpperCase() !== "READY_WITH_EXCLUSIONS"
  );

  // US Final: Fiat Section Audit strict muss verified sein, sonst kein PDF-Export
  const usFiatAudit = ds?.fiatSectionAudit || ds?.usFiatAudit || null;
  const usFiatAuditFailed =
    String(selectedCountry || "").toUpperCase() === "US" &&
    usFiatAudit?.summary &&
    usFiatAudit.summary.preview !== true &&
    usFiatAudit.summary.strict === true &&
    String(usFiatAudit.summary.status || "").toLowerCase() !== "verified";

  // Informational only — does not gate downloads outside preview.
  // Year-specific counts are computed after finalExcludedReviewCsv below.

  /*
   * Preview bleibt gesperrt.
   * REVIEW_REQUIRED und Review-CSVs blockieren Downloads nicht.
   * Harte Auditwarnung: PDF optional gesperrt, CSV bleibt verfügbar.
   */
  const csvExportDisabled = isPreviewMode;

  // Kaufsperre nur im Preview. Audit-Hinweise werden unten angezeigt, sperren aber nicht.
  let pdfExportDisabledReason =
    usFiatAuditFailed
      ? (
          language === "en"
            ? "US Fiat Section Audit failed (strict). Fix the audit before exporting final PDFs."
            : "US Fiat-Section-Audit fehlgeschlagen (strict). Bitte Audit beheben, bevor finale PDFs exportiert werden."
        )
      : hasHardExportFailure
      ? (
          language === "en"
            ? "The calculation completed, but a hard audit warning was detected. CSV exports remain available."
            : "Die Berechnung wurde abgeschlossen, aber es liegt eine harte Auditwarnung vor. CSV-Exporte bleiben verfügbar."
        )
      : "";

  const selectedYear =
    year ||
    new URLSearchParams(window.location.search).get("year") ||
    new Date().getFullYear();
  const yearInt = Number(selectedYear);

  const sanitizeFilename = (s) =>
    String(s)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Umlaute -> Basisbuchstaben
      .replace(/ß/g, "ss")
      .replace(/[^a-zA-Z0-9._-]+/g, "_");

  // 🚀 FIX: isDE, isIT und isGR müssen vor ihrer Verwendung definiert werden
  const cc = String(selectedCountry || "DE").toUpperCase();
  const isDE = cc === "DE";
  const isIT = cc === "IT";
  const isGR = cc === "GR";
  const isUS = cc === "US" || cc === "USA" || cc.includes("UNITED STATES");
  const isUK = cc === "UK" || cc === "GB" || cc.includes("UNITED_KINGDOM") || cc.includes("UNITED KINGDOM");
  const isDotDecimalCountry = isUS || isUK;
  const csvDelimiter = isDotDecimalCountry ? "," : ";";

  const getUkTaxYearFromTs = (tsSec) => {
    const ts = Number(tsSec || 0);
    if (!ts) return null;
    const d = new Date(ts * 1000);
    const y = d.getUTCFullYear();
    const startCurrent = Date.UTC(y, 3, 6, 0, 0, 0);
    return d.getTime() >= startCurrent ? y : y - 1;
  };

  const taxYearLabelFor = (yInt) =>
    isUK && yInt
      ? `${yInt}/${String((yInt + 1) % 100).padStart(2, "0")}`
      : String(yInt || "");

  const taxYearLabel = taxYearLabelFor(yearInt);

  const rowInTaxYear = (tsSec, yInt = yearInt) => {
    if (!yInt || !tsSec) return true;
    if (isUK) return getUkTaxYearFromTs(tsSec) === yInt;
    return new Date(tsSec * 1000).getUTCFullYear() === yInt;
  };

  const BASE_TOKEN_DISPOSAL_TYPES = new Set([
    "dex_aggregator_execution_cost",
    "swap_out",
    "nfbc_purchase",
    "v3tax_purchase",
    "mugshot_pro_subscription",
    "vewin_4ocean_donation",
    "one_million_pixel_purchase",

    // Vaiyans VCO – beide echten Token-Spends sind Disposals.
    "vaiyans_vco_game_entry",
    "vaiyans_vco_prediction_entry",

    "velottery_ticket_purchase",
    "scoopup_puppy_pass_fee",
    "groncard_gift_card_purchase",
    "pause_your_carbon_purchase",

    // Fee-Token-Veräußerungen in allen Länderpfaden
    ...FEE_TOKEN_DISPOSAL_CLASSES,
  ]);

  const isTokenCapitalDisposalCls =
    (cls) => {
      const c =
        String(
          cls || ''
        )
          .trim()
          .toLowerCase();

      /*
       * UK MUSS exakt dieselbe kanonische
       * Disposal-Definition benutzen wie
       * buildUkFinalExport().
       */
      if (isUK) {
        return (
          isUkTokenCapitalDisposal(
            c
          )
        );
      }

      return (
        BASE_TOKEN_DISPOSAL_TYPES.has(
          c
        )
      );
    };

  const datasetCurrency = backendCurrencyOrFallback(
    selectedCountry,
    ds?.vetTotals,
    ds?.vthoTotals,
    ds?.b3trTotals,
    ds?.nftTotals,
    ds?.exchangeTotals,
    ds?.liquidityTotals
  );
  const fiatCurrency = normalizeFiatCurrency(datasetCurrency, selectedCountry);

  const PROCEEDS_KEY = `Erlös_${fiatCurrency}_brutto`;
  const FEE_COST_KEY = `Transaktionskosten_${fiatCurrency}`;
  const COST_KEY = `Anschaffungskosten_${fiatCurrency}_brutto`;
  const PROFIT_KEY = `GewinnVerlust_${fiatCurrency}`;
  const TAXABLE_KEY = `Steuerpflichtig_${fiatCurrency}`;
  const NONTAX_KEY = `Steuerfrei_${fiatCurrency}`;
  const INCOME_KEY = `Einnahmen_${fiatCurrency}_brutto`;
  const ST_KEY = `ShortTerm_${fiatCurrency}`;
  const LT_KEY = `LongTerm_${fiatCurrency}`;

  // Helper: Legal Reference pro Land + Exporttyp
  function legalReference(countryCode, exportType) {
    const c = String(countryCode || "DE").toUpperCase();
    const t = String(exportType || "").toLowerCase(); // "disposals" | "income"

    if (c === "DE") {
      return t === "disposals"
        ? "§ 23 EStG (Private Veräußerungsgeschäfte – Kryptowerte); Anlage SO"
        : "§ 22 Nr. 3 EStG (Sonstige Einkünfte aus Leistungen); BMF-Schreiben 06.03.2025";
    }

    if (c === "IT") {
      return t === "disposals"
        ? "Art. 67, comma 1, lett. c-sexies TUIR; Legge 197/2022; Circolare AE 27.10.2023"
        : "Proventi da cripto-attività (imposta sostitutiva); Legge 197/2022; Circolare AE 27.10.2023";
    }

    if (c === "GR") {
      return t === "disposals"
        ? "Greek ITC (Law 4172/2013) – income from capital gains; AADE income categories"
        : "Greek ITC (Law 4172/2013) – income categories; AADE income categories";
    }

    if (c === "UK" || c === "GB" || c.includes("UNITED KINGDOM") || c.includes("UNITED KINGDOM")) {
      return t === "disposals"
        ? "TCGA 1992 Section 104; same-day and 30-day matching; SA108 / Cryptoassets"
        : "HMRC Cryptoassets Manual: miscellaneous income; Self Assessment";
    }

    // US (default)
    if (c === "US" || c.includes("UNITED STATES")) {
      return t === "disposals"
        ? "IRS Notice 2014-21; IRC §1001 (dispositions – capital gain/loss)"
        : "IRS Notice 2014-21; IRC §61 (gross income – ordinary income)";
    }

    return "";
  }

  const finalFilenameDisposals = sanitizeFilename(
    isUS
      ? `V3Tax_Disposals_${yearInt || "year"}`
      : isUK
        ? `V3Tax_UK_Capital_Gains_SA108_${taxYearLabel || yearInt || "year"}`
        : isIT
          ? `V3Tax_Plusvalenze_${yearInt || "year"}`
          : isGR
            ? `V3Tax_GR_CapitalTransferGains_E1_4E_${yearInt || "year"}`
            : `V3Tax_Veraeusserungsgeschaefte_${yearInt || "year"}`
  );

  const finalFilenameOtherIncome = sanitizeFilename(
    isUS
      ? `V3Tax_Other_Income_${yearInt || "year"}`
      : isUK
        ? `V3Tax_UK_Miscellaneous_Income_${taxYearLabel || yearInt || "year"}`
        : isIT
          ? `V3Tax_Redditi_${yearInt || "year"}`
          : isGR
            ? `V3Tax_GR_OtherCryptoIncome_${yearInt || "year"}`
            : `V3Tax_Sonstige_Einkuenfte_${yearInt || "year"}`
  );

  const finalFilenameExcludedReview = sanitizeFilename(
    isUK
      ? `V3Tax_UK_Review_Items_${taxYearLabel || yearInt || "year"}`
      : `V3Tax_Excluded_Review_${yearInt || "year"}`
  );

  const finalFilenameRawTransfers = sanitizeFilename(
    isUS || isUK
      ? isUK
        ? `V3Tax_UK_Classified_OnChain_Ledger_through_${taxYearLabel || yearInt || "year"}`
        : `V3Tax_Classified_OnChain_Ledger_through_${yearInt || "year"}`
      : isIT
        ? `V3Tax_Ledger_OnChain_Classificato_fino_al_${yearInt || "year"}`
        : `V3Tax_Klassifiziertes_OnChain_Ledger_bis_${yearInt || "year"}`
  );

  const finalFilenameNftAcquisitions = sanitizeFilename(
    isUS || isUK
      ? isUK
        ? `V3Tax_UK_NFT_Acquisition_Ledger_${taxYearLabel || yearInt || "year"}`
        : `V3Tax_NFT_Acquisition_Ledger_${yearInt || "year"}`
      : isIT
        ? `V3Tax_NFT_Acquisti_${yearInt || "year"}`
        : `V3Tax_NFT_Anschaffungsledger_${yearInt || "year"}`
  );

  // FINAL CSV: 2 Spalten (lesbare Steuerkategorie + optionale Rechtsgrundlage nur für DE-Regime)
  
  const TAX_CATEGORY_LABEL = {
    disposals: isUS
      ? "Disposals"
      : isUK
        ? "Capital gains/losses"
        : isIT
          ? "Plusvalenze (Redditi diversi)"
          : isGR
            ? "Income from capital transfer gains (E1 Table 4E)"
            : "Veräußerungsgeschäfte",
    other_income: isUS
      ? "Other income"
      : isUK
        ? "Miscellaneous income"
        : isIT
          ? "Redditi (staking, ricompense, claim)"
          : isGR
            ? "Other crypto income (ordinary income)"
            : "Sonstige Einkünfte",
  };
  const LEGAL_REF = {
    disposals: isIT
      ? "TUIR Art. 67"
      : isUK
        ? "TCGA 1992 Section 104; same-day and 30-day matching; SA108 / Cryptoassets"
        : "EStG §23",
    other_income: isIT
      ? "TUIR Art. 6"
      : isUK
        ? "HMRC Cryptoassets Manual: miscellaneous income; Self Assessment"
        : "EStG §22 Nr. 3",
  };
  
  // 🚀 GR-spezifische Legal References
  if (isGR) {
    LEGAL_REF.disposals = "E1 Table 4E – Capital transfer gains (ITC Art. 42/43)";
    LEGAL_REF.other_income = "Ordinary income (E1; E3 if business)";
  }
  
  // UI-Labels bleiben Deutsch/Englisch (nicht italienisch)
  const UI_DISPOSALS_LABEL = language === "en" ? "Disposals" : "Veräußerungsgeschäfte";
  const UI_OTHER_INCOME_LABEL = language === "en" ? "Other income" : "Sonstige Einkünfte";

  const FINAL_DISPOSAL_HEADER_MAP_US = {
    Steuerjahr: "Tax year",
    Steuerkategorie: "Tax category",
    Rechtsgrundlage: "Legal reference (US)",
    Quelle: "Source",
    Klassifizierung: "Classification",
    Asset: "Asset",
    Menge: "Quantity",
    Datum_UTC: "Date (UTC)",
    [PROCEEDS_KEY]: `Proceeds ${fiatCurrency} (gross)`,
    [FEE_COST_KEY]: `Transaction costs ${fiatCurrency}`,
    [COST_KEY]: `Cost basis ${fiatCurrency} (gross)`,
    [PROFIT_KEY]: `Profit/Loss ${fiatCurrency} (gross)`,
    [TAXABLE_KEY]: `Taxable ${fiatCurrency}`,
    [ST_KEY]: `Short-term (≤ 1y) ${fiatCurrency}`,
    [LT_KEY]: `Long-term (> 1y) ${fiatCurrency}`,
    Haltezeit_min_Ta: "Holding period min (days)",
    Haltezeit_max_Ta: "Holding period max (days)",
    Anschaffungsdatum_min_UTC: "Acquired min (UTC)",
    Anschaffungsdatum_max_UTC: "Acquired max (UTC)",
    FIFO_Lots: "FIFO lots",
    Event_Key: "Event key",
    clause_index: "Clause index",
    event_index: "Event index",
    log_index: "Log index",
    token_contract: "Token contract",
    canonical_event_key: "Canonical event key",
    disposal_event_key: "Disposal event key",
    fifo_leg_index: "FIFO leg index",
    nft_transform_kind: "NFT transform kind",
    nft_transform_source_ordinal: "Transform source ordinal",
    nft_transform_result_token_id: "Result NFT token ID",
    allocation_method: "Proceeds allocation method",
    allocation_policy: "Allocation policy",
    allocation_basis: "Allocation basis",
    allocation_confidence: "Allocation confidence",
    allocation_safe_harbor: "Allocation legal safe harbor",
    valuation_method: "Valuation method",
    valuation_side: "Exchange value side",
    Tx_Hash: "Tx hash",
  };

  const EXCLUDED_REVIEW_HEADER_MAP_US = {
    Steuerjahr: "Tax year",
    Quelle: "Source",
    Klassifizierung: "Classification",
    Asset: "Asset",
    Menge: "Quantity",
    Datum_UTC: "Date (UTC)",
    [PROCEEDS_KEY]: `Proceeds ${fiatCurrency} (reference)`,
    Tx_Hash: "Tx hash",
    Event_Key: "Event key",
    Ausschlussgrund: "Exclusion reason",
    InSteuerSumme: "Included in tax totals",
    FIFO_Lots: "FIFO lots",
  };

  const FINAL_INCOME_HEADER_MAP_US = {
    Steuerjahr: "Tax year",
    Steuerkategorie: "Tax category",
    Rechtsgrundlage: "Legal reference (US)",
    Quelle: "Source",
    Klassifizierung: "Classification",
    Asset: "Asset",
    Menge: "Quantity",
    Datum_UTC: "Date (UTC)",
    [INCOME_KEY]: `Income ${fiatCurrency} (gross)`,
    [TAXABLE_KEY]: `Taxable ${fiatCurrency}`,
    Tx_Hash: "Tx hash",
    Event_Key: "Event key",
    clause_index: "Clause index",
    event_index: "Event index",
    log_index: "Log index",
    token_contract: "Token contract",
    canonical_event_key: "Canonical event key",
  };

  const FINAL_DISPOSAL_HEADER_MAP_UK = {
    Steuerjahr: "UK tax year",
    Steuerkategorie: "Tax category",
    Rechtsgrundlage: "UK tax treatment",
    Quelle: "Source",
    Klassifizierung: "Classification",
    Asset: "Asset",
    Menge: "Quantity",
    Datum_UTC: "Date (UTC)",
    [PROCEEDS_KEY]: `Proceeds ${fiatCurrency} (gross)`,
    [FEE_COST_KEY]: `Transaction costs ${fiatCurrency}`,
    [COST_KEY]: `Allowable cost ${fiatCurrency}`,
    [PROFIT_KEY]: `Gain/Loss ${fiatCurrency}`,
    [TAXABLE_KEY]: `Gain/Loss before AEA ${fiatCurrency}`,
    Haltezeit_min_Ta: "Matched acquisition offset min (days)",
    Haltezeit_max_Ta: "Matched acquisition offset max (days)",
    Anschaffungsdatum_min_UTC: "Matched acquisition min / pool",
    Anschaffungsdatum_max_UTC: "Matched acquisition max / pool",
    FIFO_Lots: "UK matching legs",
    Event_Key: "Event key",
    clause_index: "Clause index",
    event_index: "Event index",
    log_index: "Log index",
    token_contract: "Token contract",
    canonical_event_key: "Canonical event key",
    disposal_event_key: "Disposal event key",
    fifo_leg_index: "FIFO leg index",
    Tx_Hash: "Tx hash",
  };

  const EXCLUDED_REVIEW_HEADER_MAP_UK = {
    Steuerjahr: "UK tax year",
    Quelle: "Source",
    Klassifizierung: "Classification",
    Asset: "Asset",
    Menge: "Quantity",
    Datum_UTC: "Date (UTC)",
    [PROCEEDS_KEY]: `Proceeds ${fiatCurrency} (reference)`,
    Tx_Hash: "Tx hash",
    Event_Key: "Event key",
    Ausschlussgrund: "Review reason",
    InSteuerSumme: "Included in tax totals",
    FIFO_Lots: "UK matching legs",
  };

  const FINAL_INCOME_HEADER_MAP_UK = {
    Steuerjahr: "UK tax year",
    Steuerkategorie: "Tax category",
    Rechtsgrundlage: "UK tax treatment",
    Quelle: "Source",
    Klassifizierung: "Classification",
    Asset: "Asset",
    Menge: "Quantity",
    Datum_UTC: "Date received (UTC)",
    [INCOME_KEY]: `Income ${fiatCurrency} (gross)`,
    [TAXABLE_KEY]: `Taxable income ${fiatCurrency}`,
    Tx_Hash: "Tx hash",
    Event_Key: "Event key",
  };

  const FINAL_DISPOSAL_HEADER_MAP_DE = {
    Steuerjahr: "Steuerjahr",
    Steuerkategorie: "Steuerkategorie",
    Rechtsgrundlage: "Rechtsgrundlage (DE)",
    Quelle: "Quelle",
    Klassifizierung: "Klassifizierung",
    Asset: "Asset",
    Menge: "Menge",
    Datum_UTC: "Datum (UTC)",
    [PROCEEDS_KEY]: `Erlös ${fiatCurrency} (brutto)`,
    [FEE_COST_KEY]: `Transaktionskosten ${fiatCurrency}`,
    [COST_KEY]: `Anschaffungskosten ${fiatCurrency} (brutto)`,
    [PROFIT_KEY]: `Gewinn/Verlust ${fiatCurrency} (brutto)`,
    [TAXABLE_KEY]: `Steuerpflichtig ${fiatCurrency}`,
    [NONTAX_KEY]: `Steuerfrei ${fiatCurrency}`,
    Vollständig_steuerfrei: "Vollständig steuerfrei",
    Haltezeit_min_Ta: "Haltezeit min (Tage)",
    Haltezeit_max_Ta: "Haltezeit max (Tage)",
    Anschaffungsdatum_min_UTC: "Anschaffung min (UTC)",
    Anschaffungsdatum_max_UTC: "Anschaffung max (UTC)",
    FIFO_Lots: "FIFO-Lots",
    Event_Key: "Event-Schlüssel",
    clause_index: "Clause-Index",
    event_index: "Event-Index",
    log_index: "Log-Index",
    token_contract: "Token-Contract",
    canonical_event_key: "Kanonischer Event-Schlüssel",
    disposal_event_key: "Disposal-Event-Schlüssel",
    fifo_leg_index: "FIFO-Leg-Index",
    Tx_Hash: "Tx Hash",
  };

  const EXCLUDED_REVIEW_HEADER_MAP_DE = {
    Steuerjahr: "Steuerjahr",
    Quelle: "Quelle",
    Klassifizierung: "Klassifizierung",
    Asset: "Asset",
    Menge: "Menge",
    Datum_UTC: "Datum (UTC)",
    [PROCEEDS_KEY]: `Erlös ${fiatCurrency} (Referenz)`,
    Tx_Hash: "Tx Hash",
    Event_Key: "Event-Schlüssel",
    Ausschlussgrund: "Ausschlussgrund",
    InSteuerSumme: "In Steuer-Summe enthalten",
    FIFO_Lots: "FIFO-Lots",
  };

  const FINAL_INCOME_HEADER_MAP_DE = {
    Steuerjahr: "Steuerjahr",
    Steuerkategorie: "Steuerkategorie",
    Rechtsgrundlage: "Rechtsgrundlage (DE)",
    Quelle: "Quelle",
    Klassifizierung: "Klassifizierung",
    Asset: "Asset",
    Menge: "Menge",
    Datum_UTC: "Datum (UTC)",
    [INCOME_KEY]: `Einnahmen ${fiatCurrency} (brutto)`,
    [TAXABLE_KEY]: `Steuerpflichtig ${fiatCurrency}`,
    Tx_Hash: "Tx Hash",
  };

  const FINAL_DISPOSAL_HEADER_MAP_IT = {
    Steuerjahr: "Anno fiscale",
    Steuerkategorie: "Categoria fiscale",
    Rechtsgrundlage: "Riferimento legale (IT)",
    Quelle: "Fonte",
    Klassifizierung: "Classificazione",
    Asset: "Attività",
    Menge: "Quantità",
    Datum_UTC: "Data (UTC)",
    [PROCEEDS_KEY]: `Ricavi ${fiatCurrency} (lordi)`,
    [FEE_COST_KEY]: `Costi di transazione ${fiatCurrency}`,
    [COST_KEY]: `Costo di acquisto ${fiatCurrency} (lordi)`,
    [PROFIT_KEY]: `Utile/Perdita ${fiatCurrency} (lordi)`,
    [TAXABLE_KEY]: `Imponibile ${fiatCurrency}`,
    Haltezeit_min_Ta: "Periodo di detenzione min (giorni)",
    Haltezeit_max_Ta: "Periodo di detenzione max (giorni)",
    Anschaffungsdatum_min_UTC: "Acquistato min (UTC)",
    Anschaffungsdatum_max_UTC: "Acquistato max (UTC)",
    FIFO_Lots: "Lotti LIFO",
    Event_Key: "Chiave evento",
    clause_index: "Indice clause",
    event_index: "Indice event",
    log_index: "Indice log",
    token_contract: "Contratto token",
    canonical_event_key: "Chiave evento canonica",
    disposal_event_key: "Chiave evento disposal",
    fifo_leg_index: "Indice leg FIFO",
    Tx_Hash: "Hash transazione",
  };

  const EXCLUDED_REVIEW_HEADER_MAP_IT = {
    Steuerjahr: "Anno fiscale",
    Quelle: "Fonte",
    Klassifizierung: "Classificazione",
    Asset: "Attività",
    Menge: "Quantità",
    Datum_UTC: "Data (UTC)",
    [PROCEEDS_KEY]: `Ricavi ${fiatCurrency} (riferimento)`,
    Tx_Hash: "Hash transazione",
    Event_Key: "Chiave evento",
    Ausschlussgrund: "Motivo esclusione",
    InSteuerSumme: "Incluso nei totali fiscali",
    FIFO_Lots: "Lotti LIFO",
  };

  const FINAL_INCOME_HEADER_MAP_IT = {
    Steuerjahr: "Anno fiscale",
    Steuerkategorie: "Categoria fiscale",
    Rechtsgrundlage: "Riferimento legale (IT)",
    Quelle: "Fonte",
    Klassifizierung: "Classificazione",
    Asset: "Attività",
    Menge: "Quantità",
    Datum_UTC: "Data (UTC)",
    [INCOME_KEY]: `Redditi ${fiatCurrency} (lordi)`,
    [TAXABLE_KEY]: `Imponibile ${fiatCurrency}`,
    Tx_Hash: "Hash transazione",
  };

  const FINAL_DISPOSAL_HEADER_MAP_GR = {
    Steuerjahr: "Tax year",
    Steuerkategorie: "Tax category",
    Rechtsgrundlage: "Legal reference (GR)",
    Quelle: "Source",
    Klassifizierung: "Classification",
    Asset: "Asset",
    Menge: "Quantity",
    Datum_UTC: "Date (UTC)",
    [PROCEEDS_KEY]: `Proceeds ${fiatCurrency} (gross)`,
    [FEE_COST_KEY]: `Transaction costs ${fiatCurrency}`,
    [COST_KEY]: `Cost basis ${fiatCurrency} (gross)`,
    [PROFIT_KEY]: `Profit/Loss ${fiatCurrency} (gross)`,
    [TAXABLE_KEY]: `Taxable ${fiatCurrency}`,
    Haltezeit_min_Ta: "Holding period min (days)",
    Haltezeit_max_Ta: "Holding period max (days)",
    Anschaffungsdatum_min_UTC: "Acquired min (UTC)",
    Anschaffungsdatum_max_UTC: "Acquired max (UTC)",
    FIFO_Lots: "FIFO lots",
    Event_Key: "Event key",
    clause_index: "Clause index",
    event_index: "Event index",
    log_index: "Log index",
    token_contract: "Token contract",
    canonical_event_key: "Canonical event key",
    disposal_event_key: "Disposal event key",
    fifo_leg_index: "FIFO leg index",
    Tx_Hash: "Tx hash",
  };

  const EXCLUDED_REVIEW_HEADER_MAP_GR = {
    Steuerjahr: "Tax year",
    Quelle: "Source",
    Klassifizierung: "Classification",
    Asset: "Asset",
    Menge: "Quantity",
    Datum_UTC: "Date (UTC)",
    [PROCEEDS_KEY]: `Proceeds ${fiatCurrency} (reference)`,
    Tx_Hash: "Tx hash",
    Event_Key: "Event key",
    Ausschlussgrund: "Exclusion reason",
    InSteuerSumme: "Included in tax totals",
    FIFO_Lots: "FIFO lots",
  };

  const FINAL_INCOME_HEADER_MAP_GR = {
    Steuerjahr: "Tax year",
    Steuerkategorie: "Tax category",
    Rechtsgrundlage: "Legal reference (GR)",
    Quelle: "Source",
    Klassifizierung: "Classification",
    Asset: "Asset",
    Menge: "Quantity",
    Datum_UTC: "Date (UTC)",
    [INCOME_KEY]: `Income ${fiatCurrency} (gross)`,
    [TAXABLE_KEY]: `Taxable ${fiatCurrency}`,
    Tx_Hash: "Tx hash",
  };

  const finalDisposalHeaderMap = isUS
    ? FINAL_DISPOSAL_HEADER_MAP_US
    : isUK
      ? FINAL_DISPOSAL_HEADER_MAP_UK
      : isIT
        ? FINAL_DISPOSAL_HEADER_MAP_IT
        : isGR
          ? FINAL_DISPOSAL_HEADER_MAP_GR
          : FINAL_DISPOSAL_HEADER_MAP_DE;

  const finalIncomeHeaderMap = isUS
    ? FINAL_INCOME_HEADER_MAP_US
    : isUK
      ? FINAL_INCOME_HEADER_MAP_UK
      : isIT
        ? FINAL_INCOME_HEADER_MAP_IT
        : isGR
          ? FINAL_INCOME_HEADER_MAP_GR
          : FINAL_INCOME_HEADER_MAP_DE;

  const replaceCurrencyInHeaderMap = (hm) => {
    if (!hm) return hm;
    const out = {};
    for (const [k, v] of Object.entries(hm)) {
      const s = String(v ?? "");
      out[k] = s
        .replace(/UNKNOWN/g, fiatCurrency)
        .replace(/EUR/g, fiatCurrency)
        .replace(/USD/g, fiatCurrency);
    }
    return out;
  };

  const finalDisposalHeaderMapForExport = replaceCurrencyInHeaderMap(finalDisposalHeaderMap);

  const excludedReviewHeaderMap = isUS
    ? EXCLUDED_REVIEW_HEADER_MAP_US
    : isUK
      ? EXCLUDED_REVIEW_HEADER_MAP_UK
      : isIT
        ? EXCLUDED_REVIEW_HEADER_MAP_IT
        : isGR
          ? EXCLUDED_REVIEW_HEADER_MAP_GR
          : EXCLUDED_REVIEW_HEADER_MAP_DE;
  const excludedReviewHeaderMapForExport =
    replaceCurrencyInHeaderMap(excludedReviewHeaderMap);
  const finalIncomeHeaderMapForExport = replaceCurrencyInHeaderMap(finalIncomeHeaderMap);

  // ---------- helpers ----------
  const parseDateLikeToMs = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") {
      if (!Number.isFinite(value)) return 0;
      return value < 1e12 ? value * 1000 : value;
    }

    const s = String(value).trim();
    if (!s) return 0;

    let m = s.match(
      /^(\d{2})\.(\d{2})\.(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (m) {
      const [, dd, mm, yyyy, hh = "0", mi = "0", ss = "0"] = m;
      return new Date(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(hh),
        Number(mi),
        Number(ss)
      ).getTime();
    }

    m = s.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i
    );
    if (m) {
      let [, p1, p2, yyyy, hh = "0", mi = "0", ss = "0", ampm = ""] = m;

      let day;
      let month;

      if (ampm) {
        month = Number(p1);
        day = Number(p2);
      } else if (Number(p1) > 12) {
        day = Number(p1);
        month = Number(p2);
      } else if (Number(p2) > 12) {
        month = Number(p1);
        day = Number(p2);
      } else {
        month = Number(p1);
        day = Number(p2);
      }

      let hour = Number(hh);
      if (ampm) {
        const upper = ampm.toUpperCase();
        if (upper === "PM" && hour < 12) hour += 12;
        if (upper === "AM" && hour === 12) hour = 0;
      }

      return new Date(
        Number(yyyy),
        month - 1,
        day,
        hour,
        Number(mi),
        Number(ss)
      ).getTime();
    }

    const parsed = new Date(s).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const toSec = (ts) => {
    const ms = parseDateLikeToMs(ts);
    return ms ? Math.floor(ms / 1000) : 0;
  };

  const inYear = (tsSec) => rowInTaxYear(tsSec);

  // CSV-Export: KEINE harte Rundung hier.
  // ExportButton formatiert Zahlen zentral (plain decimals, keine Sci-Notation).

  const csvLocale = isUS ? "en-US" : isUK ? "en-GB" : isIT ? "it-IT" : "de-DE";

  const formatCsvDateTime = (timestamp, { utc = false } = {}) => {
    const tsSec = toSec(timestamp);
    if (!tsSec) return "";

    if (utc) {
      return formatCsvDateTimeUtc(tsSec);
    }

    const d = new Date(tsSec * 1000);

    return d.toLocaleString(csvLocale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: isUS ? "numeric" : "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: isUS,
    });
  };

  const fmtUtc = (tsSec) => formatCsvDateTimeUtc(tsSec);

  // robust number helpers (avoid "" => 0) + handle "€ 1.234,56" / "1,23" / "1 234.56"
  const toNum = (v) => {
    if (v === null || v === undefined) return NaN;
    if (typeof v === "number") return Number.isFinite(v) ? v : NaN;

    let s = String(v).trim();
    if (!s) return NaN;

    // remove currency + whitespace (EUR + USD)
    s = s
      .replace(/€/g, "")
      .replace(/£/g, "")
      .replace(/\$/g, "")
      .replace(/EUR/gi, "")
      .replace(/USD/gi, "")
      .replace(/GBP/gi, "")
      .replace(/CHF/gi, "")
      .replace(/\s+/g, "");

    // If both "." and "," occur, assume "." thousands and "," decimal (DE-style)
    if (s.includes(",") && s.includes(".")) {
      s = s.replace(/\./g, "").replace(",", ".");
    } else if (s.includes(",") && !s.includes(".")) {
      // only comma -> decimal comma
      s = s.replace(",", ".");
    }

    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  };
  const firstFinite = (...vals) => {
    for (const v of vals) {
      const n = toNum(v);
      if (Number.isFinite(n)) return n;
    }
    return NaN;
  };

  const parseBool = (value) => {
    if (value === true || value === 1) return true;
    const s = String(value ?? "").trim().toLowerCase();
    return ["true", "1", "ja", "yes", "si", "sì"].includes(s);
  };

  const boolLabel = (value) => {
    if (isIT) return parseBool(value) ? "Sì" : "No";
    if (isDE) return parseBool(value) ? "Ja" : "Nein";
    return parseBool(value) ? "Yes" : "No";
  };

  const pickHash = (r) => String(r?.hash || r?.txid || r?.event_id || r?.txHash || "").trim();
  const pickCls = (r) => String(r?.classification || r?.type || r?.kind || "").toLowerCase();
  const pickToken = (r) => String(r?.tokenSymbol || r?.token || r?.base || r?.asset || "").toUpperCase();

  const isNftTransformDisposalRow = (r = {}) =>
    String(
      r?.classification ||
      r?.type ||
      ""
    )
      .trim()
      .toLowerCase() ===
    "nft_transform_disposal";

  const nftTransformSourceLegsOf = (r = {}) =>
    Array.isArray(
      r?.sourceAcquisitionLegs
    )
      ? r.sourceAcquisitionLegs
      : [];

  const pickNftTransformCollectionName =
    (r = {}) => {
      const firstSource =
        nftTransformSourceLegsOf(r)[0];

      const direct =
        String(
          firstSource?.nftName ||
          r?.nftTransformResultNft
            ?.nftName ||
          r?.nftTransformAcquisition
            ?.nftName ||
          r?.nftName ||
          ""
        ).trim();

      if (
        direct &&
        !direct
          .toLowerCase()
          .includes("input bundle")
      ) {
        return direct;
      }

      const kind =
        String(
          r?.nftTransformKind || ""
        ).toLowerCase();

      if (
        kind === "thorhead_remint"
      ) {
        return "Thorheads";
      }

      if (
        kind === "vemugs_melt"
      ) {
        return "VeMugs";
      }

      return "NFT";
    };

  const pickExportAsset = (r = {}) => {
    if (
      !isNftTransformDisposalRow(r)
    ) {
      return pickToken(r);
    }

    const collection =
      pickNftTransformCollectionName(r);

    const tokenId =
      r?.tokenId ??
      r?.nft_token_id ??
      r?.token_id ??
      null;

    if (
      tokenId != null &&
      tokenId !== ""
    ) {
      return `${collection} NFT #${tokenId}`;
    }

    const sourceIds = (
      Array.isArray(r?.sourceTokenIds)
        ? r.sourceTokenIds
        : nftTransformSourceLegsOf(r)
            .map(
              (leg) => leg?.tokenId
            )
    )
      .filter(
        (id) =>
          id != null &&
          id !== ""
      )
      .map(
        (id) => `#${id}`
      );

    return sourceIds.length > 0
      ? `${collection} NFTs ${sourceIds.join(" + ")}`
      : `${collection} NFT bundle`;
  };

  const pickNftTransformTokenContract = (r = {}) => {
    const direct = String(
      r?.nftContract ||
        r?.token_contract ||
        r?.tokenContract ||
        r?.contract ||
        ""
    )
      .trim()
      .toLowerCase();
    if (direct) return direct;

    const legs = nftTransformSourceLegsOf(r);
    const contracts = [
      ...new Set(
        legs
          .map((leg) =>
            String(leg?.nftContract || "")
              .trim()
              .toLowerCase()
          )
          .filter(Boolean)
      ),
    ];
    return contracts.length === 1 ? contracts[0] : "";
  };

  /**
   * Stable 1/2/3 source ordinal for known
   * 3→1 NFT transform disposals.
   */
  const pickNftTransformSourceOrdinal = (
    r = {},
    leg = null,
    fallbackIndex = 0
  ) => {
    if (!isNftTransformDisposalRow(r)) {
      return null;
    }

    /*
     * Preferred:
     * backend already supplies ordinal.
     */
    const explicitOrdinal = firstFinite(
      r?.nftTransformSourceOrdinal,
      leg?.nftTransformSourceOrdinal,
      leg?.transformSourceOrdinal
    );

    if (Number.isFinite(explicitOrdinal) && explicitOrdinal >= 1) {
      return Math.trunc(explicitOrdinal);
    }

    /*
     * Backend source index is zero-based.
     */
    const zeroBased = firstFinite(
      r?.nftTransformSourceIndex,
      leg?.nftTransformSourceIndex
    );

    if (Number.isFinite(zeroBased) && zeroBased >= 0) {
      return Math.trunc(zeroBased) + 1;
    }

    /*
     * Already normalized FIFO leg identity.
     */
    const explicitLeg = firstFinite(
      leg?.fifoLegIndex,
      leg?.fifo_leg_index,
      leg?.legIndex,
      leg?.leg_index
    );

    if (Number.isFinite(explicitLeg) && explicitLeg >= 1) {
      return Math.trunc(explicitLeg);
    }

    return Math.max(1, Number(fallbackIndex || 0) + 1);
  };

  /**
   * Deterministic identity for ONE surrendered NFT.
   *
   * IMPORTANT:
   * Never reuse one shared transform-parent identity
   * for all three source NFT disposals.
   */
  const buildNftTransformDisposalExportKey = (
    r = {},
    fallbackEventKey = "",
    fallbackIndex = 0
  ) => {
    if (!isNftTransformDisposalRow(r)) {
      return fallbackEventKey;
    }

    const hash = String(
      r?.nftTransformBurnHash || pickHash(r) || "nft-transform"
    )
      .trim()
      .toLowerCase();

    const ordinal = pickNftTransformSourceOrdinal(
      r,
      r?.fifoLegs?.[0] || null,
      fallbackIndex
    );

    const contract = pickNftTransformTokenContract(r) || "no-contract";

    const tokenId = String(
      r?.tokenId ??
        r?.nft_token_id ??
        r?.token_id ??
        `source-${ordinal}`
    ).trim();

    return (
      `${hash}` +
      `#nft_transform_disposal:` +
      `${ordinal}:` +
      `${contract}:` +
      `${tokenId || `source-${ordinal}`}`
    );
  };

  /**
   * Extra machine-readable metadata
   * for final US disposal CSV.
   */
  const nftTransformExportMetaOf = (
    r = {},
    leg = null,
    fallbackIndex = 0
  ) => {
    if (!isNftTransformDisposalRow(r)) {
      return {};
    }

    const ordinal = pickNftTransformSourceOrdinal(r, leg, fallbackIndex);

    return {
      nft_transform_kind: r?.nftTransformKind || "",
      nft_transform_source_ordinal: ordinal,
      nft_transform_result_token_id:
        r?.nftTransformResultTokenId ??
        r?.nftTransformAcquisition?.tokenId ??
        "",
      allocation_method: r?.proceedsAllocationMethod || "",
      allocation_policy: r?.allocationPolicy || "",
      allocation_basis: r?.allocationBasis || "",
      allocation_confidence: r?.allocationMethodConfidence || "",
      allocation_safe_harbor:
        r?.allocationLegalSafeHarbor == null
          ? ""
          : String(r.allocationLegalSafeHarbor),
      valuation_method: r?.valuationMethod || "",
      valuation_side: r?.exchangeValueSide || "",
    };
  };

  const pickAmount = (r) => {
    const raw = firstFinite(r?.amount, r?.qty, r?.quantity, r?.lpAmount, r?.tokenAmount, 0) || 0;
    const exported = pickExportVthoTokenAmount(r);
    return exported != null && Number.isFinite(Number(exported)) ? exported : raw;
  };

  const pickRowTs = (r) =>
    r?.timestamp ??
    r?.ts ??
    r?.block_timestamp ??
    r?.blockTimestamp ??
    r?.blockTime ??
    r?.time ??
    r?.tx_timestamp ??
    r?.txTimestamp ??
    r?.sell_ts ??
    r?.sellTs ??
    r?.sold_ts ??
    r?.soldTs ??
    r?.out_ts ??
    r?.outTs ??
    r?.date ??
    r?.datetime ??
    r?.createdAt ??
    r?.executedAt ??
    0;

  /** Tax-Tabellen-CSV: nur Zeilen im gewählten Steuerjahr (Raw-Transfers bleiben ungefiltert). */
  const filterRowsToSelectedYear = (rows = []) =>
    (Array.isArray(rows) ? rows : []).filter((r) => inYear(toSec(pickRowTs(r))));

  const pickRawEventRef = (r) => {
    const v =
      r?.eventRef ??
      r?.event_id ??
      r?.eventId ??
      r?.logIndex ??
      r?.log_index ??
      r?.transferIndex ??
      r?.transfer_index ??
      r?.clauseIndex ??
      r?.clause_index ??
      r?.eventIndex ??
      r?.event_index ??
      r?.subIndex ??
      r?.sub_index ??
      r?.idx ??
      r?.index ??
      "";

    return v === "" || v === null || v === undefined ? "" : String(v);
  };

  const ZERO_ADDRESS =
    "0x0000000000000000000000000000000000000000";

  const normalizeLedgerWalletAddress = (value) => {
    const raw = String(value || "")
      .trim()
      .toLowerCase();

    const address = raw.startsWith("wallet:")
      ? raw.slice("wallet:".length)
      : raw;

    if (!/^0x[a-f0-9]{40}$/.test(address)) {
      return "";
    }

    if (address === ZERO_ADDRESS) {
      return "";
    }

    return address;
  };

  const ownedLedgerWalletAddresses =
    React.useMemo(() => {
      const values = [
        ...(walletSet?.mainAddresses || []),
        ...(walletSet?.mainWallets || []),
        ...(walletSet?.addresses || []),
        ...(walletSet?.delegateAddresses || []),
        ...(walletSet?.delegateWallets || []),
        ...(walletSet?.voteRewardAddresses || []),
      ];

      return new Set(
        values
          .map(normalizeLedgerWalletAddress)
          .filter(Boolean)
      );
    }, [walletSet]);

  const resolveClassifiedLedgerCustody = (
    row = {},
    {
      isGasFee = false,
      gasPayer = "",
    } = {}
  ) => {
    const from = normalizeLedgerWalletAddress(
      row.from ||
      row.sender ||
      gasPayer
    );

    const to = normalizeLedgerWalletAddress(
      row.to ||
      row.recipient ||
      row.receiver
    );

    /*
     * Raw-Transfer-Datensätze enthalten häufig die Wallet, aus deren
     * Perspektive die Zeile geladen wurde. Das ist ein wichtiger
     * Fallback für ältere Snapshots.
     */
    const rowWallet = normalizeLedgerWalletAddress(
      row.wallet ||
      row.walletAddress ||
      row.wallet_address ||
      row.mainWallet ||
      row.main_wallet
    );

    const isOwned = (address) =>
      Boolean(
        address &&
        (
          ownedLedgerWalletAddresses.has(address) ||
          (
            rowWallet &&
            address === rowWallet
          )
        )
      );

    const fromOwned = isOwned(from);
    const toOwned = isOwned(to);

    /*
     * Gas Fee: Payer beziehungsweise explizite Source-Custody.
     */
    if (isGasFee) {
      const candidates = [
        row.custodyLocationId,
        row.custody_location_id,
        row.sourceCustodyLocationId,
        row.source_custody_location_id,
        gasPayer,
        row.from,
        row.sender,
      ];

      for (const candidate of candidates) {
        const raw = String(candidate || "").trim();

        if (raw.toLowerCase().startsWith("exchange:")) {
          return raw.toLowerCase();
        }

        const address =
          normalizeLedgerWalletAddress(candidate);

        if (isOwned(address)) {
          return raw.toLowerCase().startsWith("wallet:")
            ? raw.toLowerCase()
            : address;
        }
      }

      return "";
    }

    /*
     * Wichtigste Regel:
     *
     * Gehört genau eine Transferseite zum Wallet-Set, ist diese Seite
     * die steuerliche Custody.
     *
     * Dadurch funktionieren auch Transform-, Mint-, Refund-,
     * Liquidity- und Stargate-Klassen ohne fragile Classifier-Liste.
     */
    if (fromOwned !== toOwned) {
      return fromOwned
        ? from
        : to;
    }

    /*
     * Explizite Custody-Felder nur übernehmen, wenn sie tatsächlich
     * eine eigene Wallet oder ein Exchange-Konto bezeichnen.
     *
     * Zero Address und fremde Contracts werden nicht akzeptiert.
     */
    const explicitCandidates = [
      row.custodyLocationId,
      row.custody_location_id,

      row.targetCustodyLocationId,
      row.target_custody_location_id,

      row.sourceCustodyLocationId,
      row.source_custody_location_id,

      row.wallet,
      row.walletAddress,
      row.wallet_address,
      row.mainWallet,
      row.main_wallet,
    ];

    for (const candidate of explicitCandidates) {
      const raw = String(candidate || "")
        .trim()
        .toLowerCase();

      if (!raw) {
        continue;
      }

      if (raw.startsWith("exchange:")) {
        return raw;
      }

      const address =
        normalizeLedgerWalletAddress(raw);

      if (isOwned(address)) {
        return raw.startsWith("wallet:")
          ? raw
          : address;
      }
    }

    /*
     * Interner Transfer: beide Seiten gehören zum Wallet-Set.
     * Die Sender-/Empfänger-Spalten dokumentieren die Bewegung.
     * Für das einzelne Custody-Feld bleibt die Source-Custody.
     */
    if (fromOwned && toOwned) {
      return from;
    }

    /*
     * Fail closed:
     * Fremde Sender, Contracts oder Zero Address niemals als
     * steuerliche Custody ausgeben.
     */
    return "";
  };

  const normalizeCsvRows = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((row) => {
      const tsSec =
        toSec(pickRowTs(row));

      const identity =
        extractEventIdentityFields(row);

      const token =
        pickToken(row);

      const rawClassification = String(
        row?.classification ||
          row?.type ||
          ""
      ).toLowerCase();

      const taxClassification =
        normalizeTaxClassificationForExport(
          rawClassification,
          token
        ) || rawClassification;

      const canonicalEventKey =
        identity.canonical_event_key ||
        buildCanonicalEventKeyFromRow(
          row,
          {
            txHash: pickHash(row),
            kind:
              taxClassification ||
              "raw_transfer",
          }
        );

      const isGasFee =
        taxClassification === "gas_fee" ||
        taxClassification.includes("gas_fee");

      const gasPayer =
        row.gasPayer ||
        row.from ||
        row.meta?.gasPayer ||
        "";

      const custodyLocationId =
        resolveClassifiedLedgerCustody(
          row,
          {
            isGasFee,
            gasPayer,
          }
        );

      return {
        ...row,

        type:
          taxClassification ||
          row.type,

        classification:
          taxClassification ||
          row.classification,

        eventRef:
          pickRawEventRef(row),

        date: tsSec
          ? formatCsvDateTimeUtc(tsSec)
          : String(row?.date || ""),

        sortTimestamp:
          tsSec || 0,

        clause_index:
          identity.clause_index,

        event_index:
          identity.event_index,

        log_index:
          identity.log_index,

        token_contract:
          identity.token_contract,

        canonical_event_key:
          canonicalEventKey || null,

        disposal_event_key:
          identity.disposal_event_key,

        fifo_leg_index:
          identity.fifo_leg_index,

        sender:
          row.sender ||
          row.from ||
          gasPayer ||
          row.source ||
          "",

        gasPayer: isGasFee
          ? gasPayer || ""
          : (
              row.gasPayer ||
              row.meta?.gasPayer ||
              ""
            ),

        custodyLocationId,
      };
    });

  const normalizedVetVthoRawData = React.useMemo(
    () => normalizeCsvRows(vetVthoData || []),
    [vetVthoData, csvLocale, walletSet, ownedLedgerWalletAddresses]
  );

  const normalizedVip180RawData = React.useMemo(
    () => normalizeCsvRows(vip180Data || []),
    [vip180Data, csvLocale, walletSet, ownedLedgerWalletAddresses]
  );

  const normalizedExchangeRawData = React.useMemo(() => {
    if (!Array.isArray(exchangeData)) return [];

    return exchangeData.map((row) => {
      const tsSec = toSec(row.timestamp ?? row.ts ?? row.date ?? row.time ?? 0);
      const kind = String(row.kind || row.type || "").toLowerCase();
      const side = String(row.side || "").toUpperCase();

      let exchangeVendor = String(
        row.exchange_source ||
        row.exchangeSource ||
        row.source ||
        "unknown"
      )
        .trim()
        .toLowerCase();

      while (exchangeVendor.startsWith("exchange:")) {
        exchangeVendor =
          exchangeVendor.slice("exchange:".length);
      }

      exchangeVendor =
        exchangeVendor.split(":").filter(Boolean)[0] ||
        "unknown";

      const exchangeAccountId = String(
        row.exchange_account_id ||
        row.exchangeAccountId ||
        row.accountId ||
        "default"
      )
        .trim()
        .toLowerCase() ||
        "default";

      const exchangeCustodyLocationId =
        row.custodyLocationId ||
        row.custody_location_id ||
        `exchange:${exchangeVendor}:${exchangeAccountId}`;

      return {
        rawSource: "Exchange",
        type: side ? `exchange_${kind}_${side.toLowerCase()}` : `exchange_${kind}`,
        token: row.base || "",
        amount: row.amount ?? "",
        quote: row.quote || "",
        side: row.side || "",
        price: row.price ?? "",
        fee: row.fee ?? "",
        feeAsset: row.feeAsset || "",
        recipient: row.source || "",
        sender: row.source || "",
        custodyLocationId: exchangeCustodyLocationId,
        hash: row.txid || "",
        date: tsSec ? formatCsvDateTimeUtc(tsSec) : String(row.date || ""),
        eventRef: `exchange:${row.source || ""}:${row.txid || tsSec || ""}`,
        sortTimestamp: tsSec || 0,
        notes: row.notes || "",
      };
    });
  }, [exchangeData, csvLocale]);

  const normalizedGasFeeLedgerData =
    React.useMemo(() => {
      const sourceRows =
        getVthoDisplayRows(ds, vthoData);

      return sourceRows
        .filter(isVthoGasFeeRow)
        .filter((row) => {
          const tsSec = toSec(pickRowTs(row));
          return rowInTaxYear(tsSec, yearInt);
        })
        .map((row, index) => {
          const tsSec =
            toSec(pickRowTs(row));

          const token =
            pickToken(row) || "VTHO";

          const amount =
            Number(pickAmount(row) || 0);

          const fiatPrice =
            finiteMoneyOrNull(
              pickFiatPrice(row)
            );

          const fiatValue =
            finiteMoneyOrNull(
              pickFiatSell(row) ??
              pickFiatTotal(row)
            );

          const directCost =
            finiteMoneyOrNull(
              pickFiatBuy(row) ??
              row.totalCost ??
              row.costBasisFiat ??
              row.costBasisEUR
            );

          const costBasis =
            directCost !== null
              ? directCost
              : (
                  Array.isArray(row.fifoLegs)
                    ? row.fifoLegs.reduce(
                        (sum, leg) => {
                          const qty = Number(
                            leg.qty ??
                            leg.quantity ??
                            leg.amount ??
                            0
                          );

                          const unit = Number(
                            leg.unitCostFiat ??
                            leg.unit_cost_eur ??
                            leg.unit_cost ??
                            0
                          );

                          return (
                            Number.isFinite(qty) &&
                            Number.isFinite(unit)
                          )
                            ? sum + qty * unit
                            : sum;
                        },
                        0
                      )
                    : null
                );

          const directGain =
            finiteMoneyOrNull(
              pickFiatGrossProfit(row)
            );

          const gainLoss =
            directGain !== null
              ? directGain
              : (
                  fiatValue !== null &&
                  costBasis !== null
                    ? fiatValue - costBasis
                    : null
                );

          const taxableFiat =
            finiteMoneyOrNull(
              pickFiatTaxable(row)
            );

          const hash =
            pickHash(row);

          const id =
            extractEventIdentityFields(row);

          return {
            rawSource:
              "GAS_FEE_DISPOSAL",

            type:
              String(
                row.classification ||
                row.type ||
                "gas_fee"
              ).toLowerCase(),

            token,
            amount,

            quote: fiatCurrency,
            side: "DISPOSAL",

            price: fiatPrice,
            fiatCurrency,
            fiatPrice,
            fiatValue,
            costBasisFiat: costBasis,
            gainLossFiat: gainLoss,
            taxableFiat,

            fee: amount,
            feeAsset: token,

            recipient:
              row.to ||
              row.recipient ||
              "chain:gas",

            sender:
              row.from ||
              row.gasPayer ||
              row.meta?.gasPayer ||
              "",

            gasPayer:
              row.gasPayer ||
              row.from ||
              row.meta?.gasPayer ||
              "",

            custodyLocationId:
              resolveClassifiedLedgerCustody(
                row,
                {
                  isGasFee: true,
                  gasPayer:
                    row.gasPayer ||
                    row.from ||
                    row.meta?.gasPayer ||
                    "",
                }
              ),

            hash,

            date:
              tsSec
                ? formatCsvDateTimeUtc(tsSec)
                : "",

            eventRef:
              row.eventId ||
              row.event_id ||
              row.exportEventKey ||
              `gas-fee:${hash || index}`,

            clause_index:
              id.clause_index,

            event_index:
              id.event_index,

            log_index:
              id.log_index,

            token_contract:
              id.token_contract,

            notes:
              "Receipt-derived fee-token disposal; individual market value and cost basis calculated at transaction time.",

            sortTimestamp:
              tsSec || 0,
          };
        });
    }, [
      ds?.vthoTaxRows,
      vthoData,
      yearInt,
      selectedCountry,
      fiatCurrency,
      csvLocale,
      walletSet,
      ownedLedgerWalletAddresses,
    ]);

  const finalRawTransfersCsv =
    React.useMemo(() => {
      const nativeRows =
        (normalizedVetVthoRawData || []).map(
          (row) => ({
            ...row,
            rawSource: "VET/VTHO",
          })
        );

      const vip180Rows =
        (normalizedVip180RawData || []).map(
          (row) => ({
            ...row,
            rawSource: "VIP-180",
          })
        );

      const transformEvents = Array.isArray(
        ds?.blockpitNftTransformEvents
      )
        ? ds.blockpitNftTransformEvents
        : [];

      const transformRows = transformEvents.map((event, index) => {
        const inputs = Array.isArray(event?.inputs)
          ? event.inputs
          : [];
        const outputs = Array.isArray(event?.outputs)
          ? event.outputs
          : [];
        const sourceTokenIds = inputs
          .map((input) => input?.tokenId)
          .filter((id) => id != null && id !== "")
          .map(String)
          .join("+");
        const resultTokenId =
          outputs[0]?.tokenId ??
          null;
        const tsSec = toSec(
          event?.timestamp ||
            event?.mintedAt ||
            0
        );
        const burnHash = String(
          event?.burnHash ||
            event?.hash ||
            ""
        ).trim();
        const mintHash = String(
          event?.mintHash ||
            ""
        ).trim();
        const lifecycleKey = String(
          event?.lifecycleKey || ""
        ).trim();
        const classification = "nft_transform";
        const identity = extractEventIdentityFields({
          hash: burnHash || mintHash || lifecycleKey || `transform:${index}`,
          classification,
          type: classification,
          token_contract:
            inputs[0]?.contract ||
            outputs[0]?.contract ||
            "",
        });

        return {
          rawSource: "NFT_TRANSFORM",
          type: classification,
          classification,
          token: "NFT",
          amount:
            Number(event?.mechanism?.inputCount || inputs.length || 0) ||
            "",
          quote: "",
          side: "TRANSFORM",
          price: "",
          fiatCurrency:
            event?.valuation?.currency ||
            fiatCurrency ||
            "",
          fiatPrice: "",
          fiatValue:
            event?.valuation?.exchangeValueFiat ??
            "",
          costBasisFiat:
            event?.valuation?.resultAcquisitionCostFiat ??
            "",
          gainLossFiat: "",
          taxableFiat: "",
          fee: "",
          feeAsset: "",
          recipient: "",
          sender: "",
          gasPayer: "",
          custodyLocationId: "",
          hash: burnHash || mintHash || "",
          date: tsSec ? formatCsvDateTimeUtc(tsSec) : "",
          sortTimestamp: tsSec || 0,
          eventRef: lifecycleKey || burnHash || mintHash || "",
          notes:
            event?.taxPolicy?.reviewRequired === true
              ? String(event?.taxPolicy?.reviewReason || "review_required")
              : "",
          nft_transform_kind:
            event?.transformKind || "",
          nft_source_token_ids: sourceTokenIds,
          nft_result_token_id:
            resultTokenId == null ? "" : String(resultTokenId),
          nft_mint_hash: mintHash,
          nft_lifecycle_key: lifecycleKey,
          valuation_method: event?.valuation?.method || "",
          valuation_source: event?.valuation?.resultFmvSource || "",
          valuation_confidence:
            event?.valuation?.valuationConfidence ||
            event?.valuation?.primaryMintReference?.valuationConfidence ||
            "",
          valuation_reference_type:
            event?.valuation?.primaryMintReference?.referenceType || "",
          valuation_reference_hash:
            event?.valuation?.primaryMintReference?.referenceHash || "",
          valuation_reference_timestamp:
            event?.valuation?.primaryMintReference?.referenceTimestamp || "",
          valuation_reference_age_days:
            event?.valuation?.primaryMintReference?.evidenceAgeDays ?? "",
          allocation_method:
            event?.valuation?.proceedsAllocationMethod || "",
          allocation_policy: event?.valuation?.allocationPolicy || "",
          allocation_basis: event?.valuation?.allocationBasis || "",
          allocation_confidence:
            event?.valuation?.allocationMethodConfidence || "",
          allocation_safe_harbor:
            event?.valuation?.allocationLegalSafeHarbor == null
              ? ""
              : String(event.valuation.allocationLegalSafeHarbor),
          ...identity,
        };
      });

      const combined = [
        ...nativeRows,
        ...vip180Rows,
        ...normalizedExchangeRawData,
        ...normalizedGasFeeLedgerData,
        ...transformRows,
      ];

      const seen = new Set();
      const deduped = [];

      for (const row of combined) {
        const key =
          buildRawLedgerDedupeKey(row);

        if (seen.has(key)) {
          continue;
        }

        seen.add(key);
        deduped.push(row);
      }

      return deduped.sort(
        (a, b) =>
          Number(b.sortTimestamp || 0) -
          Number(a.sortTimestamp || 0)
      );
    }, [
      normalizedVetVthoRawData,
      normalizedVip180RawData,
      normalizedExchangeRawData,
      normalizedGasFeeLedgerData,
      ds?.blockpitNftTransformEvents,
      fiatCurrency,
    ]);

  const normalizedVetTaxData = React.useMemo(
    () => normalizeCsvRows(vetData || []),
    [vetData, csvLocale, walletSet, ownedLedgerWalletAddresses]
  );

  const normalizedVthoTaxData = React.useMemo(
    () => normalizeCsvRows(vthoData || []),
    [vthoData, csvLocale, walletSet, ownedLedgerWalletAddresses]
  );

  const normalizedB3trTaxData = React.useMemo(
    () => normalizeCsvRows(b3trData || []),
    [b3trData, csvLocale, walletSet, ownedLedgerWalletAddresses]
  );

  const normalizedNftTaxData = React.useMemo(
    () => normalizeCsvRows(nftData || []),
    [nftData, csvLocale, walletSet, ownedLedgerWalletAddresses]
  );

  const pickEventUniq = pickUkEventUniq;

  const buildExportEventKey = (hash, row = {}, { seq = 0, kind = "event" } = {}) => {
    const baseHash = String(hash || "").trim() || `synthetic:${kind}:${seq}`;
    const id = extractEventIdentityFields(row);

    // Clause + event are required for multi-clause txs (same eventIndex in C0/C1).
    if (id.clause_index != null && id.event_index != null) {
      return buildCanonicalEventKey({
        txHash: baseHash,
        clauseIndex: id.clause_index,
        eventIndex: id.event_index,
        logIndex:
          id.log_index != null && id.log_index !== id.event_index
            ? id.log_index
            : undefined,
        classification: row.classification || row.type || kind,
        seq,
        kind,
      });
    }

    // Prefer structured row builder when any index is present.
    const structured = buildCanonicalEventKeyFromRow(row, {
      txHash: baseHash,
      seq,
      kind,
    });
    if (structured.includes("#c") || structured.includes("#e")) {
      return structured;
    }

    // Legacy event uniq only when it is already structured / non-ambiguous.
    const eventUniq = String(pickEventUniq(row) || "").trim();
    if (eventUniq && !/^\d+$/.test(eventUniq)) {
      return `${baseHash}#${eventUniq}`;
    }

    const parts = [];
    if (id.clause_index != null) parts.push(`c${id.clause_index}`);
    if (id.event_index != null) parts.push(`e${id.event_index}`);
    else if (id.log_index != null) parts.push(`l${id.log_index}`);

    const cls = String(row.classification || row.type || kind || "")
      .trim()
      .toLowerCase();
    if (cls) parts.push(cls.replace(/\s+/g, "_"));

    if (parts.length) return `${baseHash}#${parts.join(".")}`;
    return `${baseHash}#${kind}:${seq}`;
  };


  const uniquifyEventKeys = (rows = []) => {
    const seen = new Map();

    return (Array.isArray(rows) ? rows : []).map((row) => {
      const base = String(row?.Event_Key || "").trim();
      if (!base) return row;

      const count = (seen.get(base) || 0) + 1;
      seen.set(base, count);

      return {
        ...row,
        Event_Key: count === 1 ? base : `${base}:${count}`,
      };
    });
  };

  const legsStats = (r) => {
    const legs = Array.isArray(r?.fifoLegs) ? r.fifoLegs : [];
    if (!legs.length) {
      return {
        fifoLots: 0,
        holdingMin: "",
        holdingMax: "",
        acqMinUTC: "",
        acqMaxUTC: "",
        buyEUR: 0,
        sellEUR: 0,
        profitEUR: 0,
        taxableProfitEUR: 0,
        nonTaxableProfitEUR: 0,
        shortTermProfitEUR: 0,
        longTermProfitEUR: 0,
      };
    }

    const acqTs = legs
      .map((l) =>
        toSec(
          l?.acquired_ts ?? l?.acquiredTs ??
          l?.acq_ts ?? l?.acqTs ??
          l?.acquiredAt ?? l?.acquired_at ??
          l?.acqTimestamp ?? l?.acq_timestamp ??
          l?.lotTs ?? l?.lot_ts ??
          l?.timestamp ?? l?.ts ?? 0
        )
      )
      .filter((x) => Number.isFinite(x) && x > 0);

    const disposalTs = toSec(
      r?.timestamp ??
      r?.ts ??
      pickRowTs(r) ??
      0
    );

    const holding = legs
      .map((l) => resolveLegHoldingDays(l, {
        timestamp: disposalTs,
        ts: disposalTs,
      }))
      .filter((x) => Number.isFinite(x) && x >= 0);

    const num0 = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    // Sum EUR from legs (this fixes the "Anschaffungskosten = 0" bug)
    const buyEUR = legs.reduce(
      (s, l) => s + num0(l?.buyEUR ?? l?.einstand ?? l?.costEUR ?? l?.costBasisEUR ?? l?.grossBuyEUR),
      0
    );
    const sellEUR = legs.reduce(
      (s, l) => s + num0(l?.sellEUR ?? l?.verkauf ?? l?.proceedsEUR ?? l?.grossSellEUR),
      0
    );
    const profitEUR = legs.reduce((s, l) => {
      const p = num0(l?.legProfitEUR ?? l?.profitEUR ?? l?.gewinn ?? l?.gainEUR);
      if (p !== 0) return s + p;
      const be = num0(l?.buyEUR ?? l?.einstand ?? l?.costEUR ?? l?.costBasisEUR ?? l?.grossBuyEUR);
      const se = num0(l?.sellEUR ?? l?.verkauf ?? l?.proceedsEUR ?? l?.grossSellEUR);
      return s + (se - be);
    }, 0);
    const taxableProfitEUR = legs.reduce((s, l) => {
      const taxableFlag =
        l?.isTaxable === true ||
        String(l?.taxable ?? "").toLowerCase() === "true" ||
        String(l?.steuerpflichtig ?? "").toLowerCase() === "ja";
      if (!taxableFlag) return s;
      const p = num0(l?.legProfitEUR ?? l?.profitEUR ?? l?.gewinn ?? l?.gainEUR);
      if (p !== 0) return s + p;
      const be = num0(l?.buyEUR ?? l?.einstand ?? l?.costEUR ?? l?.costBasisEUR ?? l?.grossBuyEUR);
      const se = num0(l?.sellEUR ?? l?.verkauf ?? l?.proceedsEUR ?? l?.grossSellEUR);
      return s + (se - be);
    }, 0);
    const nonTaxableProfitEUR = profitEUR - taxableProfitEUR;

    const acqMin = acqTs.length ? Math.min(...acqTs) : 0;
    const acqMax = acqTs.length ? Math.max(...acqTs) : 0;

    const holdingMinFromRow = firstFinite(r?.holdingDaysMin, r?.holdingMin);
    const holdingMaxFromRow = firstFinite(r?.holdingDaysMax, r?.holdingMax);
    const holdingMin = Number.isFinite(holdingMinFromRow)
      ? holdingMinFromRow
      : (holding.length ? Math.min(...holding) : "");
    const holdingMax = Number.isFinite(holdingMaxFromRow)
      ? holdingMaxFromRow
      : (holding.length ? Math.max(...holding) : "");

    // 🇺🇸 NEU: Short/Long-Term Split aus Legs aggregieren
    const shortTermProfitEUR = legs.reduce((s, l) => {
      const taxableFlag =
        l?.isTaxable === true ||
        String(l?.taxable ?? "").toLowerCase() === "true" ||
        String(l?.steuerpflichtig ?? "").toLowerCase() === "ja";
      if (!taxableFlag) return s;
      const isLongTerm = resolveLegIsLongTerm(l, {
        timestamp: disposalTs,
        ts: disposalTs,
      });
      if (isLongTerm) return s;
      const p = num0(l?.legProfitEUR ?? l?.profitEUR ?? l?.gewinn ?? l?.gainEUR);
      if (p !== 0) return s + p;
      const be = num0(l?.buyEUR ?? l?.einstand ?? l?.costEUR ?? l?.costBasisEUR ?? l?.grossBuyEUR);
      const se = num0(l?.sellEUR ?? l?.verkauf ?? l?.proceedsEUR ?? l?.grossSellEUR);
      return s + (se - be);
    }, 0);
    const longTermProfitEUR = legs.reduce((s, l) => {
      const taxableFlag =
        l?.isTaxable === true ||
        String(l?.taxable ?? "").toLowerCase() === "true" ||
        String(l?.steuerpflichtig ?? "").toLowerCase() === "ja";
      if (!taxableFlag) return s;
      const isLongTerm = resolveLegIsLongTerm(l, {
        timestamp: disposalTs,
        ts: disposalTs,
      });
      if (!isLongTerm) return s;
      const p = num0(l?.legProfitEUR ?? l?.profitEUR ?? l?.gewinn ?? l?.gainEUR);
      if (p !== 0) return s + p;
      const be = num0(l?.buyEUR ?? l?.einstand ?? l?.costEUR ?? l?.costBasisEUR ?? l?.grossBuyEUR);
      const se = num0(l?.sellEUR ?? l?.verkauf ?? l?.proceedsEUR ?? l?.grossSellEUR);
      return s + (se - be);
    }, 0);

    return {
      fifoLots: legs.length,
      holdingMin,
      holdingMax,
      acqMinUTC: acqMin ? fmtUtc(acqMin) : "",
      acqMaxUTC: acqMax ? fmtUtc(acqMax) : "",
      buyEUR,
      sellEUR,
      profitEUR,
      taxableProfitEUR,
      nonTaxableProfitEUR,
      shortTermProfitEUR,
      longTermProfitEUR,
    };
  };

  // ---------- Exchange-Daten für "Detail-Export" (bestehender Button) ----------
  const formattedExchangeData = React.useMemo(() => {
    if (!exchangeData || !Array.isArray(exchangeData)) return [];

    const taxByKey = new Map();
    if (exchangeTaxRows && Array.isArray(exchangeTaxRows)) {
      for (const taxRow of exchangeTaxRows) {
        const key = `${String(taxRow.token || "").toUpperCase()}|${taxRow.event_id || taxRow.hash || ""}`;
        taxByKey.set(key, taxRow);
      }
    }

    const formattedData = exchangeData.map((row) => {
      const isSell =
        String(row.kind).toLowerCase() === "trade" &&
        String(row.side).toUpperCase() === "SELL";

      const taxKey = `${String(row.base || "").toUpperCase()}|${buildExchangeEventId(row, row.ts ?? row.timestamp)}`;
      const taxRow = taxByKey.get(taxKey);

      const priceFiat = firstFinite(
        taxRow?.valuationPrice,
        taxRow?.exchange_price_fiat,
        taxRow?.exchangePriceFiat,
        taxRow?.unitPriceFiat,
        taxRow?.priceFiat,
        row?.price
      );

      const totalFiat = firstFinite(
        taxRow?.proceeds_fiat,
        taxRow?.proceeds_eur,
        taxRow?.grossSellFiat,
        taxRow?.grossSellEUR,
        row?.quoteAmount,
        row?.quote_amount
      );

      const taxableAmount =
        isSell && taxRow
          ? firstFinite(
              taxRow?.taxableProfitFiat,
              taxRow?.taxableProfitEUR,
              taxRow?.taxableProfit,
              0
            )
          : 0;

      const tsRaw = row.timestamp ?? row.ts ?? row.date ?? 0;
      const tsSec = toSec(tsRaw);

      return {
        source: row.source || "",
        kind: row.kind || "",
        txid: row.txid || "",
        base: row.base || "",
        quote: fiatCurrency,
        side: row.side || "",
        amount: row.amount ?? "",
        price: Number.isFinite(priceFiat) ? priceFiat : "",
        totalFiat: Number.isFinite(totalFiat) ? totalFiat : "",
        fee: row.fee ?? "",
        feeAsset: row.feeAsset || "",
        taxableProfit: Number.isFinite(taxableAmount) ? taxableAmount : 0,
        rawQuote: row.quote || "",
        rawPrice: row.price ?? "",
        date: tsSec ? formatCsvDateTime(tsSec) : String(row.date || ""),
        sortTimestamp: tsSec,
        event_id: taxRow?.event_id || taxRow?.hash || buildExchangeEventId(row, tsSec),
        exchange_account_id:
          taxRow?.exchange_account_id ||
          taxRow?.exchangeAccountId ||
          row.exchange_account_id ||
          row.exchangeAccountId ||
          row.account_id ||
          "default",
        custodyLocationId:
          taxRow?.custodyLocationId ||
          row.custodyLocationId ||
          null,
        fifoLegs: Array.isArray(taxRow?.fifoLegs) ? taxRow.fifoLegs : [],
      };
    });

    return formattedData.sort((a, b) => (b.sortTimestamp || 0) - (a.sortTimestamp || 0));
  }, [exchangeData, exchangeTaxRows, fiatCurrency, csvLocale]);

  /** Kompakt: eine Zeile pro steuerlichem Disposal aus liquidityTaxRows[].disposals[] — keine Leg-JSONs (die stehen im form-8949-audit). */
  const formattedLiquidityData = React.useMemo(() => {
    const liqRows = filterRowsToSelectedYear(
      Array.isArray(ds?.liquidityTaxRows)
        ? ds.liquidityTaxRows
        : (liquidityData || [])
    );

    if (!Array.isArray(liqRows) || liqRows.length === 0) return [];

    const summarizeLegs = (legs = []) =>
      (Array.isArray(legs) ? legs : [])
        .map((leg) => {
          const token = String(
            leg?.tokenSymbol || leg?.token || leg?.symbol || leg?.asset || ""
          ).toUpperCase();
          const qty = Number(leg?.amount ?? leg?.qty ?? leg?.quantity ?? 0);
          if (!token || !Number.isFinite(qty)) return null;
          return `${token} ${qty}`;
        })
        .filter(Boolean)
        .join(" | ");

    const out = [];

    for (const r of liqRows) {
      const ts = Number(r?.timestamp || r?.ts || 0);
      const hash = r?.hash || r?.txid || r?.txHash || "";
      const pool =
        r?.poolSymbol ||
        r?.pool ||
        r?.pair ||
        r?.lpSymbol ||
        "";
      const poolAddress = r?.poolAddress || "";
      const lpToken = r?.lpToken || r?.lpSymbol || "";
      const lpAmount =
        r?.lpAmountDisplay ??
        r?.lpAmountRaw ??
        r?.lpAmount ??
        "";

      const depositSummary = summarizeLegs(r?.deposits || []);
      const withdrawSummary = summarizeLegs(r?.withdrawals || []);

      const depositFiatRaw = firstFinite(r?.depositFiat, r?.depositEUR, 0);
      const withdrawFiatRaw = firstFinite(r?.withdrawFiat, r?.withdrawEUR, 0);
      const depositFiat = Number.isFinite(depositFiatRaw) ? depositFiatRaw : 0;
      const withdrawFiat = Number.isFinite(withdrawFiatRaw) ? withdrawFiatRaw : 0;

      const disposals = Array.isArray(r?.disposals) ? r.disposals : [];

      for (const d of disposals) {
        const taxableFiatRaw = firstFinite(
          pickFiatTaxable(d),
          d?.taxableProfitFiat,
          d?.taxableProfitEUR,
          d?.taxableProfit,
          0
        );
        const taxableFiat = Number.isFinite(taxableFiatRaw) ? taxableFiatRaw : 0;

        const nonTaxableFiatRaw = firstFinite(
          pickFiatNonTaxable(d),
          d?.nonTaxableProfitFiat,
          d?.nonTaxableProfitEUR,
          0
        );
        const nonTaxableFiat = Number.isFinite(nonTaxableFiatRaw)
          ? nonTaxableFiatRaw
          : 0;

        const isFullyTaxFree =
          Boolean(d?.isFullyTaxFree) ||
          (isDE &&
            Math.abs(Number(taxableFiat || 0)) <= 1e-12 &&
            Math.abs(Number(nonTaxableFiat || 0)) > 1e-12);

        const shortFiatRaw = firstFinite(
          pickFiatShort(d),
          d?.shortTermProfitFiat,
          d?.shortTermProfitEUR,
          0
        );
        const shortFiat = Number.isFinite(shortFiatRaw) ? shortFiatRaw : 0;

        const longFiatRaw = firstFinite(
          pickFiatLong(d),
          d?.longTermProfitFiat,
          d?.longTermProfitEUR,
          0
        );
        const longFiat = Number.isFinite(longFiatRaw) ? longFiatRaw : 0;

        const unitRaw = firstFinite(
          pickFiatPrice(d),
          d?.unitPriceFiat,
          d?.priceFiat,
          d?.euroPrice,
          0
        );
        const unitPriceFiat = Number.isFinite(unitRaw) ? unitRaw : 0;

        const proceedsRaw = firstFinite(
          pickFiatTotal(d),
          d?.amountFiat,
          d?.totalFiat,
          d?.euroAmount,
          d?.grossSellFiat,
          d?.grossSellEUR,
          0
        );
        const proceedsFiat = Number.isFinite(proceedsRaw) ? proceedsRaw : 0;

        const fifoLots = Array.isArray(d?.fifoLegs) ? d.fifoLegs.length : 0;

        const amt = Number(d?.amount ?? d?.qty ?? d?.quantity ?? 0);

        out.push({
          timestamp: ts,
          sortTimestamp: ts,
          date: formatCsvDateTime(ts),
          type: String(
            d?.classification || d?.type || r?.classification || ""
          ).toLowerCase(),
          hash: d?.hash || d?.txid || hash,
          pool,
          poolAddress,
          lpToken,
          lpAmount,
          depositSummary,
          depositFiat,
          withdrawSummary,
          withdrawFiat,
          token: String(d?.token || d?.asset || "").toUpperCase(),
          amount: Number.isFinite(amt) ? amt : 0,
          unitPriceFiat,
          proceedsFiat,
          taxableProfitFiat: taxableFiat,
          ...(isUS
            ? {
                shortTermProfitFiat: shortFiat,
                longTermProfitFiat: longFiat,
              }
            : {}),
          ...(isDE ? { isFullyTaxFreeLabel: boolLabel(isFullyTaxFree) } : {}),
          fifoLots,
        });
      }
    }

    return out.sort(
      (a, b) => Number(b.timestamp || 0) - Number(a.timestamp || 0)
    );
  }, [ds, liquidityData, isUS, isDE, csvLocale, yearInt]);

  // UK: single source for CSV + UI summary (ukFinalExport.js)
  const ukFinalExport = React.useMemo(() => {
    if (!isUK) return null;
    return buildUkFinalExport(ds, yearInt, {
      fiatCurrency,
      language,
      vetData,
      vthoData,
      b3trData,
      nftData,
      liquidityData,
      exchangeTaxRows,
    });
  }, [
    isUK,
    ds,
    yearInt,
    fiatCurrency,
    language,
    vetData,
    vthoData,
    b3trData,
    nftData,
    liquidityData,
    exchangeTaxRows,
  ]);

  // ---------- FINAL: Veräußerungsgeschäfte (§23) + ausgeschlossene Review-Zeilen ----------
  const finalDisposalExports = React.useMemo(() => {
    if (isUK && ukFinalExport) {
      return {
        disposals: ukFinalExport.capitalRows,
        excludedReview: ukFinalExport.reviewRows,
      };
    }

    const yearInt = Number(year);
    const inYear = (tsSec) => rowInTaxYear(tsSec, yearInt);

    // Gold-Standard: Export nutzt exakt eine Quelle (was die UI rendert)
    const vetRows = Array.isArray(ds?.vetTaxRows) ? ds.vetTaxRows : (vetData || []);
    const vthoRows = getVthoDisplayRows(ds, vthoData);
    const b3trRows = Array.isArray(ds?.b3trTaxRows) ? ds.b3trTaxRows : (b3trData || []);
    const nftRows = Array.isArray(ds?.nftTaxRows) ? ds.nftTaxRows : (nftData || []);
    const liqRows = Array.isArray(ds?.liquidityTaxRows) ? ds.liquidityTaxRows : (liquidityData || []);
    const exRows = Array.isArray(ds?.exchangeTaxRows) ? ds.exchangeTaxRows : (exchangeTaxRows || []);

    const includedRows = [];
    const excludedRows = [];
    let seq = 0;

    const push = (source, r) => {
      if (r == null || typeof r !== "object") return;

      const hash0 = pickHash(r);
      const hash = hash0 || `synthetic:${source}:${seq}`;
      const txHashDisplay = hash0 ? pickDisplayHash(r, language) : hash;
      const genericEventKey = buildExportEventKey(hash, r, {
        seq,
        kind: pickCls(r) || "disposal",
      });
      const eventKey = isNftTransformDisposalRow(r)
        ? buildNftTransformDisposalExportKey(r, genericEventKey, seq)
        : genericEventKey;

      const tsSec = toSec(pickRowTs(r));
      if (!inYear(tsSec)) return;

      const cls = pickCls(r);
      const token = isNftTransformDisposalRow(r)
        ? pickExportAsset(r)
        : pickToken(r);
      const amount = pickAmount(r);

      const leg = legsStats(r);

      const sale = firstFinite(
        r?.grossProceedsBeforeFeesFiat,
        r?.grossProceedsBeforeFeesEUR,
        pickFiatSell(r),
        pickFiatTotal(r),
        leg.sellEUR,
        0
      );
      const feeCost = Math.max(
        0,
        firstFinite(
          r?.transactionCostFiat,
          r?.gasFeeAllocatedToDisposalFiat,
          0
        )
      );
      const cost = pickFiatBuy(r) ?? firstFinite(leg.buyEUR, 0);
      const taxable = pickFiatTaxable(r) ?? firstFinite(leg.taxableProfitEUR, 0);
      const nonTaxFromRow = pickFiatNonTaxable(r);
      const profitFromRow = pickFiatGrossProfit(r);
      const profitFromSaleCost =
        Number.isFinite(sale) && Number.isFinite(cost) ? sale - cost : NaN;
      // No-FIFO-Fallback: grossProfit kann 0 sein, taxable/proceeds-cost stimmen
      const grossProfitLooksStale =
        Number.isFinite(profitFromRow) &&
        Math.abs(profitFromRow) <= 1e-12 &&
        Math.abs(taxable) > 1e-8 &&
        Number.isFinite(profitFromSaleCost) &&
        Math.abs(profitFromSaleCost - taxable) <= 0.05;
      const profit = grossProfitLooksStale
        ? profitFromSaleCost
        : Number.isFinite(profitFromRow)
          ? profitFromRow
          : Number.isFinite(nonTaxFromRow)
            ? taxable + nonTaxFromRow
            : firstFinite(leg.profitEUR, profitFromSaleCost, 0);

      const nonTaxable = isIT
        ? 0
        : (Number.isFinite(nonTaxFromRow)
          ? nonTaxFromRow
          : firstFinite(
              leg.nonTaxableProfitEUR,
              Number.isFinite(profit) ? profit - taxable : NaN,
              0
            ));

      const explicitFullyTaxFree =
        r?.isFullyTaxFree ??
        r?.fullyTaxFree ??
        r?.vollstaendig_steuerfrei ??
        null;

      const fullyTaxFree = isIT
        ? false
        : (
            explicitFullyTaxFree != null
              ? parseBool(explicitFullyTaxFree)
              : (
                  isDE &&
                  Array.isArray(r?.fifoLegs) &&
                  r.fifoLegs.length > 0 &&
                  r.fifoLegs.every((fifoLeg) => {
                    const legTaxable =
                      fifoLeg?.isTaxable ??
                      fifoLeg?.taxable ??
                      fifoLeg?.steuerpflichtig;

                    return !parseBool(legTaxable);
                  })
                )
          );

      const holdingMin = firstFinite(r?.holdingDaysMin, r?.holdingMin, leg.holdingMin);
      const holdingMax = firstFinite(r?.holdingDaysMax, r?.holdingMax, leg.holdingMax);

      const st = pickFiatShort(r) ?? firstFinite(leg.shortTermProfitEUR, 0);
      const lt = pickFiatLong(r) ?? firstFinite(leg.longTermProfitEUR, 0);

      const transformSourceOrdinal = isNftTransformDisposalRow(r)
        ? pickNftTransformSourceOrdinal(r, r?.fifoLegs?.[0] || null, 0)
        : null;

      const idFields = extractEventIdentityFields({
        ...r,
        ...leg,
        Event_Key: eventKey,
        fifo_leg_index: isNftTransformDisposalRow(r)
          ? transformSourceOrdinal
          : leg?.legIndex ?? leg?.leg_index ?? leg?.fifoLegIndex,
        disposal_event_key: eventKey,
        canonical_event_key: eventKey,
        ...(isNftTransformDisposalRow(r)
          ? {
              token_contract:
                pickNftTransformTokenContract(r) ||
                undefined,
            }
          : {}),
      });

      const sortMeta = { _sort: tsSec, _seq: seq++ };

      if (isExcludedFromFinalTaxExport(r, { isUK })) {
        excludedRows.push({
          Steuerjahr: taxYearLabelFor(yearInt),
          Quelle: source,
          Klassifizierung: cls,
          Asset: token,
          Menge: amount,
          Datum_UTC: fmtUtc(tsSec),
          [PROCEEDS_KEY]: sale,
          Tx_Hash: txHashDisplay,
          Event_Key: eventKey,
          ...idFields,
          Ausschlussgrund: pickExclusionReasonForExport(r),
          InSteuerSumme: language === "en" ? "No" : "Nein",
          FIFO_Lots: String(leg.fifoLots || 0),
          ...sortMeta,
        });
        return;
      }

      if (!shouldIncludeCustomerExportRow({ Menge: amount, amount })) {
        return;
      }

      const acquisitionDateDisplay =
        isNftTransformDisposalRow(r) &&
        (r?.nftTransformAggregateBundle === true ||
          r?.nftTransformAggregateDisposal === true ||
          String(r?.acquisitionDateDisplay || "").toUpperCase() === "VARIOUS")
          ? "VARIOUS"
          : null;

      const disposalLegalReference =
        isUS && isNftTransformDisposalRow(r)
          ? (
              "IRC §1001; " +
              "Treas. Reg. §1.1001-7; " +
              "Treas. Reg. §1.1012-1(h); " +
              "IRS Digital Asset FAQs Q70/Q72/Q74/Q102; " +
              "Form 8949"
            )
          : legalReference(cc, "disposals");

      includedRows.push({
        Steuerjahr: taxYearLabelFor(yearInt),
        Steuerkategorie: TAX_CATEGORY_LABEL.disposals,
        Rechtsgrundlage: r?.Rechtsgrundlage || disposalLegalReference,
        Quelle: source,
        Klassifizierung: cls,
        Asset: token,
        Menge: amount,
        Datum_UTC: fmtUtc(tsSec),

        [PROCEEDS_KEY]: sale,
        [FEE_COST_KEY]: feeCost,
        [COST_KEY]: cost,
        [PROFIT_KEY]: profit,
        [TAXABLE_KEY]: taxable,

        ...(isUS
          ? {
              [ST_KEY]: st,
              [LT_KEY]: lt,
            }
          : {}),

        ...(isDE
          ? {
              [NONTAX_KEY]: nonTaxable,
              Vollständig_steuerfrei: fullyTaxFree ? "Ja" : "Nein",
            }
          : {}),

        Haltezeit_min_Ta: Number.isFinite(holdingMin) ? String(holdingMin) : "",
        Haltezeit_max_Ta: Number.isFinite(holdingMax) ? String(holdingMax) : "",
        Anschaffungsdatum_min_UTC:
          acquisitionDateDisplay || leg.acqMinUTC,
        Anschaffungsdatum_max_UTC:
          acquisitionDateDisplay || leg.acqMaxUTC,
        FIFO_Lots: String(
          leg.fifoLots ||
            (isNftTransformDisposalRow(r)
              ? Number(r?.amount || nftTransformSourceLegsOf(r).length || 0)
              : 0) ||
            0
        ),
        Event_Key: eventKey,
        ...idFields,
        ...(isUS && isNftTransformDisposalRow(r)
          ? nftTransformExportMetaOf(r, r?.fifoLegs?.[0] || null, 0)
          : {}),
        Tx_Hash: txHashDisplay,

        ...sortMeta,
      });
    };

    // 1) Token-Disposals (§23): swap_out + nfbc_purchase
    const pushTokenDisposal = (source, r) => {
      const cls = pickCls(r);
      if (isTokenCapitalDisposalCls(cls)) push(source, r);
    };
    for (const r of vetRows) pushTokenDisposal("VET", r);
    for (const r of vthoRows) pushTokenDisposal("VTHO", r);
    for (const r of b3trRows) pushTokenDisposal("VIP-180", r);

    // 2) Exchange-Disposals (SELL)
    for (const r of exRows) {
      // exchangeTaxRows sind bereits "nur sells" in deiner Engine
      push("EXCHANGE", r);
    }

    // 3) Liquidity-Disposals (aus lr.disposals[])
    for (const lr of liqRows) {
      const disposals = Array.isArray(lr?.disposals) ? lr.disposals : [];
      for (const d of disposals) push("LIQUIDITY", d);
    }

    // 4) NFT-Disposals: Mint + Sale Payment
    for (const r of nftRows) {
      if (isUK) {
        if (shouldIncludeNftDisposalExportRow(r, { isUK: true })) {
          push("NFT", r);
        }
        continue;
      }

      if (shouldIncludeNftDisposalExportRow(r)) {
        push("NFT", r);
      }
    }

    const sortRows = (a, b) =>
      (b._sort || 0) - (a._sort || 0) || (a._seq || 0) - (b._seq || 0);

    includedRows.sort(sortRows);
    excludedRows.sort(sortRows);

    return {
      disposals: dedupeFinalExportRows(
        uniquifyEventKeys(includedRows),
        [PROCEEDS_KEY, COST_KEY, PROFIT_KEY, TAXABLE_KEY]
      ),
      excludedReview: dedupeFinalExportRows(
        uniquifyEventKeys(excludedRows),
        [PROCEEDS_KEY]
      ),
    };
  }, [
    ds,
    vetData,
    vthoData,
    b3trData,
    nftData,
    liquidityData,
    exchangeTaxRows,
    year,
    selectedCountry,
    isIT,
    isDE,
    isUS,
    isGR,
    isUK,
    ukFinalExport,
    fiatCurrency,
    cc,
    language,
  ]);

  const finalDisposalsCsv = finalDisposalExports.disposals;

  React.useEffect(() => {
    if (!isUS) return;
    const usFiatAudit = ds?.fiatSectionAudit || ds?.usFiatAudit || null;
    const expectedLiquidityExport =
      usFiatAudit?.sections?.find((s) => s.section === "liquidity")
        ?.exportFingerprint || null;
    const liquidityOnlyRows = finalDisposalsCsv.filter(
      (r) => String(r.Quelle || "").toUpperCase() === "LIQUIDITY"
    );
    const liquidityExportAudit = verifyExportAgainstBackend(
      "liquidity",
      liquidityOnlyRows,
      expectedLiquidityExport,
      fiatCurrency
    );
    if (
      liquidityExportAudit.status !== "verified" &&
      liquidityExportAudit.status !== "skipped"
    ) {
          }
  }, [isUS, ds?.fiatSectionAudit, ds?.usFiatAudit, finalDisposalsCsv, fiatCurrency, selectedCountry, ds]);

  // ---------- 🇺🇸 AUDIT CSV: Form-8949-ready (pro FIFO-Leg eine Zeile) ----------
  const auditForm8949Csv = React.useMemo(() => {
    if (!isUS) return []; // Nur für US

    const yearInt = Number(year);
    const inYear = (tsSec) => rowInTaxYear(tsSec, yearInt);

    const vetRows = Array.isArray(ds?.vetTaxRows) ? ds.vetTaxRows : (vetData || []);
    const vthoRows = getVthoDisplayRows(ds, vthoData);
    const b3trRows = Array.isArray(ds?.b3trTaxRows) ? ds.b3trTaxRows : (b3trData || []);
    const nftRows = Array.isArray(ds?.nftTaxRows) ? ds.nftTaxRows : (nftData || []);
    const liqRows = Array.isArray(ds?.liquidityTaxRows) ? ds.liquidityTaxRows : (liquidityData || []);
    const exRows = Array.isArray(ds?.exchangeTaxRows) ? ds.exchangeTaxRows : (exchangeTaxRows || []);

    const rows = [];
    let seq = 0;

    const transformEvents = Array.isArray(ds?.blockpitNftTransformEvents)
      ? ds.blockpitNftTransformEvents
      : [];

    const transformEventByLifecycle = new Map();
    const transformEventByBurnHash = new Map();

    for (const event of transformEvents) {
      const lifecycle = String(event?.lifecycleKey || "")
        .trim()
        .toLowerCase();

      const burnHash = String(event?.burnHash || event?.hash || "")
        .trim()
        .toLowerCase();

      if (lifecycle) {
        transformEventByLifecycle.set(lifecycle, event);
      }

      if (burnHash) {
        transformEventByBurnHash.set(burnHash, event);
      }
    }

    const findTransformEventForDisposal = (disposal = {}) => {
      const lifecycle = String(disposal?.nftTransformLifecycleKey || "")
        .trim()
        .toLowerCase();

      if (lifecycle && transformEventByLifecycle.has(lifecycle)) {
        return transformEventByLifecycle.get(lifecycle);
      }

      const burnHash = String(
        disposal?.nftTransformBurnHash || pickHash(disposal) || ""
      )
        .trim()
        .toLowerCase();

      return burnHash
        ? transformEventByBurnHash.get(burnHash) || null
        : null;
    };

    const pushLeg = (source, disposal, leg, legIndex) => {
      // Nur tax-export-eligible Parents — nie Review-Legs allein wegen isTaxable.
      if (!isTaxExportEligibleDisposal(disposal)) return;
      if (isExcludedFromFinalTaxExport(disposal, { isUK: false })) return;

      const tsSec = toSec(pickRowTs(disposal));
      if (!inYear(tsSec)) return;

      const acqTs = Number(leg.acquisitionTs || leg.timestamp || leg.ts || leg.acquired_ts || 0);
      const disposalTs = Number(disposal.timestamp || disposal.ts || 0);
      const dateAcquired = acqTs ? fmtUtc(acqTs) : "";
      const dateDisposed = disposalTs ? fmtUtc(disposalTs) : "";

      const qty = Number(leg.qty ?? leg.quantity ?? leg.amount ?? 0);
      const token = isNftTransformDisposalRow(disposal)
        ? pickExportAsset(disposal)
        : pickToken(disposal);
      const proceeds = firstFinite(
        leg.sellEUR,
        leg.sellFiat,
        leg.sell,
        0
      );
      const costBasis = firstFinite(
        leg.buyEUR,
        leg.buyFiat,
        leg.buy,
        leg.costEUR,
        leg.costBasisEUR,
        null
      );
      const gainLoss = firstFinite(
        leg.legProfitEUR,
        leg.legProfitFiat,
        leg.profitEUR,
        (Number.isFinite(proceeds) && Number.isFinite(costBasis))
          ? (proceeds - costBasis)
          : null,
        null
      );

      const holdingDays = resolveLegHoldingDays(leg, {
        timestamp: disposalTs,
        ts: disposalTs,
      });
      const termCode = (acqTs && disposalTs)
        ? getUsHoldingTerm({
          acquiredAt: acqTs,
          disposedAt: disposalTs,
        })
        : "";
      const term = termCode === "LONG_TERM"
        ? "LT"
        : termCode === "SHORT_TERM"
          ? "ST"
          : "";

      const hash0 = pickHash(disposal);

      const genericDisposalEventKey = buildExportEventKey(
        hash0 || `synthetic:${source}:${seq}`,
        disposal,
        {
          seq,
          kind: pickCls(disposal) || "disposal",
        }
      );

      const isTransformDisposal = isNftTransformDisposalRow(disposal);

      const transformSourceOrdinal = isTransformDisposal
        ? pickNftTransformSourceOrdinal(disposal, leg, legIndex)
        : null;

      const disposalEventKey = isTransformDisposal
        ? buildNftTransformDisposalExportKey(
            disposal,
            genericDisposalEventKey,
            legIndex
          )
        : genericDisposalEventKey;

      const exportLegIndex = isTransformDisposal
        ? transformSourceOrdinal
        : legIndex + 1;

      const transformEvent = isTransformDisposal
        ? findTransformEventForDisposal(disposal)
        : null;

      const transformValuation = transformEvent?.valuation || {};

      const primaryMintReference =
        transformValuation?.primaryMintReference || {};

      const idFields = extractEventIdentityFields({
        ...disposal,
        ...leg,
        Event_Key: disposalEventKey,
        disposal_event_key: disposalEventKey,
        canonical_event_key: disposalEventKey,
        fifo_leg_index: exportLegIndex,
        ...(isTransformDisposal
          ? {
              token_contract:
                pickNftTransformTokenContract(disposal) || undefined,
            }
          : {}),
      });

      rows.push({
        token,
        qty,
        date_acquired: dateAcquired,
        date_disposed: dateDisposed,
        proceeds: proceeds,
        cost_basis: costBasis,
        gain_loss: gainLoss,
        term,
        holding_days: holdingDays == null ? "" : holdingDays,
        source,
        disposal_hash: hash0,
        leg_index: exportLegIndex,
        transform_source_ordinal: isTransformDisposal
          ? transformSourceOrdinal
          : "",
        transform_kind: isTransformDisposal
          ? disposal?.nftTransformKind ||
            transformEvent?.transformKind ||
            ""
          : "",
        result_token_id: isTransformDisposal
          ? disposal?.nftTransformResultTokenId ??
            transformEvent?.outputs?.[0]?.tokenId ??
            ""
          : "",
        allocation_method: isTransformDisposal
          ? disposal?.proceedsAllocationMethod ||
            transformValuation?.proceedsAllocationMethod ||
            ""
          : "",
        allocation_basis: isTransformDisposal
          ? disposal?.allocationBasis ||
            transformValuation?.allocationBasis ||
            ""
          : "",
        allocation_confidence: isTransformDisposal
          ? disposal?.allocationMethodConfidence ||
            transformValuation?.allocationMethodConfidence ||
            ""
          : "",
        allocation_safe_harbor: isTransformDisposal
          ? disposal?.allocationLegalSafeHarbor == null &&
            transformValuation?.allocationLegalSafeHarbor == null
            ? ""
            : String(
                disposal?.allocationLegalSafeHarbor ??
                  transformValuation?.allocationLegalSafeHarbor
              )
          : "",
        valuation_method: isTransformDisposal
          ? disposal?.valuationMethod || transformValuation?.method || ""
          : "",
        valuation_source: isTransformDisposal
          ? transformValuation?.resultFmvSource ||
            disposal?.nftTransformResultFmvSource ||
            ""
          : "",
        valuation_confidence: isTransformDisposal
          ? transformValuation?.valuationConfidence ||
            primaryMintReference?.valuationConfidence ||
            disposal?.nftTransformResultValuationConfidence ||
            ""
          : "",
        valuation_reference_type: isTransformDisposal
          ? primaryMintReference?.referenceType || ""
          : "",
        valuation_reference_hash: isTransformDisposal
          ? primaryMintReference?.referenceHash || ""
          : "",
        form_8949_box_family:
          term === "ST" ? "G/H/I" : term === "LT" ? "J/K/L" : "",
        legal_reference: isTransformDisposal
          ? (
              "IRC §1001; " +
              "Treas. Reg. §1.1001-7; " +
              "Treas. Reg. §1.1012-1(h); " +
              "IRS Digital Asset FAQs Q70/Q72/Q74/Q102; " +
              "Form 8949"
            )
          : "IRC §1001; Form 8949",
        disposal_event_key: disposalEventKey,
        parentDisposalEventKey: disposalEventKey,
        parentIncludedInTaxTotals: disposal.includedInTaxTotals !== false,
        parentReviewReason: disposal.reviewReason || "",
        clause_index: idFields.clause_index,
        event_index: idFields.event_index,
        log_index: idFields.log_index,
        token_contract: idFields.token_contract,
        _sort: disposalTs,
        _seq: seq++,
      });
    };

    // 1) Token-Disposals
    const pushTokenDisposalLegs = (source, tokenRows) => {
      for (const r of tokenRows) {
        const cls = pickCls(r);
        if (isTokenCapitalDisposalCls(cls) && Array.isArray(r?.fifoLegs)) {
          for (const [i, leg] of r.fifoLegs.entries()) {
            if (leg.isTaxable) {
              pushLeg(source, r, leg, i);
            }
          }
        }
      }
    };

    pushTokenDisposalLegs("VET", vetRows);
    pushTokenDisposalLegs("VTHO", vthoRows);
    pushTokenDisposalLegs("VIP-180", b3trRows);

    // 2) Exchange-Disposals
    for (const r of exRows) {
      if (Array.isArray(r?.fifoLegs)) {
        for (const [i, leg] of r.fifoLegs.entries()) {
          if (leg.isTaxable) {
            pushLeg("EXCHANGE", r, leg, i);
          }
        }
      }
    }

    // 3) Liquidity-Disposals
    for (const lr of liqRows) {
      const disposals = Array.isArray(lr?.disposals) ? lr.disposals : [];
      for (const d of disposals) {
        if (Array.isArray(d?.fifoLegs)) {
          for (const [i, leg] of d.fifoLegs.entries()) {
            if (leg.isTaxable) {
              pushLeg("LIQUIDITY", d, leg, i);
            }
          }
        }
      }
    }

    // 4) NFT-Disposals
    for (const r of nftRows) {
      if (!shouldIncludeNftDisposalExportRow(r)) continue;

      const isAggregateTransform =
        isNftTransformDisposalRow(r) &&
        (r?.nftTransformAggregateBundle === true ||
          r?.nftTransformAggregateDisposal === true ||
          String(r?.acquisitionDateDisplay || "").toUpperCase() ===
            "VARIOUS");

      if (isAggregateTransform) {
        if (!isTaxExportEligibleDisposal(r)) continue;
        if (isExcludedFromFinalTaxExport(r, { isUK: false })) continue;

        const tsSec = toSec(pickRowTs(r));
        if (!inYear(tsSec)) continue;

        const disposalTs = Number(r.timestamp || r.ts || 0);
        const sourceLegs = nftTransformSourceLegsOf(r);
        const proceeds = firstFinite(
          r?.grossProceedsBeforeFeesFiat,
          pickFiatSell(r),
          pickFiatTotal(r),
          0
        );
        const costBasis = firstFinite(pickFiatBuy(r), r?.grossBuyFiat, 0);
        const gainLoss = firstFinite(
          pickFiatGrossProfit(r),
          Number.isFinite(proceeds) && Number.isFinite(costBasis)
            ? proceeds - costBasis
            : null,
          0
        );
        const hash0 = pickHash(r);
        const disposalEventKey = buildExportEventKey(
          hash0 || `synthetic:NFT:${seq}`,
          r,
          {
            seq,
            kind: pickCls(r) || "disposal",
          }
        );
        const tokenContract =
          pickNftTransformTokenContract(r) ||
          extractEventIdentityFields(r).token_contract ||
          "";

        rows.push({
          token: pickExportAsset(r),
          qty: Number(
            r?.amount ||
              sourceLegs.length ||
              0
          ),
          date_acquired: "VARIOUS",
          date_disposed: disposalTs ? fmtUtc(disposalTs) : "",
          proceeds,
          cost_basis: costBasis,
          gain_loss: gainLoss,
          term:
            r?.isLongTerm === true
              ? "LT"
              : "ST",
          holding_days: "",
          source: "NFT",
          disposal_hash: hash0,
          leg_index: 1,
          transform_source_ordinal: "",
          transform_kind: r?.nftTransformKind || "",
          result_token_id: r?.nftTransformResultTokenId ?? "",
          allocation_method: r?.proceedsAllocationMethod || "",
          allocation_basis: r?.allocationBasis || "",
          allocation_confidence: r?.allocationMethodConfidence || "",
          allocation_safe_harbor:
            r?.allocationLegalSafeHarbor == null
              ? ""
              : String(r.allocationLegalSafeHarbor),
          valuation_method: r?.valuationMethod || "",
          valuation_source: r?.nftTransformResultFmvSource || "",
          valuation_confidence:
            r?.nftTransformResultValuationConfidence || "",
          valuation_reference_type: "",
          valuation_reference_hash: "",
          form_8949_box_family:
            r?.isLongTerm === true ? "J/K/L" : "G/H/I",
          legal_reference:
            "IRC §1001; " +
            "Treas. Reg. §1.1001-7; " +
            "Treas. Reg. §1.1012-1(h); " +
            "IRS Digital Asset FAQs Q70/Q72/Q74/Q102; " +
            "Form 8949",
          disposal_event_key: disposalEventKey,
          parentDisposalEventKey: disposalEventKey,
          parentIncludedInTaxTotals:
            r.includedInTaxTotals !== false,
          parentReviewReason: r.reviewReason || "",
          token_contract: tokenContract,
          _sort: disposalTs,
          _seq: seq++,
        });
        continue;
      }

      if (Array.isArray(r?.fifoLegs)) {
        for (const [i, leg] of r.fifoLegs.entries()) {
          if (leg.isTaxable) {
            pushLeg("NFT", r, leg, i);
          }
        }
      }
    }

    rows.sort((a, b) => (b._sort || 0) - (a._sort || 0) || (a._seq || 0) - (b._seq || 0));
    return rows;
  }, [ds, vetData, vthoData, b3trData, nftData, liquidityData, exchangeTaxRows, year, isUS, ds?.blockpitNftTransformEvents]);

  const form8949DisposalCrossExport = React.useMemo(() => {
    if (!isUS) return { ok: true, status: "skipped" };

    return reconcileDisposalForm8949({
      disposalRows: finalDisposalsCsv,
      form8949Rows: auditForm8949Csv,
      keys: {
        proceeds: PROCEEDS_KEY,
        feeCost: FEE_COST_KEY,
        cost: COST_KEY,
        taxable: TAXABLE_KEY,
        shortTerm: ST_KEY,
        longTerm: LT_KEY,
      },
    });
  }, [
    isUS,
    finalDisposalsCsv,
    auditForm8949Csv,
    PROCEEDS_KEY,
    FEE_COST_KEY,
    COST_KEY,
    TAXABLE_KEY,
    ST_KEY,
    LT_KEY,
  ]);

  const form8949MismatchBlocksExport =
    isUS && form8949DisposalCrossExport?.ok === false;

  /*
   * Kauf-/Preview-Sperre gilt nur im Preview-Modus.
   * Form-8949-Reconciliation: nur echte Netto-Abweichungen melden.
   */
  if (isUS && form8949DisposalCrossExport?.status === "verified") {
    // Success: do not keep stale audit/export warnings about Form 8949.
    if (
      String(pdfExportDisabledReason || "").toLowerCase().includes("form 8949")
    ) {
      pdfExportDisabledReason = "";
    }
  } else if (form8949MismatchBlocksExport) {
    pdfExportDisabledReason =
      form8949DisposalCrossExport?.message ||
      (language === "en"
        ? "Form 8949 reconciliation failed. Review mismatched disposal money totals."
        : "Form-8949-Reconciliation fehlgeschlagen. Abweichende Disposal-Summen prüfen.");
  }

  const form8949ReconciliationOkMessage =
    isUS && form8949DisposalCrossExport?.ok
      ? form8949DisposalCrossExport.message
      : "";

  /*
   * General report blocker:
   * preview, strict fiat audit or hard backend audit.
   */
  const pdfExportDisabled =
    isPreviewMode ||
    usFiatAuditFailed ||
    hasHardExportFailure;

  /*
   * Calculation + Capital Gains reports additionally depend
   * on successful Disposal ↔ Form-8949 reconciliation.
   */
  const capitalPdfExportDisabled =
    pdfExportDisabled ||
    form8949MismatchBlocksExport;

  /*
   * Keep Form-8949 audit CSV downloadable even if the
   * reconciliation failed. Otherwise the user cannot
   * investigate the mismatch.
   */
  const form8949ExportDisabled = isPreviewMode;

  const usNftTransform8949Rows = React.useMemo(() => {
    if (!isUS) {
      return [];
    }

    return (auditForm8949Csv || []).filter((row) =>
      Boolean(
        String(row?.transform_kind || "").trim() ||
          String(row?.disposal_event_key || "")
            .toLowerCase()
            .includes("#nft_transform_disposal:")
      )
    );
  }, [isUS, auditForm8949Csv]);

  const usNftTransformPrintEvents = React.useMemo(() => {
    if (!isUS) {
      return [];
    }

    return (
      Array.isArray(ds?.blockpitNftTransformEvents)
        ? ds.blockpitNftTransformEvents
        : []
    )
      .filter((event) => {
        const kind = String(event?.transformKind || "")
          .trim()
          .toLowerCase();

        if (kind !== "thorhead_remint" && kind !== "vemugs_melt") {
          return false;
        }

        const tsSec = toSec(event?.timestamp || event?.mintedAt || 0);

        return rowInTaxYear(tsSec, Number(year));
      })
      .sort(
        (a, b) => Number(b?.timestamp || 0) - Number(a?.timestamp || 0)
      );
  }, [isUS, ds?.blockpitNftTransformEvents, year]);

  const AUDIT_FORM8949_COLS = [
    "token",
    "qty",
    "date_acquired",
    "date_disposed",
    "proceeds",
    "cost_basis",
    "gain_loss",
    "term",
    "holding_days",
    "source",
    "disposal_hash",
    "leg_index",
    "transform_source_ordinal",
    "transform_kind",
    "result_token_id",
    "allocation_method",
    "allocation_basis",
    "allocation_confidence",
    "allocation_safe_harbor",
    "valuation_method",
    "valuation_source",
    "valuation_confidence",
    "valuation_reference_type",
    "valuation_reference_hash",
    "form_8949_box_family",
    "legal_reference",
    "disposal_event_key",
    "clause_index",
    "event_index",
    "log_index",
    "token_contract",
  ];

  const AUDIT_FORM8949_HEADER_MAP = {
    token: "Description of property / digital asset",
    qty: "Quantity",
    date_acquired: "Date Acquired (UTC)",
    date_disposed: "Date Disposed (UTC)",
    proceeds: `Proceeds (${fiatCurrency})`,
    cost_basis: `Cost Basis (${fiatCurrency})`,
    gain_loss: `Gain/Loss (${fiatCurrency})`,
    term: "Term (ST/LT)",
    holding_days: "Holding Days",
    source: "Source",
    disposal_hash: "Disposal Tx Hash",
    leg_index: "Leg Index",
    transform_source_ordinal: "Transform source ordinal",
    transform_kind: "NFT transform kind",
    result_token_id: "Result NFT token ID",
    allocation_method: "Proceeds allocation method",
    allocation_basis: "Allocation basis",
    allocation_confidence: "Allocation confidence",
    allocation_safe_harbor: "Allocation legal safe harbor",
    valuation_method: "Valuation method",
    valuation_source: "Result FMV source",
    valuation_confidence: "Valuation confidence",
    valuation_reference_type: "Valuation reference type",
    valuation_reference_hash: "Valuation reference Tx hash",
    form_8949_box_family: "Form 8949 digital-asset box family",
    legal_reference: "Legal reference (US)",
    disposal_event_key: "Disposal event key",
    clause_index: "Clause index",
    event_index: "Event index",
    log_index: "Log index",
    token_contract: "Token contract",
  };

  const normalizeOtherIncomeClassificationForExport = (source, row) => {
    return (
      normalizeTaxClassificationForExport(pickCls(row), pickToken(row)) ||
      pickCls(row)
    );
  };

  const resolveExportRowSource = (sectionSource, row) => {
    const normalizedSection = String(sectionSource || "").toUpperCase();
    if (
      normalizedSection === "B3TR" ||
      normalizedSection === "VIP-180" ||
      normalizedSection === "VIP180"
    ) {
      return "VIP-180";
    }

    const fromRow = row?.sourcePlatform || row?.application || row?.source;
    if (fromRow) {
      const s = String(fromRow).trim();
      const upper = s.toUpperCase();
      if (upper === "VIP180" || upper === "VIP-180" || upper === "B3TR") {
        return "VIP-180";
      }
      return s;
    }

    return sectionSource;
  };

  // ---------- FINAL: Sonstige Einkünfte (§22 Nr. 3) ----------
  const finalOtherIncomeCsv = React.useMemo(() => {
    if (isUK && ukFinalExport) {
      return ukFinalExport.incomeRows;
    }

    const yearInt = Number(year);
    const inYear = (tsSec) => rowInTaxYear(tsSec, yearInt);

    const vetRows = Array.isArray(ds?.vetTaxRows) ? ds.vetTaxRows : (vetData || []);
    const vthoRows = getVthoDisplayRows(ds, vthoData);
    const b3trRows = Array.isArray(ds?.b3trTaxRows) ? ds.b3trTaxRows : (b3trData || []);

    const rows = [];
    let seq = 0;

    const OTHER_INCOME_TYPES = new Set([
      "staking_reward",
      "governance_reward",
      "airdrop",
      "solarwise_electricity_revenue",
      "foundation_bonus",
      "juicyfinance_lending_income",
      "juicyfinance_b3tr_reward_claim",
      "generated",
      "stargate_staking_rewards",
      "thorhead_staking_rewards",
      "vemugs_staking_rewards",
      "vemugs_mint_reward",
      "vemugs_reward_distribution",
      "verocket_generated_vtho_claim",
      "node_legacy_rewards",
      "vtho_synthetic",
      "x2earn_reward",
      "x2earn_reward_distribution",
      "x2earn_reward_claim",
      "vestation_reward_claim",
      "vexchange_farm_rewards",
      "betterswap_pool2earn_rewards",
      "mva_staking_rewards",
      "wov_staking_rewards",
      "wov_genesis_staking_rewards",
      "wov_genesis_staking_reward",
      "wov_genesis_reward",
      "genesis_staking_rewards",
      "golden_empire_reward",
      "safehaven_staking_rewards",
      "sagaz_reward_claim",
      "vfox_nft_holder_reward",
      "vesea_nft_staking_rewards",
      "union_nft_holder_rewards",
      "vimworld_veed_reward_distribution",
    ]);

    const pushIncome = (source, r) => {
      if (r == null || typeof r !== "object") return;

      const tsSec = toSec(pickRowTs(r));
      if (!inYear(tsSec)) return;

      if (isExcludedFromFinalTaxExport(r, { isUK })) return;

      const cls = pickCls(r);
      if (!OTHER_INCOME_TYPES.has(cls)) return;

      const hash0 = pickHash(r);
      const hash = hash0 || `synthetic:${source}:${seq}`;
      const txHashDisplay = hash0 ? pickDisplayHash(r, language) : hash;
      const eventKey = buildExportEventKey(hash, r, {
        seq,
        kind: pickCls(r) || "disposal",
      });

      const token = pickToken(r);
      const amount = pickAmount(r);
      if (!shouldIncludeCustomerExportRow({ Menge: amount, amount })) return;

      const rowSource = resolveExportRowSource(source, r);

      const incomeAmt =
        (isSyntheticVthoRow(r) || cls === "generated"
          ? pickSyntheticVthoIncomeFiat(r)
          : null) ??
        pickFiatTotal(r) ??
        firstFinite(r?.euroAmount, r?.totalEUR, r?.valueEUR, 0);

      const idFields = extractEventIdentityFields({
        ...r,
        Event_Key: eventKey,
        canonical_event_key: eventKey,
      });

      rows.push({
        Steuerjahr: taxYearLabelFor(yearInt),
        Steuerkategorie: TAX_CATEGORY_LABEL.other_income,
        Rechtsgrundlage: r?.Rechtsgrundlage || legalReference(cc, "income"),
        Quelle: rowSource,
        Klassifizierung: normalizeOtherIncomeClassificationForExport(rowSource, r),
        Asset: token,
        Menge: amount,
        Datum_UTC: fmtUtc(tsSec),
        [INCOME_KEY]: incomeAmt,
        [TAXABLE_KEY]: incomeAmt,
        Tx_Hash: txHashDisplay,
        Event_Key: eventKey,
        ...idFields,
        _sort: tsSec,
        _seq: seq++,
      });
    };

    for (const r of vetRows) pushIncome("VET", r);
    for (const r of vthoRows) pushIncome("VTHO", r);
    for (const r of b3trRows) pushIncome("VIP-180", r);

    rows.sort(
      (a, b) =>
        (b._sort || 0) - (a._sort || 0) || (a._seq || 0) - (b._seq || 0)
    );

    const baseRows = isUK
      ? dedupeUkIncomeExportRows(rows, INCOME_KEY, TAXABLE_KEY)
      : rows;

    return dedupeFinalExportRows(
      uniquifyEventKeys(baseRows),
      [INCOME_KEY, TAXABLE_KEY]
    );
  }, [
    ds,
    vetData,
    vthoData,
    b3trData,
    year,
    selectedCountry,
    isIT,
    isUS,
    isGR,
    isDE,
    isUK,
    ukFinalExport,
    INCOME_KEY,
    TAXABLE_KEY,
    fiatCurrency,
    cc,
    language,
  ]);

  // ---------- FINAL: Excluded Review (alle Tax-Tabellen, unabhängig von Export-Pfaden) ----------
  const finalExcludedReviewCsv = React.useMemo(() => {
    if (isUK && ukFinalExport) {
      return ukFinalExport.reviewRows || [];
    }

    const yearInt = Number(year);

    const inYearLocal = (tsSec) => rowInTaxYear(tsSec, yearInt);

    const vetRows = Array.isArray(ds?.vetTaxRows) ? ds.vetTaxRows : (vetData || []);
    const vthoRows = getVthoDisplayRows(ds, vthoData);
    const b3trRows = Array.isArray(ds?.b3trTaxRows) ? ds.b3trTaxRows : (b3trData || []);
    const nftRows = Array.isArray(ds?.nftTaxRows) ? ds.nftTaxRows : (nftData || []);
    const exRows = Array.isArray(ds?.exchangeTaxRows) ? ds.exchangeTaxRows : (exchangeTaxRows || []);
    const liqRows = Array.isArray(ds?.liquidityTaxRows) ? ds.liquidityTaxRows : (liquidityData || []);

    const engineRows = Array.isArray(ds?.reviewRows)
      ? ds.reviewRows
      : (
          Array.isArray(ds?.walletScopeReviewRows)
            ? ds.walletScopeReviewRows
            : []
        );

    const rows = [];
    let seq = 0;
    const includedEconomicKeys = new Set([
      ...(finalDisposalsCsv || []).flatMap((row) => [
        economicExportKey(row, [PROCEEDS_KEY, COST_KEY, PROFIT_KEY, TAXABLE_KEY]),
        economicExportLooseKey(row),
      ]),
      ...(finalOtherIncomeCsv || []).flatMap((row) => [
        economicExportKey(row, [INCOME_KEY, TAXABLE_KEY]),
        economicExportLooseKey(row),
      ]),
    ]);

    const pushReview = (source, r) => {
      if (r == null || typeof r !== "object") return;
      if (isInternalNftAcquisitionAllocationRow(r)) return;

      const tsSec = toSec(pickRowTs(r));
      if (!inYearLocal(tsSec)) return;
      if (!isExcludedFromFinalTaxExport(r, { isUK })) return;

      const hash0 = pickHash(r);
      const hash = hash0 || `synthetic:${source}:${seq}`;
      const txHashDisplay = hash0 ? pickDisplayHash(r, language) : hash;
      const eventKey = buildExportEventKey(hash, r, {
        seq,
        kind: pickCls(r) || "disposal",
      });

      const leg = legsStats(r);
      const cls = pickCls(r);
      const token = pickToken(r);
      const amount = pickAmount(r);

      const rowSource = resolveExportRowSource(source, r);

      const referenceFiat =
        pickFiatSell(r) ??
        pickFiatTotal(r) ??
        firstFinite(
          r?.euroAmount,
          r?.amountFiat,
          r?.totalFiat,
          r?.taxableProfitFiat,
          r?.taxableProfitEUR,
          r?.taxableProfit,
          0
        );

      const reviewProbe = {
        Steuerjahr: taxYearLabelFor(yearInt),
        Quelle: rowSource,
        Klassifizierung: normalizeOtherIncomeClassificationForExport(rowSource, r) || cls,
        Asset: token,
        Menge: amount,
        Datum_UTC: fmtUtc(tsSec),
        Tx_Hash: txHashDisplay,
        Event_Key: eventKey,
        [PROCEEDS_KEY]: Number.isFinite(referenceFiat) ? referenceFiat : 0,
        [INCOME_KEY]: Number.isFinite(referenceFiat) ? referenceFiat : 0,
        [TAXABLE_KEY]: Number.isFinite(referenceFiat) ? referenceFiat : 0,
      };
      if (
        includedEconomicKeys.has(economicExportKey(reviewProbe, [PROCEEDS_KEY, COST_KEY, PROFIT_KEY, TAXABLE_KEY])) ||
        includedEconomicKeys.has(economicExportKey(reviewProbe, [INCOME_KEY, TAXABLE_KEY])) ||
        includedEconomicKeys.has(economicExportLooseKey(reviewProbe))
      ) {
        return;
      }

      rows.push({
        Steuerjahr: taxYearLabelFor(yearInt),
        Quelle: rowSource,
        Klassifizierung: normalizeOtherIncomeClassificationForExport(rowSource, r) || cls,
        Asset: token,
        Menge: amount,
        Datum_UTC: fmtUtc(tsSec),
        [PROCEEDS_KEY]: Number.isFinite(referenceFiat) ? referenceFiat : 0,
        Tx_Hash: txHashDisplay,
        Event_Key: eventKey,
        Ausschlussgrund: pickExclusionReasonForExport(r),
        InSteuerSumme: language === "en" ? "No" : "Nein",
        FIFO_Lots: String(leg.fifoLots || 0),
        _sort: tsSec,
        _seq: seq++,
      });
    };

    const hasCanonicalEngineReviewRows = Array.isArray(ds?.reviewRows);

    if (hasCanonicalEngineReviewRows) {
      /*
       * Aktuelle Backend-Analysen:
       * dataset.reviewRows ist bereits der final wirtschaftlich
       * deduplizierte Review-Satz.
       *
       * Deshalb NICHT zusätzlich nftRows/vthoRows/etc. einsammeln.
       */
      for (const r of engineRows) {
        pushReview(
          r?.reviewSource ||
            r?.source ||
            "REVIEW",
          r
        );
      }
    } else {
      /*
       * Legacy-Snapshot-Fallback:
       * Alte Snapshots hatten ggf. noch kein dataset.reviewRows.
       */
      for (const r of vetRows) pushReview("VET", r);
      for (const r of vthoRows) pushReview("VTHO", r);
      for (const r of b3trRows) pushReview("VIP-180", r);
      for (const r of nftRows) pushReview("NFT", r);
      for (const r of exRows) pushReview("EXCHANGE", r);

      for (const lr of liqRows) {
        if (Array.isArray(lr?.disposals)) {
          for (const d of lr.disposals) pushReview("LIQUIDITY", d);
        } else {
          pushReview("LIQUIDITY", lr);
        }
      }
    }

    rows.sort(
      (a, b) =>
        (b._sort || 0) - (a._sort || 0) ||
        (a._seq || 0) - (b._seq || 0)
    );

    return dedupeFinalExportRows(
      uniquifyEventKeys(rows),
      [PROCEEDS_KEY]
    );
  }, [
    isUK,
    ukFinalExport,
    ds,
    engineReviewRows,
    vetData,
    vthoData,
    b3trData,
    nftData,
    liquidityData,
    exchangeTaxRows,
    finalDisposalsCsv,
    finalOtherIncomeCsv,
    year,
    selectedCountry,
    language,
    PROCEEDS_KEY,
    COST_KEY,
    PROFIT_KEY,
    TAXABLE_KEY,
    INCOME_KEY,
    fiatCurrency,
  ]);

  const selectedYearReviewCount =
    Number(finalExcludedReviewCsv?.length || 0);

  const historicalReviewCount = React.useMemo(() => {
    if (isUK) return 0;
    const yInt = Number(year);
    return (engineReviewRows || []).filter((row) => {
      const ts = toSec(pickRowTs(row));
      return !rowInTaxYear(ts, yInt);
    }).length;
  }, [isUK, year, engineReviewRows]);

  const reviewItemCount = selectedYearReviewCount;
  const hasReviewItems = selectedYearReviewCount > 0;
  const hasHistoricalReviewItems = historicalReviewCount > 0;
  const unresolvedMultiwalletCsvCount = Number(
    ds?.exchangeCustodyReviewSummary?.unresolvedMultiwalletCsvCount || 0
  );

  React.useEffect(() => {
    if (!isUK || !ukFinalExport) return;

    const fromExport = Number(ukFinalExport.reviewRows?.length || 0);
    const fromCsv = Number(finalExcludedReviewCsv?.length || 0);

    if (fromExport !== fromCsv) {
      console.error("❌ UK REVIEW EXPORT MISMATCH", {
        ukFinalExportReviewRows: fromExport,
        finalExcludedReviewCsvRows: fromCsv,
      });
    }
  }, [isUK, ukFinalExport, finalExcludedReviewCsv]);

  // ---------- columns ----------
  const FINAL_DISPOSAL_COLS = [
    "Steuerjahr",
    "Steuerkategorie",
    "Rechtsgrundlage",
    "Quelle",
    "Klassifizierung",
    "Asset",
    "Menge",
    "Datum_UTC",
    PROCEEDS_KEY,
    FEE_COST_KEY,
    COST_KEY,
    PROFIT_KEY,
    TAXABLE_KEY,
    ...(isUS ? [ST_KEY, LT_KEY] : []),
    ...(isDE ? [NONTAX_KEY, "Vollständig_steuerfrei"] : []),
    "Haltezeit_min_Ta",
    "Haltezeit_max_Ta",
    "Anschaffungsdatum_min_UTC",
    "Anschaffungsdatum_max_UTC",
    "FIFO_Lots",
    "Event_Key",
    "clause_index",
    "event_index",
    "log_index",
    "token_contract",
    "canonical_event_key",
    "disposal_event_key",
    "fifo_leg_index",
    ...(isUS
      ? [
          "nft_transform_kind",
          "nft_transform_source_ordinal",
          "nft_transform_result_token_id",
          "allocation_method",
          "allocation_policy",
          "allocation_basis",
          "allocation_confidence",
          "allocation_safe_harbor",
          "valuation_method",
          "valuation_side",
        ]
      : []),
    "Tx_Hash",
  ];

  const EXCLUDED_REVIEW_COLS = [
    "Steuerjahr",
    "Quelle",
    "Klassifizierung",
    "Asset",
    "Menge",
    "Datum_UTC",
    PROCEEDS_KEY,
    "Tx_Hash",
    "Event_Key",
    "Ausschlussgrund",
    "InSteuerSumme",
    "FIFO_Lots",
  ];

  const FINAL_INCOME_COLS = [
    "Steuerjahr",
    "Steuerkategorie",
    "Rechtsgrundlage",
    "Quelle",
    "Klassifizierung",
    "Asset",
    "Menge",
    "Datum_UTC",
    INCOME_KEY,
    TAXABLE_KEY,
    "Tx_Hash",
    "Event_Key",
    "clause_index",
    "event_index",
    "log_index",
    "token_contract",
    "canonical_event_key",
  ];

  const FINAL_RAW_TRANSFER_COLS = [
    "rawSource",
    "type",
    "token",
    "amount",
    "quote",
    "side",
    "price",

    "fiatCurrency",
    "fiatPrice",
    "fiatValue",
    "costBasisFiat",
    "gainLossFiat",
    "taxableFiat",

    "fee",
    "feeAsset",
    "recipient",
    "sender",
    "gasPayer",
    "custodyLocationId",
    "hash",
    "date",
    "eventRef",
    "clause_index",
    "event_index",
    "log_index",
    "token_contract",
    "canonical_event_key",
    "nft_transform_kind",
    "nft_source_token_ids",
    "nft_result_token_id",
    "nft_mint_hash",
    "nft_lifecycle_key",
    "valuation_method",
    "valuation_source",
    "valuation_confidence",
    "valuation_reference_type",
    "valuation_reference_hash",
    "valuation_reference_timestamp",
    "valuation_reference_age_days",
    "allocation_method",
    "allocation_policy",
    "allocation_basis",
    "allocation_confidence",
    "allocation_safe_harbor",
    "notes",
  ];

  const FINAL_DISPOSAL_NUMERIC_COLS = [
    "Menge",
    PROCEEDS_KEY,
    FEE_COST_KEY,
    COST_KEY,
    PROFIT_KEY,
    TAXABLE_KEY,
    ST_KEY,
    LT_KEY,
    NONTAX_KEY,
    "Haltezeit_min_Ta",
    "Haltezeit_max_Ta",
    "FIFO_Lots",
    "clause_index",
    "event_index",
    "log_index",
    "fifo_leg_index",
    "nft_transform_source_ordinal",
  ];

  const FINAL_INCOME_NUMERIC_COLS = [
    "Menge",
    INCOME_KEY,
    TAXABLE_KEY,
    "clause_index",
    "event_index",
    "log_index",
  ];

  const EXCLUDED_REVIEW_NUMERIC_COLS = [
    "Menge",
    PROCEEDS_KEY,
    "FIFO_Lots",
  ];

  const FINAL_RAW_TRANSFER_NUMERIC_COLS = [
    "amount",
    "price",
    "fiatPrice",
    "fiatValue",
    "costBasisFiat",
    "gainLossFiat",
    "taxableFiat",
    "fee",
    "clause_index",
    "event_index",
    "log_index",
    "valuation_reference_timestamp",
    "valuation_reference_age_days",
  ];

  const TAX_LOT_AUDIT_NUMERIC_COLS = [
    "quantity",
    "costBasisFiat",
    "proceedsFiat",
    "profitFiat",
    "taxableFiat",
    "nonTaxableFiat",
    "holdingDays",
    "lotIndex",
  ];

  const FORM_8949_NUMERIC_COLS = [
    "qty",
    "proceeds",
    "cost_basis",
    "gain_loss",
    "holding_days",
    "leg_index",
    "transform_source_ordinal",
    "clause_index",
    "event_index",
    "log_index",
  ];

  // ---------- UI ----------
  const RAW_TRANSFER_HEADER_MAP = isUS
    ? {
        rawSource: "Source",
        type: "Classification",
        token: "Asset",
        amount: "Quantity",
        quote: "Quote",
        side: "Side",
        price: "Price",
        fiatCurrency: "Fiat currency",
        fiatPrice: `Price ${fiatCurrency}`,
        fiatValue: `Market value / proceeds ${fiatCurrency}`,
        costBasisFiat: `Cost basis ${fiatCurrency}`,
        gainLossFiat: `Gain / loss ${fiatCurrency}`,
        taxableFiat: `Taxable ${fiatCurrency}`,
        fee: "Fee",
        feeAsset: "Fee asset",
        recipient: "Recipient / Exchange",
        sender: "Sender",
        gasPayer: "Gas Payer",
        custodyLocationId: "Custody",
        hash: "Tx hash / Exchange ID",
        date: "Date (UTC)",
        eventRef: "Event reference",
        clause_index: "Clause index",
        event_index: "Event index",
        log_index: "Log index",
        token_contract: "Token contract",
        canonical_event_key: "Canonical event key",
        nft_transform_kind: "NFT transform kind",
        nft_source_token_ids: "NFT source token IDs",
        nft_result_token_id: "NFT result token ID",
        nft_mint_hash: "NFT mint hash",
        nft_lifecycle_key: "NFT lifecycle key",
        valuation_method: "Valuation method",
        valuation_source: "Result FMV source",
        valuation_confidence: "Valuation confidence",
        valuation_reference_type: "Valuation reference type",
        valuation_reference_hash: "Valuation reference Tx hash",
        valuation_reference_timestamp: "Valuation reference timestamp",
        valuation_reference_age_days: "Valuation evidence age (days)",
        allocation_method: "Proceeds allocation method",
        allocation_policy: "Allocation policy",
        allocation_basis: "Allocation basis",
        allocation_confidence: "Allocation confidence",
        allocation_safe_harbor: "Allocation legal safe harbor",
        notes: "Notes",
      }
    : isIT
      ? {
          rawSource: "Fonte",
          type: "Classificazione",
          token: "Attività",
          amount: "Quantità",
          quote: "Quote",
          side: "Lato",
          price: "Prezzo",
          fiatCurrency: "Valuta fiat",
          fiatPrice: `Prezzo ${fiatCurrency}`,
          fiatValue: `Valore / ricavi ${fiatCurrency}`,
          costBasisFiat: `Costo di acquisto ${fiatCurrency}`,
          gainLossFiat: `Utile / perdita ${fiatCurrency}`,
          taxableFiat: `Imponibile ${fiatCurrency}`,
          fee: "Commissione",
          feeAsset: "Asset commissione",
          recipient: "Destinatario / Exchange",
          sender: "Mittente",
          gasPayer: "Gas Payer",
          custodyLocationId: "Custody",
          hash: "Tx hash / Exchange ID",
          date: "Data (UTC)",
          eventRef: "Riferimento evento",
          clause_index: "Indice clause",
          event_index: "Indice event",
          log_index: "Indice log",
          token_contract: "Contratto token",
          canonical_event_key: "Chiave evento canonica",
          nft_transform_kind: "Tipo transform NFT",
          nft_source_token_ids: "Token ID sorgente NFT",
          nft_result_token_id: "Token ID risultato NFT",
          nft_mint_hash: "Hash mint NFT",
          nft_lifecycle_key: "Chiave lifecycle NFT",
          valuation_method: "Metodo di valutazione",
          valuation_source: "Fonte FMV risultato",
          valuation_confidence: "Confidenza valutazione",
          valuation_reference_type: "Tipo riferimento valutazione",
          valuation_reference_hash: "Hash Tx riferimento valutazione",
          valuation_reference_timestamp: "Timestamp riferimento valutazione",
          valuation_reference_age_days: "Età evidenza valutazione (giorni)",
          allocation_method: "Metodo allocazione ricavi",
          allocation_policy: "Policy allocazione",
          allocation_basis: "Base allocazione",
          allocation_confidence: "Confidenza allocazione",
          allocation_safe_harbor: "Safe harbor legale allocazione",
          notes: "Note",
        }
      : {
          rawSource: "Quelle",
          type: "Klassifizierung",
          token: "Asset",
          amount: "Menge",
          quote: "Quote",
          side: "Seite",
          price: "Preis",
          fiatCurrency: "Fiatwährung",
          fiatPrice: `Kurs ${fiatCurrency}`,
          fiatValue: `Marktwert / Erlös ${fiatCurrency}`,
          costBasisFiat: `Anschaffungskosten ${fiatCurrency}`,
          gainLossFiat: `Gewinn / Verlust ${fiatCurrency}`,
          taxableFiat: `Steuerpflichtig ${fiatCurrency}`,
          fee: "Gebühr",
          feeAsset: "Gebühren-Asset",
          recipient: "Empfänger / Exchange",
          sender: "Sender",
          gasPayer: "Gas Payer",
          custodyLocationId: "Custody",
          hash: "Tx Hash / Exchange ID",
          date: "Datum (UTC)",
          eventRef: "Event-Referenz",
          clause_index: "Clause-Index",
          event_index: "Event-Index",
          log_index: "Log-Index",
          token_contract: "Token-Contract",
          canonical_event_key: "Kanonischer Event-Schlüssel",
          nft_transform_kind: "NFT-Transform-Art",
          nft_source_token_ids: "NFT-Quell-Token-IDs",
          nft_result_token_id: "NFT-Ergebnis-Token-ID",
          nft_mint_hash: "NFT-Mint-Hash",
          nft_lifecycle_key: "NFT-Lifecycle-Key",
          valuation_method: "Bewertungsmethode",
          valuation_source: "Quelle Result-FMV",
          valuation_confidence: "Bewertungs-Confidence",
          valuation_reference_type: "Bewertungsreferenz-Typ",
          valuation_reference_hash: "Bewertungsreferenz-Tx-Hash",
          valuation_reference_timestamp: "Bewertungsreferenz-Timestamp",
          valuation_reference_age_days: "Alter Bewertungsnachweis (Tage)",
          allocation_method: "Erlös-Allokationsmethode",
          allocation_policy: "Allokations-Policy",
          allocation_basis: "Allokationsbasis",
          allocation_confidence: "Allokations-Confidence",
          allocation_safe_harbor: "Allokation gesetzlicher Safe Harbor",
          notes: "Notizen",
        };

  const EXCHANGE_DETAIL_HEADER_MAP = isUS
    ? {
        source: "Source",
        kind: "Kind",
        txid: "Tx hash",
        base: "Asset",
        quote: "Price currency",
        side: "Side",
        amount: "Quantity",
        price: `Price ${fiatCurrency}`,
        totalFiat: `Total ${fiatCurrency}`,
        fee: "Fee",
        feeAsset: "Fee asset",
        taxableProfit: `Taxable ${fiatCurrency}`,
        rawQuote: "Original quote currency",
        rawPrice: "Original trade price",
        date: "Date (UTC)",
      }
    : isIT
      ? {
          source: "Fonte",
          kind: "Tipo",
          txid: "Hash transazione",
          base: "Attività",
          quote: "Valuta di valutazione",
          side: "Lato",
          amount: "Quantità",
          price: `Prezzo ${fiatCurrency}`,
          totalFiat: `Totale ${fiatCurrency}`,
          fee: "Commissione",
          feeAsset: "Asset commissione",
          taxableProfit: `Imponibile ${fiatCurrency}`,
          rawQuote: "Valuta quota originale",
          rawPrice: "Prezzo originale trade",
          date: "Data",
        }
      : {
          source: "Quelle",
          kind: "Typ",
          txid: "Tx Hash",
          base: "Asset",
          quote: "Bewertungswährung",
          side: "Seite",
          amount: "Menge",
          price: `Kurs ${fiatCurrency}`,
          totalFiat: `Gesamtwert ${fiatCurrency}`,
          fee: "Gebühr",
          feeAsset: "Gebühren-Asset",
          taxableProfit: `Steuerpflichtig ${fiatCurrency}`,
          rawQuote: "Original-Quote-Währung",
          rawPrice: "Original-Handelspreis",
          date: "Datum (UTC)",
        };

  const LIQUIDITY_DETAIL_HEADER_MAP = isUS
    ? {
        date: "Date (UTC)",
        type: "Type",
        hash: "Tx hash",
        pool: "Pool",
        poolAddress: "Pool address",
        lpToken: "LP token",
        lpAmount: "LP amount",
        depositSummary: "Deposits",
        depositFiat: `Deposits ${fiatCurrency}`,
        withdrawSummary: "Withdrawals",
        withdrawFiat: `Withdrawals ${fiatCurrency}`,
        token: "Disposed asset",
        amount: "Disposed quantity",
        unitPriceFiat: `Unit price ${fiatCurrency}`,
        proceedsFiat: `Proceeds ${fiatCurrency}`,
        taxableProfitFiat: `Taxable ${fiatCurrency}`,
        shortTermProfitFiat: `Short-term ${fiatCurrency}`,
        longTermProfitFiat: `Long-term ${fiatCurrency}`,
        fifoLots: "FIFO lots",
      }
    : isIT
      ? {
          date: "Data",
          type: "Tipo",
          hash: "Hash transazione",
          pool: "Pool",
          poolAddress: "Indirizzo pool",
          lpToken: "Token LP",
          lpAmount: "Quantità LP",
          depositSummary: "Depositi",
          depositFiat: `Depositi ${fiatCurrency}`,
          withdrawSummary: "Prelievi",
          withdrawFiat: `Prelievi ${fiatCurrency}`,
          token: "Asset ceduto",
          amount: "Quantità ceduta",
          unitPriceFiat: `Prezzo unitario ${fiatCurrency}`,
          proceedsFiat: `Corrispettivo ${fiatCurrency}`,
          taxableProfitFiat: `Imponibile ${fiatCurrency}`,
          fifoLots: "Lotti LIFO",
        }
      : {
          date: "Datum (UTC)",
          type: "Typ",
          hash: "Tx Hash",
          pool: "Pool",
          poolAddress: "Pool-Adresse",
          lpToken: "LP-Token",
          lpAmount: "LP-Menge",
          depositSummary: "Einzahlungen",
          depositFiat: `Einzahlungen ${fiatCurrency}`,
          withdrawSummary: "Auszahlungen",
          withdrawFiat: `Auszahlungen ${fiatCurrency}`,
          token: "Veräußertes Asset",
          amount: "Veräußerte Menge",
          unitPriceFiat: `Kurs ${fiatCurrency}`,
          proceedsFiat: `Erlös ${fiatCurrency}`,
          taxableProfitFiat: `Steuerpflichtig ${fiatCurrency}`,
          ...(isDE ? { isFullyTaxFreeLabel: "Vollständig steuerfrei" } : {}),
          fifoLots: "FIFO-Lots",
        };

  const DETAIL_HEADER_MAP = isUS
    ? {
        type: "Classification",
        token: "Asset",
        amount: "Quantity",
        hash: "Tx hash",
        eventIdType: "Event ID type",
        syntheticIntervalFromUtc: "Generation interval from (UTC)",
        syntheticIntervalToUtc: "Generation interval to (UTC)",
        syntheticIntervalEndReason: "Interval end reason",
        syntheticIntervalDurationSec: "Interval duration (sec)",
        walletSetWalletCount: "Wallets in set",
        internalEventId: "Internal event ID",
        euroPrice: `Price ${fiatCurrency}`,
        totalEUR: `Total ${fiatCurrency}`,
        fifoProfitEUR: `Profit/Loss ${fiatCurrency}`,
        taxableProfitEUR: `Taxable ${fiatCurrency}`,
        nonTaxableProfitEUR: `Tax free ${fiatCurrency}`,
        isFullyTaxFreeLabel: "Fully tax free",
        holdingDaysMin: "Holding min (days)",
        holdingDaysMax: "Holding max (days)",
        fifoLegsCount: "Lots",
        date: "Date (UTC)",
      }
    : isUK
      ? {
          type: "Classification",
          token: "Asset",
          amount: "Quantity",
          hash: "Tx hash",
          eventIdType: "Event ID type",
          syntheticIntervalFromUtc: "Generation interval from (UTC)",
          syntheticIntervalToUtc: "Generation interval to (UTC)",
          syntheticIntervalEndReason: "Interval end reason",
          syntheticIntervalDurationSec: "Interval duration (sec)",
          walletSetWalletCount: "Wallets in set",
          internalEventId: "Internal event ID",
          euroPrice: `Price ${fiatCurrency}`,
          totalEUR: `Total ${fiatCurrency}`,
          fifoProfitEUR: `Gain/Loss ${fiatCurrency}`,
          taxableProfitEUR: `Taxable ${fiatCurrency}`,
          holdingDaysMin: "Holding min (days)",
          holdingDaysMax: "Holding max (days)",
          fifoLegsCount: "UK matching legs",
          date: "Date (UTC)",
        }
    : isIT
      ? {
          type: "Classificazione",
          token: "Attività",
          amount: "Quantità",
          hash: "Hash transazione",
          eventIdType: "Tipo ID evento",
          syntheticIntervalFromUtc: "Intervallo generazione da (UTC)",
          syntheticIntervalToUtc: "Intervallo generazione a (UTC)",
          syntheticIntervalEndReason: "Motivo fine intervallo",
          syntheticIntervalDurationSec: "Durata intervallo (sec)",
          walletSetWalletCount: "Wallet nel set",
          internalEventId: "ID evento interno",
          euroPrice: `Prezzo ${fiatCurrency}`,
          totalEUR: `Valore totale ${fiatCurrency}`,
          fifoProfitEUR: `Utile/Perdita ${fiatCurrency}`,
          taxableProfitEUR: `Imponibile ${fiatCurrency}`,
          nonTaxableProfitEUR: `Non imponibile ${fiatCurrency}`,
          isFullyTaxFreeLabel: "Totalmente esente",
          holdingDaysMin: "Detenzione min (giorni)",
          holdingDaysMax: "Detenzione max (giorni)",
          fifoLegsCount: "Lotti LIFO",
          date: "Data",
        }
      : {
          type: "Klassifizierung",
          token: "Asset",
          amount: "Menge",
          hash: "Tx Hash",
          eventIdType: "Event-ID-Typ",
          syntheticIntervalFromUtc: "Generierungsintervall von (UTC)",
          syntheticIntervalToUtc: "Generierungsintervall bis (UTC)",
          walletSetWalletCount: "Anzahl Wallets im Set",
          internalEventId: "Interne Event-ID",
          euroPrice: `Kurs ${fiatCurrency}`,
          totalEUR: `Gesamtwert ${fiatCurrency}`,
          fifoProfitEUR: `Gewinn/Verlust ${fiatCurrency}`,
          taxableProfitEUR: `Steuerpflichtig ${fiatCurrency}`,
          nonTaxableProfitEUR: `Steuerfrei ${fiatCurrency}`,
          isFullyTaxFreeLabel: "Vollständig steuerfrei",
          holdingDaysMin: "Haltezeit min (Tage)",
          holdingDaysMax: "Haltezeit max (Tage)",
          fifoLegsCount: "FIFO-Lots",
          date: "Datum (UTC)",
        };

  const NFT_DETAIL_HEADER_MAP = isUS
    ? {
        type: "Classification",
        hash: "Tx hash",
        nftName: "NFT name",
        tokenId: "Token ID",
        token: "Payment asset",
        amount: "Quantity",
        euroPrice: `Price ${fiatCurrency}`,
        date: "Date (UTC)",
        saleValueEUR: `Sale value ${fiatCurrency}`,
        purchasePriceEUR: `Cost basis ${fiatCurrency}`,
        profitEUR: `Profit/Loss ${fiatCurrency}`,
        taxableLabel: "Taxable",
        taxableProfitEUR: `Taxable ${fiatCurrency}`,
        holdingDaysMin: "Holding min (days)",
        holdingDaysMax: "Holding max (days)",
        fifoLegsCount: "Lots",
      }
    : isUK
      ? {
          type: "Classification",
          hash: "Tx hash",
          nftName: "NFT name",
          tokenId: "Token ID",
          token: "Payment asset",
          amount: "Quantity",
          euroPrice: `Price ${fiatCurrency}`,
          date: "Date (UTC)",
          saleValueEUR: `Proceeds ${fiatCurrency}`,
          purchasePriceEUR: `Allowable cost ${fiatCurrency}`,
          profitEUR: `Gain/Loss ${fiatCurrency}`,
          taxableLabel: "Taxable",
          taxableProfitEUR: `Taxable ${fiatCurrency}`,
          holdingDaysMin: "Holding min (days)",
          holdingDaysMax: "Holding max (days)",
          fifoLegsCount: "UK matching legs",
        }
    : isIT
      ? {
          type: "Classificazione",
          hash: "Hash transazione",
          nftName: "Nome NFT",
          tokenId: "Token ID",
          token: "Asset pagamento",
          amount: "Quantità",
          euroPrice: `Prezzo ${fiatCurrency}`,
          date: "Data",
          saleValueEUR: `Corrispettivo ${fiatCurrency}`,
          purchasePriceEUR: `Costo di acquisto ${fiatCurrency}`,
          profitEUR: `Utile/Perdita ${fiatCurrency}`,
          taxableLabel: "Imponibile",
          taxableProfitEUR: `Imponibile ${fiatCurrency}`,
          holdingDaysMin: "Detenzione min (giorni)",
          holdingDaysMax: "Detenzione max (giorni)",
          fifoLegsCount: "Lotti LIFO",
        }
      : {
          type: "Klassifizierung",
          hash: "Tx Hash",
          nftName: "NFT-Name",
          tokenId: "Token-ID",
          token: "Zahlungs-Asset",
          amount: "Menge",
          euroPrice: `Kurs ${fiatCurrency}`,
          date: "Datum (UTC)",
          saleValueEUR: `Erlös ${fiatCurrency}`,
          purchasePriceEUR: `Anschaffungskosten ${fiatCurrency}`,
          profitEUR: `Gewinn/Verlust ${fiatCurrency}`,
          taxableLabel: "Steuerpflichtig",
          taxableProfitEUR: `Steuerpflichtig ${fiatCurrency}`,
          nonTaxableProfitEUR: `Steuerfrei ${fiatCurrency}`,
          isFullyTaxFreeLabel: "Vollständig steuerfrei",
          taxFreeBasisEUR: `Steuerfreie Basis ${fiatCurrency}`,
          holdingDaysMin: "Haltezeit min (Tage)",
          holdingDaysMax: "Haltezeit max (Tage)",
          fifoLegsCount: "FIFO-Lots",
        };

  const TOKEN_TAX_DETAIL_COLUMNS = [
    "type",
    "token",
    "amount",
    "hash",
    "eventIdType",
    "syntheticIntervalFromUtc",
    "syntheticIntervalToUtc",
    "syntheticIntervalEndReason",
    "syntheticIntervalDurationSec",
    "walletSetWalletCount",
    "internalEventId",
    "euroPrice",
    "totalEUR",
    "fifoProfitEUR",
    "taxableProfitEUR",
    ...(isDE ? ["nonTaxableProfitEUR", "isFullyTaxFreeLabel"] : []),
    "holdingDaysMin",
    "holdingDaysMax",
    "fifoLegsCount",
    "date",
  ];

  const NFT_TAX_DETAIL_COLUMNS = [
    "type",
    "hash",
    "nftName",
    "tokenId",
    "token",
    "amount",
    "euroPrice",
    "date",
    "saleValueEUR",
    "purchasePriceEUR",
    "profitEUR",
    "taxableLabel",
    "taxableProfitEUR",
    ...(isDE ? ["nonTaxableProfitEUR", "isFullyTaxFreeLabel", "taxFreeBasisEUR"] : []),
    "holdingDaysMin",
    "holdingDaysMax",
    "fifoLegsCount",
  ];

  const normalizeTaxDetailRows = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((r) => {
      const leg = legsStats(r);
      const isGeneratedVtho =
        isSyntheticVthoRow(r) ||
        String(r?.classification || r?.type || "").toLowerCase() === "generated";

      const taxableProfitEUR = isGeneratedVtho
        ? firstFinite(
            pickSyntheticVthoIncomeFiat(r),
            pickFiatTaxable(r),
            pickFiatTotal(r),
            r?.taxableProfitEUR,
            r?.euroAmount,
            0
          )
        : firstFinite(
            pickFiatTaxable(r),
            r?.taxableProfitEUR,
            leg.taxableProfitEUR,
            0
          );
      const nonTaxableProfitEUR = firstFinite(
        pickFiatNonTaxable(r),
        r?.nonTaxableProfitEUR,
        leg.nonTaxableProfitEUR,
        0
      );
      const totalEUR = firstFinite(
        pickFiatTotal(r),
        r?.totalEUR,
        r?.euroAmount,
        r?.grossSellEUR,
        leg.sellEUR
      );
      const fifoProfitEUR = firstFinite(
        pickFiatGrossProfit(r),
        r?.fifoProfitEUR,
        r?.grossProfitEUR,
        r?.profitEUR,
        leg.profitEUR
      );

      const holdingDaysMin = firstFinite(r?.holdingDaysMin, r?.holdingMin, leg.holdingMin);
      const holdingDaysMax = firstFinite(r?.holdingDaysMax, r?.holdingMax, leg.holdingMax);

      const fullyTaxFree =
        parseBool(r?.isFullyTaxFree ?? r?.fullyTaxFree) ||
        (!isIT &&
          Math.abs(Number(taxableProfitEUR || 0)) <= 1e-12 &&
          Math.abs(Number(nonTaxableProfitEUR || 0)) > 1e-12);
      const tsSec = toSec(pickRowTs(r));

      const hashRaw = pickHash(r);
      const hashDisplay = hashRaw ? pickDisplayHash(r, language) : "";

      return {
        ...r,
        ...getSyntheticVthoCsvFields(r),
        type: r?.type ?? r?.classification ?? "",
        amount: pickExportVthoTokenAmount(r),
        hash: hashDisplay || hashRaw,
        euroPrice: firstFinite(pickFiatPrice(r), r?.euroPrice, r?.unitPriceFiat, 0),
        totalEUR: Number.isFinite(totalEUR) ? totalEUR : "",
        fifoProfitEUR: Number.isFinite(fifoProfitEUR) ? fifoProfitEUR : "",
        taxableProfitEUR: Number.isFinite(taxableProfitEUR) ? taxableProfitEUR : "",
        nonTaxableProfitEUR: Number.isFinite(nonTaxableProfitEUR) ? nonTaxableProfitEUR : "",
        isFullyTaxFreeLabel: boolLabel(fullyTaxFree),
        holdingDaysMin: Number.isFinite(holdingDaysMin) ? holdingDaysMin : "",
        holdingDaysMax: Number.isFinite(holdingDaysMax) ? holdingDaysMax : "",
        fifoLegsCount:
          Array.isArray(r?.fifoLegs) ? r.fifoLegs.length : (r?.fifoLegsCount ?? leg.fifoLots ?? 0),
        date: r?.date || (tsSec ? formatCsvDateTime(tsSec) : ""),
      };
    });

  const normalizeNftTaxDetailRows = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((r) => {
      const leg = legsStats(r);
      const saleValueEUR = firstFinite(
        pickFiatSell(r),
        r?.saleValueEUR,
        r?.grossSellEUR,
        leg.sellEUR
      );
      const purchasePriceEUR = firstFinite(
        pickFiatBuy(r),
        r?.purchasePriceEUR,
        r?.grossBuyEUR,
        leg.buyEUR
      );
      const profitEUR = firstFinite(
        pickFiatGrossProfit(r),
        r?.profitEUR,
        r?.grossProfitEUR,
        leg.profitEUR
      );

      const taxableProfitEUR = firstFinite(
        pickFiatTaxable(r),
        r?.taxableProfitEUR,
        leg.taxableProfitEUR,
        0
      );

      const nonTaxableProfitEUR = firstFinite(
        pickFiatNonTaxable(r),
        r?.nonTaxableProfitEUR,
        leg.nonTaxableProfitEUR,
        0
      );

      const holdingDaysMin = isUK
        ? ""
        : firstFinite(r?.holdingDaysMin, r?.holdingMin, leg.holdingMin);
      const holdingDaysMax = isUK
        ? ""
        : firstFinite(r?.holdingDaysMax, r?.holdingMax, leg.holdingMax);

      const fullyTaxFree =
        parseBool(r?.isFullyTaxFree ?? r?.fullyTaxFree) ||
        (!isIT &&
          Math.abs(Number(taxableProfitEUR || 0)) <= 1e-12 &&
          Math.abs(Number(nonTaxableProfitEUR || 0)) > 1e-12);
      const tsSec = toSec(pickRowTs(r));
      const taxFreeBasisEUR = firstFinite(
        r?.taxFreeBasisEUR,
        r?.taxFreeBasisFiat,
        fullyTaxFree ? saleValueEUR : nonTaxableProfitEUR
      );

      return {
        ...r,
        type: r?.type ?? r?.classification ?? "",
        date: r?.date || (tsSec ? formatCsvDateTime(tsSec) : ""),
        saleValueEUR: Number.isFinite(saleValueEUR) ? saleValueEUR : "",
        purchasePriceEUR: Number.isFinite(purchasePriceEUR) ? purchasePriceEUR : "",
        profitEUR: Number.isFinite(profitEUR) ? profitEUR : "",
        taxableLabel: boolLabel(r?.taxable ?? Math.abs(Number(taxableProfitEUR || 0)) > 1e-12),
        taxableProfitEUR: Number.isFinite(taxableProfitEUR) ? taxableProfitEUR : "",
        nonTaxableProfitEUR: Number.isFinite(nonTaxableProfitEUR) ? nonTaxableProfitEUR : "",
        isFullyTaxFreeLabel: boolLabel(fullyTaxFree),
        taxFreeBasisEUR: Number.isFinite(taxFreeBasisEUR) ? taxFreeBasisEUR : "",
        holdingDaysMin: isUK ? "" : (Number.isFinite(holdingDaysMin) ? holdingDaysMin : ""),
        holdingDaysMax: isUK ? "" : (Number.isFinite(holdingDaysMax) ? holdingDaysMax : ""),
        fifoLegsCount:
          Array.isArray(r?.fifoLegs) ? r.fifoLegs.length : (r?.fifoLegsCount ?? leg.fifoLots ?? 0),
      };
    });

  const vetDetailRows = React.useMemo(
    () =>
      normalizeTaxDetailRows(
        filterRowsToSelectedYear(
          Array.isArray(ds?.vetTaxRows) ? ds.vetTaxRows : (vetData || [])
        )
      ),
    [ds, vetData, selectedCountry, yearInt]
  );
  const vthoDetailRows = React.useMemo(
    () =>
      normalizeTaxDetailRows(
        filterRowsToSelectedYear(getVthoDisplayRows(ds, vthoData))
      ),
    [ds, vthoData, selectedCountry, yearInt]
  );
  const vthoGeneratedIntervalDetailRows = React.useMemo(
    () =>
      normalizeTaxDetailRows(
        filterRowsToSelectedYear(
          Array.isArray(ds?.vthoGeneratedIntervalRows) ? ds.vthoGeneratedIntervalRows : []
        )
      ),
    [ds, selectedCountry, yearInt]
  );
  const b3trDetailRows = React.useMemo(
    () =>
      normalizeTaxDetailRows(
        filterRowsToSelectedYear(
          Array.isArray(ds?.b3trTaxRows) ? ds.b3trTaxRows : (b3trData || [])
        )
      ),
    [ds, b3trData, selectedCountry, yearInt]
  );
  const nftDetailRows = React.useMemo(
    () =>
      normalizeNftTaxDetailRows(
        filterRowsToSelectedYear(
          Array.isArray(ds?.nftTaxRows) ? ds.nftTaxRows : (nftData || [])
        )
      ),
    [ds, nftData, selectedCountry, yearInt]
  );

  const NFT_ACQUISITION_LEDGER_COLUMNS = [
    "eventType",
    "collection",
    "nftContract",
    "tokenId",
    "acquisitionType",
    "paymentAsset",
    "paymentAmount",
    "acquisitionCostFiat",
    "acquisitionFiatCurrency",
    "paymentHash",
    "mintHash",
    "economicEventId",
    "batchId",
    "batchSize",
    "batchIndex",
    "batch",
    "paymentDateUtc",
    "mintDateUtc",
  ];

  const NFT_ACQUISITION_HEADER_MAP = language === "en" || isUS || isUK
    ? {
        eventType: "Event type",
        collection: "Collection",
        nftContract: "NFT contract",
        tokenId: "Token ID",
        acquisitionType: "Acquisition type",
        paymentAsset: "Payment asset",
        paymentAmount: "Payment amount",
        acquisitionCostFiat: `Acquisition cost ${fiatCurrency}`,
        acquisitionFiatCurrency: "Fiat currency",
        paymentHash: "Payment hash",
        mintHash: "Mint hash",
        economicEventId: "Economic event ID",
        batchId: "Batch ID",
        batchSize: "Batch size",
        batchIndex: "Batch index",
        batch: "Batch",
        paymentDateUtc: "Payment date (UTC)",
        mintDateUtc: "Mint date (UTC)",
      }
    : isIT
      ? {
          eventType: "Tipo evento",
          collection: "Collezione",
          nftContract: "Contratto NFT",
          tokenId: "Token ID",
          acquisitionType: "Tipo acquisizione",
          paymentAsset: "Asset pagamento",
          paymentAmount: "Importo pagamento",
          acquisitionCostFiat: `Costo di acquisto ${fiatCurrency}`,
          acquisitionFiatCurrency: "Valuta fiat",
          paymentHash: "Hash pagamento",
          mintHash: "Hash mint",
          economicEventId: "ID evento economico",
          batchId: "ID batch",
          batchSize: "Dimensione batch",
          batchIndex: "Indice batch",
          batch: "Batch",
          paymentDateUtc: "Data pagamento (UTC)",
          mintDateUtc: "Data mint (UTC)",
        }
      : {
          eventType: "Ereignistyp",
          collection: "Collection",
          nftContract: "NFT-Contract",
          tokenId: "Token-ID",
          acquisitionType: "Anschaffungsart",
          paymentAsset: "Zahlungs-Asset",
          paymentAmount: "Zahlungsbetrag",
          acquisitionCostFiat: `Anschaffungskosten ${fiatCurrency}`,
          acquisitionFiatCurrency: "Fiat-Währung",
          paymentHash: "Payment-Hash",
          mintHash: "Mint-Hash",
          economicEventId: "Economic Event ID",
          batchId: "Batch-ID",
          batchSize: "Batch-Größe",
          batchIndex: "Batch-Index",
          batch: "Batch",
          paymentDateUtc: "Payment-Datum (UTC)",
          mintDateUtc: "Mint-Datum (UTC)",
        };

  const nftAcquisitionLedgerRows = React.useMemo(
    () =>
      buildNftAcquisitionExportRows(
        filterRowsToSelectedYear(
          Array.isArray(ds?.nftAcquisitionRows) ? ds.nftAcquisitionRows : []
        ),
        { fiatCurrency }
      ),
    [ds, fiatCurrency, selectedCountry, yearInt]
  );

  const nftAcquisitionHistory =
    React.useMemo(
      () =>
        buildNftAcquisitionExportRows(
          Array.isArray(
            ds?.nftAcquisitionRows
          )
            ? ds.nftAcquisitionRows
            : [],
          {
            fiatCurrency,
          }
        ),
      [
        ds,
        fiatCurrency,
      ]
    );

  const taxLotAuditCsv = React.useMemo(() => {
    if (isUS) return [];

    const rows = [];
    let seq = 0;

    const nftAcquisitionByIdentity =
      new Map();

    for (
      const acquisition of
      nftAcquisitionHistory
    ) {
      const contract =
        String(
          acquisition?.nftContract ||
          ""
        )
          .trim()
          .toLowerCase();

      const tokenId =
        String(
          acquisition?.tokenId ??
          ""
        ).trim();

      if (
        !contract ||
        !tokenId
      ) {
        continue;
      }

      const key =
        `${contract}|${tokenId}`;

      if (
        !nftAcquisitionByIdentity.has(
          key
        )
      ) {
        nftAcquisitionByIdentity.set(
          key,
          []
        );
      }

      nftAcquisitionByIdentity
        .get(key)
        .push(acquisition);
    }

    const pickAuditAsset =
      (row = {}) => {
        const regular =
          String(
            pickToken(row) ||
            ""
          ).trim();

        if (regular) {
          return regular;
        }

        const tokenId =
          row?.tokenId ??
          row?.nft_token_id ??
          row?.token_id ??
          null;

        if (
          tokenId == null ||
          tokenId === ""
        ) {
          return "";
        }

        const name =
          String(
            row?.nftName ||
            row?.nft_name ||
            row?.collectionName ||
            row?.collection_name ||
            row?.collection ||
            "NFT"
          ).trim();

        const contract =
          String(
            row?.nftContract ||
            row?.nft_contract ||
            row?.token_contract ||
            row?.contract ||
            ""
          )
            .trim()
            .toLowerCase();

        if (
          name &&
          name.toUpperCase() !==
            "NFT"
        ) {
          return `${name} #${tokenId}`;
        }

        if (contract) {
          return `NFT:${contract}:${tokenId}`;
        }

        return `NFT #${tokenId}`;
      };

    const resolveAuditAcquisitionEventId =
      (
        disposal = {},
        leg = {}
      ) => {
        const direct =
          leg?.eventId ||
          leg?.event_id ||
          leg?.sourceEventId ||
          leg?.source_event_id ||
          leg?.acquisitionEventId ||
          leg?.src_event_id ||
          "";

        if (direct) {
          return String(
            direct
          );
        }

        const cls =
          String(
            disposal?.classification ||
            disposal?.type ||
            ""
          ).toLowerCase();

        if (
          cls !==
          "nft_transform_disposal"
        ) {
          /*
           * Legacy fallback only for normal S104 lots.
           */
          return String(
            leg?.hash ||
            ""
          );
        }

        const contract =
          String(
            disposal?.nftContract ||
            disposal?.nft_contract ||
            disposal?.token_contract ||
            disposal?.contract ||
            ""
          )
            .trim()
            .toLowerCase();

        const tokenId =
          String(
            disposal?.tokenId ??
            disposal?.nft_token_id ??
            disposal?.token_id ??
            ""
          ).trim();

        if (
          !contract ||
          !tokenId
        ) {
          return "";
        }

        const candidates =
          nftAcquisitionByIdentity.get(
            `${contract}|${tokenId}`
          ) || [];

        /*
         * Fail closed:
         * niemals bei mehreren Kandidaten raten.
         */
        if (
          candidates.length !== 1
        ) {
          return "";
        }

        return String(
          candidates[0]
            ?.economicEventId ||
          ""
        );
      };

    const yearInt = Number(year);
    const inYear = (tsSec) => rowInTaxYear(tsSec, yearInt);

    const utcDayKeyFromSec = (tsSec) => {
      const ts = Number(tsSec || 0);
      if (!ts) return "";
      const d = new Date(ts * 1000);
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    };

    const utcCalendarDayDiffFromSec = (fromTs, toTs) => {
      const a = utcDayKeyFromSec(fromTs);
      const b = utcDayKeyFromSec(toTs);
      if (!a || !b) return null;

      const ams = Date.parse(`${a}T00:00:00.000Z`);
      const bms = Date.parse(`${b}T00:00:00.000Z`);

      if (!Number.isFinite(ams) || !Number.isFinite(bms)) return null;
      return Math.round((bms - ams) / 86400000);
    };

    const ukDisposalExcludedFromTaxTotals = (row = {}) => {
      const joined = [
        row?.displayPriceStatus,
        row?.pricingStatus,
        row?.taxStatus,
        row?.taxableStatus,
        row?.taxResolutionStatus,
        row?.acquisitionStatus,
        row?.reviewReason,
        row?.pricingReason,
        row?.auditStatus,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join("|");

      return Boolean(
        row?.includedInTaxTotals === false ||
          row?.excludedFromTaxTotals === true ||
          row?.taxComputationSkipped === true ||
          row?.isTaxComputationBlocked === true ||
          row?.pricingMissing === true ||
          row?.priceMissing === true ||
          row?.missingFiatPrice === true ||
          row?.missingCostBasis === true ||
          row?.costBasisUnknown === true ||
          row?.cost_basis_unknown === true ||
          row?.requiresUserCostBasis === true ||
          row?.userCostBasisRequired === true ||
          row?.fifoShortage === true ||
          Number(row?.shortage || 0) > 0 ||
          joined.includes("fail") ||
          joined.includes("missing_price") ||
          joined.includes("token_price_not_available") ||
          joined.includes("cost_basis_unknown") ||
          joined.includes("unknown_cost_basis") ||
          joined.includes("liquidity_underlying_price_missing") ||
          joined.includes("manual_review") ||
          joined.includes("review_required")
      );
    };

    const ukReviewReasonForAudit = (row = {}) => {
      return (
        row?.reviewReason ||
        row?.pricingReason ||
        row?.taxResolutionStatus ||
        row?.auditStatus ||
        row?.auditReasons?.join?.("|") ||
        "excluded_from_tax_totals"
      );
    };

    const inferUkMatchingRuleForAudit = (disposal = {}, leg = {}, disposalTs = 0, acqTs = 0) => {
      const explicit = String(
        leg?.matchingRule ||
          leg?.ukMatchingRule ||
          leg?.matching_rule ||
          (Array.isArray(leg?.matchingRules) ? leg.matchingRules.join("+") : "") ||
          ""
      ).trim();

      if (explicit) return explicit;

      const cls = pickCls(disposal);
      const method = String(
        leg?.costBasisMethod ||
          disposal?.costBasisMethod ||
          disposal?.ukMethod ||
          ""
      ).toUpperCase();

      if (cls === "sale_payment" || method === "UK_NFT_IDENTIFIABLE") {
        return "nft_identifiable";
      }

      if (isUK) {
        const dayDiff = utcCalendarDayDiffFromSec(disposalTs, acqTs);

        if (dayDiff === 0) return "same_day";
        if (dayDiff != null && dayDiff >= 1 && dayDiff <= 30) {
          return "bed_and_breakfast_30d";
        }

        return "section_104";
      }

      return "";
    };

    const normalizeUkAuditHash = (value = "") =>
      String(value || "").trim().toLowerCase().replace(/^0x/, "");

    const ukExportRowFingerprintFromCsv = (row = {}) =>
      [
        normalizeUkAuditHash(row.Tx_Hash),
        String(row.Klassifizierung || "").toLowerCase(),
        String(row.Asset || "").toUpperCase(),
        Number(row.Menge || 0).toFixed(8),
        String(row.Datum_UTC || ""),
      ].join("|");

    const ukExportRowFingerprintFromDisposal = (disposal, tsSec) => {
      const hash0 = pickHash(disposal);
      const hashDisplay = hash0 ? pickDisplayHash(disposal, language) : "";
      return [
        normalizeUkAuditHash(hashDisplay || hash0),
        pickCls(disposal),
        pickToken(disposal),
        Number(pickAmount(disposal)).toFixed(8),
        fmtUtc(tsSec),
      ].join("|");
    };

    const includedCapitalKeys = new Set();
    const excludedReviewKeys = new Set();
    const exportFingerprints = new Map();

    if (isUK && ukFinalExport) {
      for (const row of ukFinalExport.capitalRows || []) {
        const ek = String(row.Event_Key || "").trim().toLowerCase();
        if (ek) includedCapitalKeys.add(ek);
        const fp = ukExportRowFingerprintFromCsv(row);
        if (fp) exportFingerprints.set(fp, { eventKey: ek, included: true });
      }
      for (const row of ukFinalExport.reviewRows || []) {
        const ek = String(row.Event_Key || "").trim().toLowerCase();
        if (ek) excludedReviewKeys.add(ek);
        const fp = ukExportRowFingerprintFromCsv(row);
        if (fp) exportFingerprints.set(fp, { eventKey: ek, included: false });
      }
    }

    const resolveUkAuditInclusion = (disposal, tsSec, source, legIndex) => {
      const auditEventKey = makeUkFinalEventKeyFromTaxRow(disposal, {
        rowSeq: legIndex,
        syntheticSource: source,
      });
      const fp = ukExportRowFingerprintFromDisposal(disposal, tsSec);
      const fpMeta = exportFingerprints.get(fp);

      if (fpMeta) {
        return {
          included: fpMeta.included,
          auditEventKey: fpMeta.eventKey || auditEventKey,
        };
      }

      if (auditEventKey) {
        if (excludedReviewKeys.has(auditEventKey)) {
          return { included: false, auditEventKey };
        }
        if (includedCapitalKeys.has(auditEventKey)) {
          return { included: true, auditEventKey };
        }
      }

      return {
        included: !ukDisposalExcludedFromTaxTotals(disposal),
        auditEventKey,
      };
    };

    const vetRows = Array.isArray(ds?.vetTaxRows) ? ds.vetTaxRows : (vetData || []);
    const vthoRows = getVthoDisplayRows(ds, vthoData);
    const b3trRows = Array.isArray(ds?.b3trTaxRows) ? ds.b3trTaxRows : (b3trData || []);
    const nftRows = Array.isArray(ds?.nftTaxRows) ? ds.nftTaxRows : (nftData || []);
    const liqRows = Array.isArray(ds?.liquidityTaxRows) ? ds.liquidityTaxRows : (liquidityData || []);
    const exRows = Array.isArray(ds?.exchangeTaxRows) ? ds.exchangeTaxRows : (exchangeTaxRows || []);

    const normalizeCustodyId = (value) => {
      const raw = String(value || "").trim().toLowerCase();
      if (!raw) return "";
      if (
        raw.startsWith("wallet:") ||
        raw.startsWith("exchange:") ||
        raw.startsWith("chain:")
      ) {
        return raw;
      }
      if (/^0x[a-f0-9]{40}$/.test(raw)) return `wallet:${raw}`;
      return raw;
    };

    const custodyByEventId = new Map();
    const rememberCustody = (row) => {
      if (!row || typeof row !== "object") return;
      const custody = normalizeCustodyId(
        row.custodyLocationId ||
          row.sourceCustodyLocationId ||
          row.display_wallet ||
          row.wallet ||
          row.walletAddress ||
          row.address ||
          row.sender ||
          row.to ||
          row.from ||
          row.gasPayer ||
          ""
      );
      if (!custody) return;

      const keys = [
        pickHash(row),
        row.eventId,
        row.event_id,
        row.exportEventKey,
        row.acquisitionEventId,
        row.src_event_id,
      ];
      for (const key of keys) {
        const k = String(key || "").trim().toLowerCase();
        if (k && !custodyByEventId.has(k)) {
          custodyByEventId.set(k, custody);
        }
      }
    };

    for (const row of [
      ...vetRows,
      ...vthoRows,
      ...b3trRows,
      ...nftRows,
      ...exRows,
      ...(Array.isArray(vetVthoData) ? vetVthoData : []),
      ...(Array.isArray(vip180Data) ? vip180Data : []),
    ]) {
      rememberCustody(row);
      if (Array.isArray(row?.disposals)) {
        for (const d of row.disposals) rememberCustody(d);
      }
    }

    const pushLeg = (source, disposal, leg, legIndex) => {
      const tsSec = toSec(pickRowTs(disposal));
      if (!inYear(tsSec)) return;

      if (!isUK && isExcludedFromFinalTaxExport(disposal, { isUK: false })) {
        return;
      }

      const acqTs = toSec(
        leg?.acquisitionTs ??
        leg?.timestamp ??
        leg?.ts ??
        leg?.acquired_ts ??
        leg?.acquiredTs ??
        0
      );

      const proceedsFiat = firstFinite(
        leg?.sellEUR,
        leg?.sell,
        leg?.proceedsEUR,
        leg?.grossSellEUR,
        0
      );

      const baseCostBasisFiat = firstFinite(
        leg?.buyEUR,
        leg?.buy,
        leg?.costEUR,
        leg?.costBasisEUR,
        leg?.grossBuyEUR,
        0
      );

      const parentLegs = Array.isArray(disposal?.fifoLegs)
        ? disposal.fifoLegs
        : [];

      const parentBaseLegCost = parentLegs.reduce(
        (sum, currentLeg) =>
          sum +
          firstFinite(
            currentLeg?.buyEUR,
            currentLeg?.buy,
            currentLeg?.costEUR,
            currentLeg?.costBasisEUR,
            currentLeg?.grossBuyEUR,
            0
          ),
        0
      );

      const parentCostBasis = firstFinite(
        pickFiatBuy(disposal),
        disposal?.grossBuyFiat,
        disposal?.grossBuyEUR,
        disposal?.costBasisFiat
      );

      const parentMatchedQty = parentLegs.reduce(
        (sum, currentLeg) =>
          sum +
          Math.abs(
            firstFinite(
              currentLeg?.qty,
              currentLeg?.quantity,
              currentLeg?.amount,
              0
            )
          ),
        0
      );

      const currentLegQty = Math.abs(
        firstFinite(
          leg?.qty,
          leg?.quantity,
          leg?.amount,
          0
        )
      );

      const parentCostResidual =
        isUK &&
        Number.isFinite(parentCostBasis)
          ? parentCostBasis - parentBaseLegCost
          : 0;

      const residualWeight =
        parentMatchedQty > 1e-12
          ? currentLegQty / parentMatchedQty
          : legIndex === 0
            ? 1
            : 0;

      const allocatedParentAdjustment =
        Math.abs(parentCostResidual) > 1e-12
          ? parentCostResidual * residualWeight
          : 0;

      const costBasisFiat =
        baseCostBasisFiat +
        allocatedParentAdjustment;

      const profitFiat =
        proceedsFiat -
        costBasisFiat;

      const taxableFlag =
        parseBool(leg?.isTaxable ?? leg?.taxable ?? leg?.steuerpflichtig);

      const cls = pickCls(disposal);
      const holdingDaysResolved = resolveLegHoldingDays(leg, {
        timestamp: tsSec,
        ts: tsSec,
      });
      const holdingDays = isUK
        ? ""
        : holdingDaysResolved == null
          ? ""
          : holdingDaysResolved;

      const legQuantity = firstFinite(leg?.qty, leg?.quantity, leg?.amount, 0);

      const disposalQuantity = firstFinite(
        disposal?.nftPaymentAmount,
        disposal?.amount,
        disposal?.qty,
        disposal?.quantity,
        legQuantity,
        0
      );

      const quantity =
        isUK && cls === "sale_payment"
          ? disposalQuantity
          : legQuantity;

      const matchingRule = isUK
        ? inferUkMatchingRuleForAudit(disposal, leg, tsSec, acqTs)
        : String(
            leg?.matchingRule ||
              (Array.isArray(leg?.matchingRules) ? leg.matchingRules.join("+") : "") ||
              ""
          );

      let includedInTaxTotals = true;
      let auditEventKey = "";
      if (isUK) {
        const resolved = resolveUkAuditInclusion(disposal, tsSec, source, legIndex);
        includedInTaxTotals = resolved.included;
        auditEventKey = resolved.auditEventKey || "";
      }

      const disposalCustodyDirect = normalizeCustodyId(
        disposal?.custodyLocationId ||
          disposal?.sourceCustodyLocationId ||
          disposal?.gasPayer ||
          disposal?.from ||
          disposal?.sender ||
          disposal?.wallet ||
          disposal?.walletAddress ||
          disposal?.display_wallet ||
          ""
      );

      const disposalLookupKeys = [
        pickHash(disposal),
        disposal?.eventId,
        disposal?.event_id,
        disposal?.exportEventKey,
        disposal?.disposalEventKey,
        disposal?.Event_Key,
      ]
        .map((key) => String(key || "").trim().toLowerCase())
        .filter(Boolean);

      let disposalCustody = disposalCustodyDirect;
      if (!disposalCustody) {
        for (const key of disposalLookupKeys) {
          const found = custodyByEventId.get(key);
          if (found) {
            disposalCustody = found;
            break;
          }
        }
      }

      const acquisitionEventId =
        resolveAuditAcquisitionEventId(
          disposal,
          leg
        );

      const acquisitionLookupKey = String(
        acquisitionEventId ||
          leg?.hash ||
          leg?.sourceHash ||
          ""
      )
        .trim()
        .toLowerCase();

      const acquisitionCustodyDirect = normalizeCustodyId(
        leg?.custodyLocationId ||
          leg?.sourceCustodyLocationId ||
          leg?.wallet ||
          leg?.address ||
          leg?.display_wallet ||
          ""
      );

      // Wallet-scoped FIFO (DE/US/IT): Lot stammt aus dem Disposal-Custody-Pool.
      // Lookup deckt bestehende Snapshots ab, in denen Leg-Custody gestrippt wurde.
      const acquisitionCustody =
        acquisitionCustodyDirect ||
        (acquisitionLookupKey
          ? custodyByEventId.get(acquisitionLookupKey) || ""
          : "") ||
        (!isUK ? disposalCustody : "");

      const disposalEventId =
        disposal?.eventId ||
        disposal?.event_id ||
        disposal?.exportEventKey ||
        disposal?.disposalEventKey ||
        disposal?.Event_Key ||
        pickHash(disposal) ||
        "";

      let crossCustody = "UNKNOWN";
      let custodyNote = "";
      if (disposalCustody && acquisitionCustody) {
        crossCustody =
          disposalCustody === acquisitionCustody ? "NO" : "YES";
      } else if (!disposalCustody) {
        custodyNote =
          "DISPOSAL_CUSTODY_UNRESOLVED: charged wallet not found on disposal or ledger lookup";
        if (typeof console !== "undefined" && console.warn) {
          console.warn("[tax-lot-audit] empty disposal custody", {
            eventId:
              disposal?.eventId ||
              disposal?.event_id ||
              disposal?.exportEventKey ||
              "",
            hash: pickHash(disposal),
            token: pickToken(disposal),
            classification: cls,
            reason: custodyNote,
          });
        }
      }

      rows.push({
        source,
        classification: cls,
        asset:
          pickAuditAsset(
            disposal
          ),
        quantity,
        acquiredUtc: acqTs ? fmtUtc(acqTs) : "",
        disposedUtc: tsSec ? fmtUtc(tsSec) : "",
        costBasisFiat,
        proceedsFiat,
        profitFiat,
        taxableFiat: taxableFlag ? profitFiat : 0,
        ...(isDE ? {
          nonTaxableFiat: taxableFlag ? 0 : profitFiat,
          fullyTaxFree: boolLabel(!taxableFlag),
        } : {}),
        ...(isUK ? {
          matchingRule,
          includedInTaxTotals: includedInTaxTotals ? "Yes" : "No",
          reviewStatus: includedInTaxTotals ? "Tax included" : "Review / excluded",
          reviewReason: includedInTaxTotals ? "" : ukReviewReasonForAudit(disposal),
          _auditEventKey: auditEventKey,
        } : {}),
        ...(!isUK ? { holdingDays } : {}),
        disposalCustody,
        acquisitionCustody,
        acquisitionEventId,
        disposalEventId,
        crossCustody,
        ...(custodyNote ? { custodyNote } : {}),
        lotIndex: legIndex + 1,
        txHash: pickHash(disposal),
        _sort: tsSec,
        _seq: seq++,
      });
    };

    const pushTokenDisposalLegs = (source, rows) => {
      for (const r of rows) {
        if (isTokenCapitalDisposalCls(pickCls(r)) && Array.isArray(r?.fifoLegs)) {
          r.fifoLegs.forEach((leg, idx) => pushLeg(source, r, leg, idx));
        }
      }
    };

    pushTokenDisposalLegs("VET", vetRows);
    pushTokenDisposalLegs("VTHO", vthoRows);
    pushTokenDisposalLegs("VIP-180", b3trRows);

    for (const r of exRows) {
      if (Array.isArray(r?.fifoLegs)) {
        r.fifoLegs.forEach((leg, idx) => pushLeg("EXCHANGE", r, leg, idx));
      }
    }

    for (const lr of liqRows) {
      const disposals = Array.isArray(lr?.disposals) ? lr.disposals : [];
      for (const d of disposals) {
        if (Array.isArray(d?.fifoLegs)) {
          d.fifoLegs.forEach((leg, idx) => pushLeg("LIQUIDITY", d, leg, idx));
        }
      }
    }

    for (const r of nftRows) {
      if (shouldIncludeNftDisposalExportRow(r) && Array.isArray(r?.fifoLegs)) {
        r.fifoLegs.forEach((leg, idx) => pushLeg("NFT", r, leg, idx));
      }
    }

    rows.sort((a, b) => (b._sort || 0) - (a._sort || 0) || (a._seq || 0) - (b._seq || 0));

    if (isUK) {
      const includedYes = rows.filter((r) => r.includedInTaxTotals === "Yes").length;
      const includedNo = rows.filter((r) => r.includedInTaxTotals === "No").length;
      const blankMatchingRule = rows.filter((r) => !String(r.matchingRule || "").trim()).length;
      const capitalRowsMarkedNo = rows.filter((r) => {
        if (r.includedInTaxTotals !== "No") return false;
        const ek = String(r._auditEventKey || "").toLowerCase();
        return ek && includedCapitalKeys.has(ek);
      }).length;
      const noRowsMissingFromReview = rows.filter((r) => {
        if (r.includedInTaxTotals !== "No") return false;
        const ek = String(r._auditEventKey || "").toLowerCase();
        const inReview = ek && excludedReviewKeys.has(ek);
        const hasReason = Boolean(String(r.reviewReason || "").trim());
        return !inReview && !hasReason;
      }).length;

      console.log("[UK S104 AUDIT CSV CHECK]", {
        rows: rows.length,
        includedYes,
        includedNo,
        noRowsMissingFromReview,
        capitalRowsMarkedNo,
        blankMatchingRule,
      });
    }

    return rows.map(({ _auditEventKey, ...row }) => row);
  }, [
    isUS,
    isDE,
    isUK,
    ukFinalExport,
    ds,
    vetData,
    vthoData,
    b3trData,
    nftData,
    liquidityData,
    exchangeTaxRows,
    vetVthoData,
    vip180Data,
    year,
    selectedCountry,
    language,
    nftAcquisitionHistory,
  ]);

  const TAX_LOT_AUDIT_COLS = [
    "source",
    "classification",
    "asset",
    "quantity",
    "acquiredUtc",
    "disposedUtc",
    "costBasisFiat",
    "proceedsFiat",
    "profitFiat",
    "taxableFiat",
    ...(isDE ? ["nonTaxableFiat", "fullyTaxFree"] : []),
    ...(isUK
      ? ["matchingRule", "includedInTaxTotals", "reviewStatus", "reviewReason"]
      : ["holdingDays"]),
    "disposalCustody",
    "acquisitionCustody",
    "acquisitionEventId",
    "disposalEventId",
    "crossCustody",
    "custodyNote",
    "lotIndex",
    "txHash",
  ];

  const TAX_LOT_AUDIT_HEADER_MAP = isUK
    ? {
        source: "Source",
        classification: "Classification",
        asset: "Asset",
        quantity: "Quantity",
        acquiredUtc: "Acquired (UTC)",
        disposedUtc: "Disposed (UTC)",
        costBasisFiat: `Allowable cost ${fiatCurrency}`,
        proceedsFiat: `Proceeds ${fiatCurrency}`,
        profitFiat: `Gain/Loss ${fiatCurrency}`,
        taxableFiat: `Taxable ${fiatCurrency}`,
        matchingRule: "Matching rule",
        includedInTaxTotals: "Included in tax totals",
        reviewStatus: "Review status",
        reviewReason: "Review reason",
        disposalCustody: "Disposal Custody",
        acquisitionCustody: "Acquisition Custody",
        acquisitionEventId: "Acquisition Event ID",
        disposalEventId: "Disposal Event ID",
        crossCustody: "Cross-Custody",
        custodyNote: "Custody note",
        lotIndex: "Leg index",
        txHash: "Tx hash",
      }
    : isIT
    ? {
        source: "Fonte",
        classification: "Classificazione",
        asset: "Attività",
        quantity: "Quantità lotto LIFO",
        acquiredUtc: "Acquistato (UTC)",
        disposedUtc: "Ceduto (UTC)",
        costBasisFiat: `Costo di acquisto ${fiatCurrency}`,
        proceedsFiat: `Corrispettivo ${fiatCurrency}`,
        profitFiat: `Utile/Perdita ${fiatCurrency}`,
        taxableFiat: `Imponibile ${fiatCurrency}`,
        holdingDays: "Giorni di detenzione",
        disposalCustody: "Disposal Custody",
        acquisitionCustody: "Acquisition Custody",
        acquisitionEventId: "Acquisition Event ID",
        disposalEventId: "Disposal Event ID",
        crossCustody: "Cross-Custody",
        custodyNote: "Nota custody",
        lotIndex: "Indice lotto LIFO",
        txHash: "Hash transazione",
      }
    : {
        source: "Quelle",
        classification: "Klassifizierung",
        asset: "Asset",
        quantity: "Lot-Menge",
        acquiredUtc: "Anschaffung (UTC)",
        disposedUtc: "Veräußerung (UTC)",
        costBasisFiat: `Anschaffungskosten ${fiatCurrency}`,
        proceedsFiat: `Erlös ${fiatCurrency}`,
        profitFiat: `Gewinn/Verlust ${fiatCurrency}`,
        taxableFiat: `Steuerpflichtig ${fiatCurrency}`,
        nonTaxableFiat: `Steuerfrei ${fiatCurrency}`,
        fullyTaxFree: "Vollständig steuerfrei",
        holdingDays: "Haltezeit (Kalendertage)",
        disposalCustody: "Disposal Custody",
        acquisitionCustody: "Acquisition Custody",
        acquisitionEventId: "Acquisition Event ID",
        disposalEventId: "Disposal Event ID",
        crossCustody: "Cross-Custody",
        custodyNote: "Custody-Hinweis",
        lotIndex: "Lot-Index",
        txHash: "Tx Hash",
      };

  const finalFilenameUkSummary = sanitizeFilename(
    `V3Tax_UK_SA108_Summary_${taxYearLabel || yearInt || "year"}`
  );

  const ukSummaryCsv = React.useMemo(() => {
    if (!isUK) return [];
    const backendSummary = ds?.ukTaxSummary || {};

    const detailNetCapitalGains = (finalDisposalsCsv || []).reduce(
      (sum, row) => sum + Number(row[PROFIT_KEY] || 0),
      0
    );

    const detailMiscellaneousIncome = (finalOtherIncomeCsv || []).reduce(
      (sum, row) => sum + Number(row[TAXABLE_KEY] ?? row[INCOME_KEY] ?? 0),
      0
    );

    const sa108Capital = summarizeUkSa108CapitalRows(
      finalDisposalsCsv,
      {
        proceedsKey: PROCEEDS_KEY,
        costKey: COST_KEY,
        profitKey: PROFIT_KEY,
      }
    );

    const backendNet = firstFinite(
      backendSummary.disposalNetFiat,
      backendSummary.disposalNetEUR,
      null
    );

    const backendIncome = firstFinite(
      backendSummary.miscellaneousIncomeFiat,
      backendSummary.otherIncomeFiat,
      backendSummary.incomeFiat,
      backendSummary.otherIncomeEUR,
      null
    );

    if (
      backendNet !== null &&
      Math.abs(Number(backendNet) - detailNetCapitalGains) > 0.01
    ) {
      console.error("❌ UK SA108 SUMMARY DETAIL MISMATCH", {
        backendNet,
        detailNetCapitalGains,
        diff: Number(backendNet) - detailNetCapitalGains,
        detailRows: finalDisposalsCsv?.length || 0,
      });
    }

    if (
      backendIncome !== null &&
      Math.abs(Number(backendIncome) - detailMiscellaneousIncome) > 0.01
    ) {
      console.error("❌ UK MISC INCOME SUMMARY DETAIL MISMATCH", {
        backendIncome,
        detailMiscellaneousIncome,
        diff: Number(backendIncome) - detailMiscellaneousIncome,
        detailRows: finalOtherIncomeCsv?.length || 0,
      });
    }

    const netCapitalGains = detailNetCapitalGains;
    const miscellaneousIncome = detailMiscellaneousIncome;
    const annualExemptAmount = Number.isFinite(Number(backendSummary.ukAnnualExemptAmountFiat))
      ? Number(backendSummary.ukAnnualExemptAmountFiat)
      : 3000;
    const miscIncomeAllowance = Number.isFinite(Number(backendSummary.ukMiscIncomeAllowanceFiat))
      ? Number(backendSummary.ukMiscIncomeAllowanceFiat)
      : 1000;
    const taxableGainsAfterAllowance = Math.max(0, netCapitalGains - annualExemptAmount);
    const miscBand = miscellaneousIncome <= 1000
      ? 'within_1000_misc_allowance'
      : miscellaneousIncome <= 2500
        ? 'contact_hmrc'
        : 'self_assessment_likely';
    const miscBandLabel =
      miscBand === 'within_1000_misc_allowance'
        ? (language === 'en'
            ? 'Potentially within the £1,000 trading and miscellaneous income allowance; eligibility and total relevant income must be checked.'
            : 'Möglicherweise innerhalb der £1.000 Trading and Miscellaneous Income Allowance; Anspruch und sämtliche relevanten Einkünfte müssen geprüft werden.')
        : miscBand === 'contact_hmrc'
          ? (language === 'en' ? '£1,000–£2,500: contact HMRC' : '£1.000–£2.500: HMRC kontaktieren')
          : (language === 'en' ? 'Over £2,500: Self Assessment likely' : 'Über £2.500: Self Assessment wahrscheinlich');

    const reportAsOfTs = Math.floor(Date.now() / 1000);
    const openTaxYear = isUK && Number.isFinite(yearInt)
      ? isTaxYearOpen('UK', yearInt, reportAsOfTs)
      : false;
    const reviewCount =
      ukFinalExport?.reviewRows?.length || finalExcludedReviewCsv?.length || 0;
    const reportStatus = openTaxYear
      ? (
          reviewCount > 0
            ? 'Provisional — calculation verified to report date; review items excluded'
            : 'Provisional — calculation verified to report date'
        )
      : (
          reviewCount > 0
            ? 'Verified with review items excluded from tax totals'
            : 'Final verified'
        );

    return [
      { Metric: 'UK tax year', Value: backendSummary.taxYearLabel || taxYearLabel },
      { Metric: 'Currency', Value: normalizeFiatCurrency(backendSummary.currency || fiatCurrency, selectedCountry) },

      { Metric: 'SA108 box 13.1 - Number of cryptoasset disposals', Value: sa108Capital.disposalCount },
      { Metric: 'SA108 box 13.2 - Disposal proceeds', Value: sa108Capital.proceeds },
      { Metric: 'SA108 box 13.3 - Allowable costs', Value: sa108Capital.allowableCosts },
      { Metric: 'SA108 box 13.4 - Gains in the year before losses', Value: sa108Capital.gainsBeforeLosses },
      { Metric: 'SA108 box 13.5 - Losses in the year', Value: sa108Capital.lossesInYear },
      { Metric: 'SA108 box 13.7 - Real Time Transaction Return gains/losses', Value: 0 },
      { Metric: 'SA108 box 13.8 - Tax already paid', Value: 0 },

      { Metric: 'Net capital gains/losses', Value: netCapitalGains },
      { Metric: 'Miscellaneous income', Value: miscellaneousIncome },
      { Metric: 'Annual Exempt Amount (AEA)', Value: annualExemptAmount },
      {
        Metric: 'Taxable gains after AEA',
        Value: taxableGainsAfterAllowance,
      },
      {
        Metric: language === 'en'
          ? 'AEA note'
          : 'AEA-Hinweis',
        Value: language === 'en'
          ? 'Indicative net gains after current-year loss netting and AEA, before brought-forward losses and personal adjustments.'
          : 'Indikative Nettogewinne nach unterjähriger Verlustverrechnung und AEA, vor Verlustvorträgen und persönlichen Anpassungen.',
      },
      { Metric: 'Miscellaneous income allowance', Value: miscIncomeAllowance },
      { Metric: 'Misc income reporting band', Value: miscBandLabel },
      {
        Metric: 'Review items',
        Value:
          ukFinalExport?.reviewRows?.length ??
          (finalExcludedReviewCsv || []).length,
      },
      {
        Metric: 'Report status',
        Value: reportStatus,
      },
      { Metric: 'Note', Value: backendSummary.note || 'V3Tax reports taxable bases. Final UK tax depends on wider income and personal circumstances.' },
    ];
  }, [isUK, ukFinalExport, ds?.ukTaxSummary, finalDisposalsCsv, finalOtherIncomeCsv, finalExcludedReviewCsv, taxYearLabel, fiatCurrency, selectedCountry, language, PROFIT_KEY, PROCEEDS_KEY, COST_KEY, TAXABLE_KEY, INCOME_KEY]);

  const UK_SUMMARY_COLS = ['Metric', 'Value'];
  const UK_SUMMARY_HEADER_MAP = {
    Metric: language === 'en' ? 'Metric' : 'Kennzahl',
    Value: language === 'en' ? 'Value' : 'Wert',
  };

  return (
    <>
      <style>
        {`
          .v3-gas-fee-ledger-print,
          .v3-us-nft-transform-tax-print,
          .v3-us-nft-transform-valuation-print {
            display: none;
          }

          @media print {
            body[data-v3tax-pdf-variant="raw"]
            .v3-gas-fee-ledger-print {
              display: block !important;
            }

            body[data-v3tax-pdf-variant="calc"]
            .v3-us-nft-transform-tax-print,

            body[data-v3tax-pdf-variant="disposal"]
            .v3-us-nft-transform-tax-print,

            body[data-v3tax-pdf-variant="calc"]
            .v3-us-nft-transform-valuation-print,

            body[data-v3tax-pdf-variant="disposal"]
            .v3-us-nft-transform-valuation-print,

            body[data-v3tax-pdf-variant="raw"]
            .v3-us-nft-transform-valuation-print {
              display: block !important;
            }

            .v3-us-nft-transform-tax-print tr,
            .v3-us-nft-transform-valuation-print tr {
              break-inside: avoid;
              page-break-inside: avoid;
            }
          }
        `}
      </style>

      {isUS && usNftTransform8949Rows.length > 0 ? (
        <section className="v3-us-nft-transform-tax-print report-section mt-5">
          <h2 className="mb-1 text-lg font-bold">
            US NFT Transform Tax Audit - Thorheads ReMint / VeMugs Melt
          </h2>

          <p className="mb-3 text-[9px] leading-relaxed text-slate-600">
            Each surrendered NFT is reported as a separate taxable disposal.
            Its actual historical cost basis and holding term are preserved.
            If reliable individual source-NFT FMVs are unavailable for the known
            3-to-1 conversion, total consideration is allocated by equal protocol
            recipe units. This does not assert equal standalone source-NFT FMVs
            and is not an IRS statutory safe harbor.
          </p>

          <table className="w-full table-fixed border-collapse text-[7px]">
            <thead>
              <tr className="bg-slate-100">
                <th className="border p-1">Disposed</th>
                <th className="border p-1">Asset</th>
                <th className="border p-1">Acquired</th>
                <th className="border p-1">Term</th>
                <th className="border p-1">Proceeds</th>
                <th className="border p-1">Basis</th>
                <th className="border p-1">Gain/Loss</th>
                <th className="border p-1">Source #</th>
                <th className="border p-1">Disposal identity</th>
              </tr>
            </thead>
            <tbody>
              {usNftTransform8949Rows.map((row, idx) => (
                <tr
                  key={`${row.disposal_event_key || "transform"}:${idx}`}
                >
                  <td className="border p-1 align-top">{row.date_disposed}</td>
                  <td className="border p-1 align-top">{row.token}</td>
                  <td className="border p-1 align-top">{row.date_acquired}</td>
                  <td className="border p-1 text-center align-top">
                    {row.term}
                  </td>
                  <td className="border p-1 text-right align-top">
                    {row.proceeds}
                  </td>
                  <td className="border p-1 text-right align-top">
                    {row.cost_basis}
                  </td>
                  <td className="border p-1 text-right align-top">
                    {row.gain_loss}
                  </td>
                  <td className="border p-1 text-center align-top">
                    {row.transform_source_ordinal || row.leg_index || ""}
                  </td>
                  <td className="border p-1 align-top break-all">
                    {row.disposal_event_key}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="mt-2 text-[8px] leading-relaxed text-slate-500">
            US reference: IRC §1001; Treas. Reg. §1.1001-7; Treas. Reg.
            §1.1012-1(h); IRS Digital Asset FAQs Q70, Q72, Q74 and Q102; Form
            8949.
          </p>
        </section>
      ) : null}

      {isUS && usNftTransformPrintEvents.length > 0 ? (
        <section className="v3-us-nft-transform-valuation-print report-section mt-5">
          <h2 className="mb-1 text-lg font-bold">
            US NFT Transform Valuation and Allocation Evidence
          </h2>

          <p className="mb-3 text-[9px] leading-relaxed text-slate-600">
            Audit trail for exchange value, result-NFT basis and source-side
            proceeds allocation. Observed primary-mint evidence is distinguished
            from a protocol-price proxy when the backend provides that
            provenance.
          </p>

          <table className="w-full table-fixed border-collapse text-[7px]">
            <thead>
              <tr className="bg-slate-100">
                <th className="border p-1">Date</th>
                <th className="border p-1">Transform</th>
                <th className="border p-1">Inputs -&gt; Output</th>
                <th className="border p-1">Exchange value / new basis</th>
                <th className="border p-1">Valuation evidence</th>
                <th className="border p-1">Allocation</th>
              </tr>
            </thead>
            <tbody>
              {usNftTransformPrintEvents.map((event, idx) => {
                const inputs = (event?.inputs || [])
                  .map((input) => `#${input?.tokenId ?? "?"}`)
                  .join(" + ");

                const outputId = event?.outputs?.[0]?.tokenId ?? "?";
                const valuation = event?.valuation || {};
                const ref = valuation?.primaryMintReference || {};

                const refLabel = [
                  valuation?.resultFmvSource || "",
                  ref?.referenceType || "",
                  ref?.referenceHash
                    ? `tx ${String(ref.referenceHash).slice(0, 12)}...`
                    : "",
                  ref?.evidenceAgeDays != null
                    ? `${ref.evidenceAgeDays}d prior`
                    : "",
                ]
                  .filter(Boolean)
                  .join(" | ");

                const allocationLabel = [
                  valuation?.proceedsAllocationMethod || "",
                  valuation?.allocationBasis || "",
                  valuation?.allocationMethodConfidence
                    ? `confidence=${valuation.allocationMethodConfidence}`
                    : "",
                  valuation?.allocationLegalSafeHarbor === false
                    ? "safe-harbor=no"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" | ");

                const tsSec = toSec(
                  event?.timestamp || event?.mintedAt || 0
                );

                return (
                  <tr
                    key={`${
                      event?.lifecycleKey ||
                      event?.burnHash ||
                      "transform"
                    }:${idx}`}
                  >
                    <td className="border p-1 align-top">
                      {tsSec ? formatCsvDateTimeUtc(tsSec) : ""}
                    </td>
                    <td className="border p-1 align-top">
                      {event?.transformKind || "NFT transform"}
                    </td>
                    <td className="border p-1 align-top">
                      {inputs} -&gt; #{outputId}
                    </td>
                    <td className="border p-1 text-right align-top">
                      {valuation?.exchangeValueFiat ?? ""}{" "}
                      {valuation?.currency || fiatCurrency}
                    </td>
                    <td className="border p-1 align-top break-words">
                      {valuation?.method || ""}
                      {refLabel ? (
                        <>
                          <br />
                          {refLabel}
                        </>
                      ) : null}
                    </td>
                    <td className="border p-1 align-top break-words">
                      {allocationLabel}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ) : null}

      {normalizedGasFeeLedgerData.length > 0 ? (
        <section className="v3-gas-fee-ledger-print report-section">
          <h2 className="mb-1 text-lg font-bold">
            {language === "en"
              ? "Classified Ledger — VTHO Gas Fee Disposals"
              : "Klassifiziertes Ledger — VTHO Gas-Fee-Veräußerungen"}
          </h2>

          <p className="mb-3 text-xs text-slate-600">
            {language === "en"
              ? "Receipt-derived fee-token disposals. Each row is valued and calculated at its individual transaction timestamp."
              : "Aus Transaction Receipts rekonstruierte Fee-Token-Veräußerungen. Jede Zeile wurde zum jeweiligen Transaktionszeitpunkt bewertet und berechnet."}
          </p>

          <table className="w-full table-fixed border-collapse text-[9px]">
            <thead>
              <tr className="bg-slate-100">
                <th className="border p-1">Datum</th>
                <th className="border p-1">Tx Hash</th>
                <th className="border p-1">VTHO</th>
                <th className="border p-1">
                  Kurs {fiatCurrency}
                </th>
                <th className="border p-1">
                  Erlös {fiatCurrency}
                </th>
                <th className="border p-1">
                  Cost Basis {fiatCurrency}
                </th>
                <th className="border p-1">
                  Gewinn/Verlust {fiatCurrency}
                </th>
              </tr>
            </thead>

            <tbody>
              {normalizedGasFeeLedgerData.map(
                (row, index) => (
                  <tr
                    key={`${row.eventRef}-${index}`}
                  >
                    <td className="border p-1">
                      {row.date}
                    </td>

                    <td className="break-all border p-1 font-mono">
                      {row.hash}
                    </td>

                    <td className="border p-1 text-right font-mono">
                      {row.amount}
                    </td>

                    <td className="border p-1 text-right font-mono">
                      {row.fiatPrice ?? ""}
                    </td>

                    <td className="border p-1 text-right font-mono">
                      {row.fiatValue ?? ""}
                    </td>

                    <td className="border p-1 text-right font-mono">
                      {row.costBasisFiat ?? ""}
                    </td>

                    <td className="border p-1 text-right font-mono">
                      {row.gainLossFiat ?? ""}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </section>
      ) : null}

      <div className="relative mt-6 print:hidden">
      <div className="relative overflow-hidden p-6 rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50/60 shadow-sm text-sm text-slate-700">
        {/* subtle premium glow */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-56 w-56 rounded-full bg-emerald-500/10 blur-3xl" />

        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {language === "en" ? "Exports" : "Exporte"}
            </h3>
            <p className="text-xs text-slate-600 mt-1">
              {language === "en"
                ? "Download detail tables, consolidated final reports, and PDF appendices."
                : "Exportiere Detailtabellen, Abschlussberichte und PDF-Anlagen."}
            </p>
          </div>

          {/* Token badge (premium) */}
          <div className="shrink-0 px-3 py-1 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200 tracking-wide">
            EXPORT
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* ---------- Block 1: Detail exports ---------- */}
          <div className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">
                  {language === "en" ? "Detail tables (CSV)" : "Detailtabellen (CSV)"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {language === "en" ? "One file per table" : "Eine Datei pro Tabelle"}
                </div>
              </div>
              <div className="text-xs text-slate-500">
                {language === "en" ? "CSV per table" : "CSV je Tabelle"}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              <ExportButton
                data={normalizedVetVthoRawData}
                filename="vetvtho-raw-transactions"
                columns={["type", "token", "amount", "recipient", "sender", "hash", "date", "eventRef"]}
                sortBy="sortTimestamp"
                className="w-full"
                label={language === "en" ? "VET/VTHO raw transactions" : "VET/VTHO Rohtransaktionen"}
                subtitle="vetvtho-raw-transactions.csv"
                disabled={isPreviewMode}
                headerMap={RAW_TRANSFER_HEADER_MAP}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
              />

              <ExportButton
                data={normalizedVip180RawData}
                filename="vip180-token-transfers"
                columns={["type", "token", "amount", "recipient", "sender", "hash", "date", "eventRef"]}
                sortBy="sortTimestamp"
                className="w-full"
                label={language === "en" ? "VIP-180 token transfers" : "VIP-180 Token-Transfers"}
                subtitle="vip180-token-transfers.csv"
                disabled={isPreviewMode}
                headerMap={RAW_TRANSFER_HEADER_MAP}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
              />

              <ExportButton
                data={vetDetailRows}
                filename="vet-tax-data"
                columns={TOKEN_TAX_DETAIL_COLUMNS}
                sortBy="sortTimestamp"
                className="w-full"
                label={language === "en" ? "VET tax table" : "VET Steuertabelle"}
                subtitle="vet-tax-data.csv"
                disabled={isPreviewMode}
                headerMap={DETAIL_HEADER_MAP}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
              />

              <ExportButton
                data={vthoDetailRows}
                filename="vtho-tax-data"
                columns={TOKEN_TAX_DETAIL_COLUMNS}
                sortBy="sortTimestamp"
                className="w-full"
                label={language === "en" ? "VTHO tax table" : "VTHO Steuertabelle"}
                subtitle="vtho-tax-data.csv"
                disabled={isPreviewMode}
                headerMap={DETAIL_HEADER_MAP}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
              />

              {vthoGeneratedIntervalDetailRows.length > 0 ? (
                <ExportButton
                  data={vthoGeneratedIntervalDetailRows}
                  filename="vtho-generated-interval-audit"
                  columns={TOKEN_TAX_DETAIL_COLUMNS}
                  sortBy="sortTimestamp"
                  className="w-full"
                  label={
                    language === "en"
                      ? "VTHO generated intervals (audit)"
                      : "VTHO Generated Intervalle (Audit)"
                  }
                  subtitle="vtho-generated-interval-audit.csv"
                  disabled={isPreviewMode}
                  headerMap={DETAIL_HEADER_MAP}
                  decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
                />
              ) : null}

              <ExportButton
                data={b3trDetailRows}
                filename="vip180-tax-data"
                columns={TOKEN_TAX_DETAIL_COLUMNS}
                sortBy="sortTimestamp"
                className="w-full"
                label={language === "en" ? "VIP-180 tax table" : "VIP-180 Steuertabelle"}
                subtitle="vip180-tax-data.csv"
                disabled={isPreviewMode}
                headerMap={DETAIL_HEADER_MAP}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
              />

              <ExportButton
                data={formattedLiquidityData}
                filename="liquidity-tax-data"
                columns={[
                  "date",
                  "type",
                  "hash",
                  "pool",
                  "poolAddress",
                  "lpToken",
                  "lpAmount",
                  "depositSummary",
                  "depositFiat",
                  "withdrawSummary",
                  "withdrawFiat",
                  "token",
                  "amount",
                  "unitPriceFiat",
                  "proceedsFiat",
                  "taxableProfitFiat",
                  ...(isUS ? ["shortTermProfitFiat", "longTermProfitFiat"] : []),
                  ...(isDE ? ["isFullyTaxFreeLabel"] : []),
                  "fifoLots",
                ]}
                sortBy="sortTimestamp"
                className="w-full"
                label={language === "en" ? "Liquidity tax table" : "Liquidity Steuertabelle"}
                subtitle="liquidity-tax-data.csv"
                disabled={isPreviewMode}
                headerMap={LIQUIDITY_DETAIL_HEADER_MAP}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
              />

              <ExportButton
                data={nftDetailRows}
                filename="nft-tax-data"
                columns={NFT_TAX_DETAIL_COLUMNS}
                sortBy="sortTimestamp"
                className="w-full"
                label={language === "en" ? "NFT tax table" : "NFT Steuertabelle"}
                subtitle="nft-tax-data.csv"
                disabled={isPreviewMode}
                headerMap={NFT_DETAIL_HEADER_MAP}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
              />

              <ExportButton
                data={formattedExchangeData}
                filename="exchange-imports"
                columns={[
                  "source",
                  "kind",
                  "txid",
                  "base",
                  "quote",
                  "side",
                  "amount",
                  "price",
                  "totalFiat",
                  "fee",
                  "feeAsset",
                  "taxableProfit",
                  "rawQuote",
                  "rawPrice",
                  "date",
                ]}
                sortBy="sortTimestamp"
                className="w-full"
                label={language === "en" ? "Exchange imports" : "Exchange-Importe"}
                subtitle="exchange-imports.csv"
                disabled={isPreviewMode}
                headerMap={EXCHANGE_DETAIL_HEADER_MAP}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
              />
            </div>
          </div>

          {/* ---------- Block 2: Final reports ---------- */}
          <div className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">
                  {language === "en" ? "Final reports (CSV)" : "Abschlussberichte (CSV)"}
                </div>
                <div className="text-xs text-slate-600 mt-0.5">
                  {language === "en"
                    ? "Accountant-ready CSV exports: tax results, raw ledger and review cases."
                    : "Accountant-ready CSV-Exporte: Steuerergebnisse, Rohdaten-Ledger und Review-Fälle."}
                </div>
              </div>
              <div className="shrink-0 px-3 py-1 text-xs font-semibold rounded-full bg-emerald-600 text-white tracking-wide">
                FINAL
              </div>
            </div>

            {unresolvedMultiwalletCsvCount > 0 && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                {language === "en"
                  ? `${unresolvedMultiwalletCsvCount} exchange transfer(s) could not be assigned to a wallet in this multi-wallet set. No wallet was selected automatically; the transfers are listed for review.`
                  : `${unresolvedMultiwalletCsvCount} Exchange-Transfer(s) konnten in diesem Multi-Wallet-Set keiner Wallet sicher zugeordnet werden. Es wurde keine Wallet automatisch ausgewählt; die Transfers sind als Review-Fälle aufgeführt.`}
              </div>
            )}

            {hasReviewItems && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                {language === "en"
                  ? `${selectedYearReviewCount} transaction(s) could not be fully calculated for ${taxYearLabel}. They were excluded from the ${taxYearLabel} tax totals and are available in the Excluded Review CSV. All other report rows remain available.`
                  : `${selectedYearReviewCount} Transaktion(en) konnten für ${taxYearLabel} nicht vollständig berechnet werden. Sie wurden aus den Steuersummen ${taxYearLabel} ausgeschlossen und sind in der CSV „Ausgeschlossene Review-Fälle“ enthalten. Alle übrigen Reportzeilen bleiben verfügbar.`}
              </div>
            )}

            {!hasReviewItems && hasHistoricalReviewItems && (
              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-700">
                {language === "en"
                  ? `No transactions were excluded from the ${taxYearLabel} tax totals. ${historicalReviewCount} historical carryover item(s) require review outside this tax year.`
                  : `Keine Transaktionen wurden aus den Steuersummen ${taxYearLabel} ausgeschlossen. ${historicalReviewCount} historische Carryover-Fälle erfordern eine Prüfung außerhalb dieses Steuerjahres.`}
              </div>
            )}

            {/* Main final reports: keep these visually dominant */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ExportButton
                data={finalDisposalsCsv}
                filename={finalFilenameDisposals}
                columns={FINAL_DISPOSAL_COLS}
                headerMap={finalDisposalHeaderMapForExport}
                sortBy="_sort"
                className="w-full"
                label={language === "en" ? "Disposals report" : "Report: Veräußerungsgeschäfte"}
                subtitle={`${finalFilenameDisposals}.csv`}
                disabled={csvExportDisabled}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
                numericColumns={FINAL_DISPOSAL_NUMERIC_COLS}
              />

              <ExportButton
                data={finalOtherIncomeCsv}
                filename={finalFilenameOtherIncome}
                columns={FINAL_INCOME_COLS}
                headerMap={finalIncomeHeaderMapForExport}
                sortBy="_sort"
                className="w-full"
                label={language === "en" ? "Other income report" : "Report: Sonstige Einkünfte"}
                subtitle={`${finalFilenameOtherIncome}.csv`}
                disabled={csvExportDisabled}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
                numericColumns={FINAL_INCOME_NUMERIC_COLS}
              />

              <ExportButton
                data={finalRawTransfersCsv}
                filename={finalFilenameRawTransfers}
                columns={FINAL_RAW_TRANSFER_COLS}
                headerMap={RAW_TRANSFER_HEADER_MAP}
                sortBy="sortTimestamp"
                className="w-full"
                label={language === "en" ? "Classified on-chain ledger" : "Klassifiziertes On-Chain-Ledger"}
                subtitle={`${finalFilenameRawTransfers}.csv`}
                disabled={isPreviewMode}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
                numericColumns={FINAL_RAW_TRANSFER_NUMERIC_COLS}
              />

              <ExportButton
                data={nftAcquisitionLedgerRows}
                filename={finalFilenameNftAcquisitions}
                columns={NFT_ACQUISITION_LEDGER_COLUMNS}
                headerMap={NFT_ACQUISITION_HEADER_MAP}
                sortBy="sortTimestamp"
                className="w-full"
                label={language === "en" ? "NFT acquisition ledger" : "NFT-Anschaffungsledger"}
                subtitle={`${finalFilenameNftAcquisitions}.csv`}
                title={
                  language === "en"
                    ? "One row per acquired NFT identity (token ID + cost basis). Not a VTHO FIFO export."
                    : "Eine Zeile je erworbenem NFT (Token-ID + Cost Basis). Kein VTHO-FIFO-Export."
                }
                disabled={isPreviewMode}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
                numericColumns={["paymentAmount", "acquisitionCostFiat", "batchSize", "batchIndex"]}
              />

              <ExportButton
                data={finalExcludedReviewCsv}
                filename={finalFilenameExcludedReview}
                columns={EXCLUDED_REVIEW_COLS}
                headerMap={excludedReviewHeaderMapForExport}
                sortBy="_sort"
                className="w-full"
                label={
                  language === "en"
                    ? "Excluded review"
                    : "Ausgeschlossene Review-Fälle"
                }
                subtitle={
                  hasReviewItems
                    ? (
                        language === "en"
                          ? `${reviewItemCount} item(s) · ${finalFilenameExcludedReview}.csv`
                          : `${reviewItemCount} Fälle · ${finalFilenameExcludedReview}.csv`
                      )
                    : `${finalFilenameExcludedReview}.csv`
                }
                title={
                  hasReviewItems
                    ? (
                        language === "en"
                          ? "Transactions that could not be fully calculated and were excluded from tax totals and final reports."
                          : "Nicht vollständig berechnete Transaktionen, die aus Steuersummen und finalen Reports ausgeschlossen wurden."
                      )
                    : undefined
                }
                disabled={isPreviewMode}
                decimalComma={!isDotDecimalCountry}
                delimiter={csvDelimiter}
                numericColumns={EXCLUDED_REVIEW_NUMERIC_COLS}
              />

              {isUK && (
                <ExportButton
                  data={ukSummaryCsv}
                  filename={finalFilenameUkSummary}
                  columns={UK_SUMMARY_COLS}
                  headerMap={UK_SUMMARY_HEADER_MAP}
                  className="w-full"
                  label={language === "en" ? "UK SA108 summary" : "UK SA108-Zusammenfassung"}
                  subtitle={`${finalFilenameUkSummary}.csv`}
                  disabled={isPreviewMode}
                  decimalComma={!isDotDecimalCountry}
                  delimiter={csvDelimiter}
                />
              )}
            </div>

            {/* Secondary audit export: visually quieter */}
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {language === "en" ? "Optional audit detail" : "Optionaler Audit-Detail"}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-slate-500">
                    {isUS
                      ? language === "en"
                        ? "Per FIFO-leg file for CPA review and Form 8949 support."
                        : "Datei pro FIFO-Leg für CPA-Prüfung und Form-8949-Nachweis."
                      : isUK
                        ? language === "en"
                          ? "Per matching leg with same-day / 30-day / Section 104 rule."
                          : "Pro Matching-Leg mit Same-Day / 30-Day / Section-104-Regel."
                        : language === "en"
                          ? "Per lot file for advisor review and plausibility checks."
                          : isIT
                            ? "File per lotto per consulente e controlli di plausibilità."
                            : "Datei pro Lot für Beraterprüfung und Plausibilitätscheck."}
                  </div>
                </div>

                <span className="shrink-0 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                  AUDIT
                </span>
              </div>

              {isUS ? (
                <ExportButton
                  data={auditForm8949Csv}
                  filename={`form-8949-audit-${year}`}
                  columns={AUDIT_FORM8949_COLS}
                  headerMap={AUDIT_FORM8949_HEADER_MAP}
                  sortBy="_sort"
                  className="w-full"
                  label={language === "en" ? "Form 8949 Audit (per FIFO leg)" : "Form 8949 Audit (pro FIFO-Leg)"}
                  subtitle={`form-8949-audit-${year}.csv`}
                  disabled={form8949ExportDisabled}
                  title={
                    form8949MismatchBlocksExport
                      ? (form8949DisposalCrossExport?.message ||
                          (language === "en"
                            ? "Form 8949 reconciliation mismatch — review money totals."
                            : "Form-8949-Reconciliation weicht ab — Summen prüfen."))
                      : form8949ReconciliationOkMessage || undefined
                  }
                  decimalComma={false}
                  numericColumns={FORM_8949_NUMERIC_COLS}
                />
              ) : (
                <ExportButton
                  data={taxLotAuditCsv}
                  filename={
                    isUK
                      ? sanitizeFilename(`V3Tax_UK_S104_Audit_${taxYearLabel || year || "year"}`)
                      : `tax-lot-audit-${year}`
                  }
                  columns={TAX_LOT_AUDIT_COLS}
                  headerMap={TAX_LOT_AUDIT_HEADER_MAP}
                  sortBy="_sort"
                  className="w-full"
                  label={
                    language === "en"
                      ? isUK
                        ? "UK S104 audit (per matching leg)"
                        : isIT
                          ? "LIFO tax lot audit (per lot)"
                          : "Tax lot audit (per cost-basis lot)"
                      : isUK
                        ? "UK S104-Audit (pro Matching-Leg)"
                        : isIT
                          ? "Audit lotti fiscali LIFO (per lotto)"
                          : "Steuer-Lot-Audit (pro Lot)"
                  }
                  subtitle={
                    isUK
                      ? `${sanitizeFilename(`V3Tax_UK_S104_Audit_${taxYearLabel || year || "year"}`)}.csv`
                      : `tax-lot-audit-${year}.csv`
                  }
                  disabled={isPreviewMode}
                  decimalComma={!isDotDecimalCountry}
                  delimiter={csvDelimiter}
                  numericColumns={TAX_LOT_AUDIT_NUMERIC_COLS}
                />
              )}
            </div>
          </div>

          {/* ---------- PDF ---------- */}
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white/80 backdrop-blur p-4 shadow-sm">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-slate-950">
                    {language === "en" ? "PDF reports" : "PDF-Berichte"}
                  </div>
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700">
                    Tax ready
                  </span>
                </div>
                <div className="mt-0.5 max-w-3xl text-xs leading-relaxed text-slate-500">
                  {language === "en"
                    ? "Choose the complete final reports for filing, or export focused specialist reports for your accountant."
                    : "Wähle die vollständigen finalen Berichte für die Steuerabgabe oder fokussierte Spezialberichte für deinen Steuerberater."}
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 xl:grid-cols-2">
              <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 shadow-sm">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-950">
                      {language === "en" ? "Final PDF reports" : "Finale PDF-Berichte"}
                    </div>
                    <div className="mt-0.5 text-xs leading-relaxed text-slate-500">
                      {language === "en"
                        ? "Primary accountant-ready documents with the complete tax calculation and audit trail."
                        : "Primäre accountant-ready Dokumente mit vollständiger Steuerberechnung und Nachweis."}
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-blue-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm">
                    FINAL
                  </span>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <PdfExportButton
                    label="Full Tax Report"
                    subtitle={
                      language === "en"
                        ? "Appendix 1 · Summary, income, disposals and calculation tables"
                        : "Anlage 1 · Summary, Einkünfte, Veräußerungen und Berechnungstabellen"
                    }
                    badge="Main"
                    variant="calc"
                    onPrintVariant={onPrintVariant}
                    disabled={capitalPdfExportDisabled}
                    disabledReason={
                      isPreviewMode
                        ? language === "en"
                          ? "Available after purchase"
                          : "Nach Kauf verfügbar"
                        : capitalPdfExportDisabled
                          ? pdfExportDisabledReason || undefined
                          : undefined
                    }
                    language={language}
                    reportYearLabel={isUK ? taxYearLabel : yearInt}
                    emphasis="primary"
                  />

                  <PdfExportButton
                    label={language === "en" ? "Classified Ledger Report" : "Klassifiziertes Ledger Report"}
                    subtitle={
                      language === "en"
                        ? "Appendix 2 · On-chain transfers + protocol reconstruction events"
                        : "Anlage 2 · On-chain Transfers + rekonstruierte Protokoll-Events"
                    }
                    badge="A2"
                    variant="raw"
                    onPrintVariant={onPrintVariant}
                    disabled={isPreviewMode}
                    language={language}
                    reportYearLabel={isUK ? taxYearLabel : yearInt}
                    emphasis="primary"
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-950">
                      {language === "en" ? "Optional specialist reports" : "Optionale Spezialberichte"}
                    </div>
                    <div className="mt-0.5 text-xs leading-relaxed text-slate-500">
                      {language === "en"
                        ? "Focused PDFs if your accountant only needs income or capital gains."
                        : "Fokussierte PDFs, falls dein Steuerberater nur Einkünfte oder Veräußerungen benötigt."}
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                    OPTIONAL
                  </span>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <PdfExportButton
                    label={language === "en" ? "Income Report" : "Einkünfte-Report"}
                    subtitle={
                      language === "en"
                        ? "Rewards, staking, governance and other income only"
                        : "Nur Rewards, Staking, Governance und sonstige Einkünfte"
                    }
                    badge="Income"
                    variant="income"
                    onPrintVariant={onPrintVariant}
                    disabled={pdfExportDisabled}
                    disabledReason={
                      isPreviewMode
                        ? language === "en"
                          ? "Available after purchase"
                          : "Nach Kauf verfügbar"
                        : pdfExportDisabled
                          ? pdfExportDisabledReason || undefined
                          : undefined
                    }
                    language={language}
                    reportYearLabel={isUK ? taxYearLabel : yearInt}
                    emphasis="soft"
                  />

                  <PdfExportButton
                    label={language === "en" ? "Capital Gains Report" : "Veräußerungs-Report"}
                    subtitle={
                      language === "en"
                        ? (isUK ? "Capital gains, Section 104 matching and gain/loss details only" : "Disposals, FIFO assignments and gain/loss details only")
                        : (isUK ? "Nur Capital Gains, Section-104-Matching und Gewinn-/Verlustdetails" : "Nur Veräußerungen, FIFO-Zuordnung und Gewinn-/Verlustdetails")
                    }
                    badge="Gains"
                    variant="disposal"
                    onPrintVariant={onPrintVariant}
                    disabled={capitalPdfExportDisabled}
                    disabledReason={
                      isPreviewMode
                        ? language === "en"
                          ? "Available after purchase"
                          : "Nach Kauf verfügbar"
                        : capitalPdfExportDisabled
                          ? pdfExportDisabledReason || undefined
                          : undefined
                    }
                    language={language}
                    reportYearLabel={isUK ? taxYearLabel : yearInt}
                  />
                </div>
              </div>
            </div>

            {isPreviewMode ? (
              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-center text-xs text-slate-500">
                {language === "en" ? "Available after purchase" : "Nach Kauf verfügbar"}
              </div>
            ) : form8949MismatchBlocksExport ? (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs text-amber-800">
                {pdfExportDisabledReason || form8949DisposalCrossExport?.message}
              </div>
            ) : form8949ReconciliationOkMessage ? (
              <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-center text-xs text-emerald-800">
                {form8949ReconciliationOkMessage}
              </div>
            ) : pdfExportDisabledReason ? (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs text-amber-800">
                {pdfExportDisabledReason}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
